// src/models/advanced-tracking.ts
var AdvancedTracking = class {
  acceleration;
  externalTemperature;
  humidity;
  latitude;
  light;
  longitude;
  pressure;
  temperature;
  batterPowerPercent;
  accuracy;
  timestamp;
  constructor(params) {
    this.acceleration = params.acceleration;
    this.externalTemperature = params.externalTemperature;
    this.humidity = params.humidity;
    this.latitude = params.latitude;
    this.light = params.light;
    this.longitude = params.longitude;
    this.pressure = params.pressure;
    this.batterPowerPercent = params.batterPowerPercent;
    this.accuracy = params.accuracy;
    this.temperature = params.temperature;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : params.timestamp ? new Date(params.timestamp) : void 0;
  }
  toJSON() {
    return {
      acceleration: this.acceleration,
      externalTemperature: this.externalTemperature,
      humidity: this.humidity,
      latitude: this.latitude,
      light: this.light,
      longitude: this.longitude,
      pressure: this.pressure,
      batterPowerPercent: this.batterPowerPercent,
      accuracy: this.accuracy,
      temperature: this.temperature,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var AdvancedTrackingConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new AdvancedTracking({ ...data, id: doc.id, timestamp: data.timestamp?.toDate() });
  }
};

// src/models/airport.ts
var Airport = class {
  name;
  city;
  code;
  countryCode;
  timezone;
  longitude;
  latitude;
  constructor(params) {
    this.name = params.name;
    this.city = params.city;
    this.code = params.code;
    this.countryCode = params.countryCode;
    this.timezone = params.timezone;
    this.longitude = params.longitude;
    this.latitude = params.latitude;
  }
  toJSON() {
    return {
      name: this.name,
      city: this.city,
      code: this.code,
      countryCode: this.countryCode,
      timezone: this.timezone,
      longitude: this.longitude,
      latitude: this.latitude
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/area.ts
var Area = class {
  id;
  name;
  createdAt;
  updatedAt;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : new Date(params.createdAt);
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : new Date(params.updatedAt);
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var AreaConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Area({ ...data, id: doc.id, createdAt: data.createdAt?.toDate(), updatedAt: data.updatedAt?.toDate() });
  }
};

// src/utils/milliseconds-to-time-array.ts
import { Duration } from "luxon";
function millisecondsToTimeArray(milliseconds) {
  const { days, hours, minutes, seconds } = Duration.fromObject({ milliseconds }).shiftTo(
    "days",
    "hours",
    "minutes",
    "seconds",
    "milliseconds"
  );
  return [days > 0 ? days : 0, hours > 0 ? hours : 0, minutes > 0 ? minutes : 0, seconds > 0 ? seconds : 0];
}

// src/utils/chart-data/chart-data-point.ts
import { nanoid } from "nanoid";

// src/utils/chart-data/chart-data-segment.ts
import { nanoid as nanoid2 } from "nanoid";

// src/utils/date.ts
function toDate(value) {
  if (!value) return void 0;
  if (value instanceof Date) return value;
  if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") {
    return value.toDate();
  }
  return new Date(value);
}

// src/utils/format-date-time.ts
import { DateTime } from "luxon";
import * as tz from "moment-timezone";
var DEFAULT_OPTIONS = {
  mode: "DATE_TIME" /* DATE_TIME */,
  separator: " - ",
  hour24: false,
  relativeReplace: {
    today: "Today",
    tomorrow: "Tomorrow",
    yesterday: "Yesterday"
  }
};
function formatDateTime(date, options) {
  let dateTime = DateTime.fromJSDate(date);
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options ?? {} };
  dateTime = moveTimezone(dateTime, mergedOptions);
  if (mergedOptions.mode === "DATE" /* DATE */) {
    return formatDateMode(dateTime, mergedOptions);
  }
  if (mergedOptions.mode === "TIME" /* TIME */) {
    return formatTimeMode(dateTime, mergedOptions);
  }
  return formatDateTimeMode(dateTime, mergedOptions);
}
function formatDateMode(dateTime, options) {
  if (options.relativeReplace?.today && isToday(dateTime)) {
    return options.relativeReplace.today;
  }
  if (options.relativeReplace?.tomorrow && isTomorrow(dateTime)) {
    return options.relativeReplace.tomorrow;
  }
  if (options.relativeReplace?.yesterday && isYesterday(dateTime)) {
    return options.relativeReplace.yesterday;
  }
  return dateTime.toLocaleString(DateTime.DATE_SHORT).replaceAll(/\//g, ".");
}
function formatTimeMode(dateTime, options) {
  let formatted = dateTime.toFormat(options.hour24 ? "HH:mm" : "hh:mm a");
  if (options.timezone?.show) {
    const timezone = options.timezone?.offset && options.timezone?.name ? options.timezone?.name : parseTimeZone(dateTime);
    if (timezone) {
      formatted += ` (${timezone})`;
    }
  }
  return formatted;
}
function formatDateTimeMode(dateTime, options) {
  const formattedDate = formatDateMode(dateTime, options);
  const formattedTime = formatTimeMode(dateTime, options);
  return `${formattedDate}${options.separator}${formattedTime}`;
}
function parseTimeZone(dateTime) {
  const zoneName = dateTime.zoneName;
  if (!zoneName) {
    return null;
  }
  const nowInZone = DateTime.local().setZone(zoneName);
  const nowIsoString = nowInZone.toISO();
  if (!nowIsoString) {
    return null;
  }
  return tz.tz(nowIsoString, zoneName).zoneAbbr();
}
function isToday(dateTime) {
  return dateTime.toISODate() === DateTime.local().toISODate();
}
function isTomorrow(dateTime) {
  return dateTime.toISODate() === DateTime.local().plus({ days: 1 }).toISODate();
}
function isYesterday(dateTime) {
  return dateTime.toISODate() === DateTime.local().minus({ days: 1 }).toISODate();
}
function moveTimezone(dateTime, options) {
  if (options.timezone?.iana) {
    return dateTime.setZone(options.timezone.iana);
  }
  const iana = getIANA();
  if (iana) {
    return dateTime.setZone(iana);
  }
  if (!options.timezone?.offset) {
    return dateTime;
  }
  const { zoneName } = DateTime.fromISO(`2020-01-01T01:00:00${options.timezone.offset}`, { setZone: true });
  if (!zoneName) {
    return dateTime;
  }
  return dateTime.setZone(zoneName);
}
function getIANA() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (err) {
    return null;
  }
}

// src/models/distance-unit.ts
var DistanceUnit = /* @__PURE__ */ ((DistanceUnit2) => {
  DistanceUnit2["IMPERIAL"] = "imperial";
  DistanceUnit2["METRIC"] = "metric";
  DistanceUnit2["NAUTICAL"] = "nautical";
  return DistanceUnit2;
})(DistanceUnit || {});
var DistanceUnitUtil = class {
  static toUIString(unit) {
    switch (unit) {
      case "imperial" /* IMPERIAL */:
        return "imperial";
      case "metric" /* METRIC */:
        return "metric";
      case "nautical" /* NAUTICAL */:
        return "nautical";
      default:
        return "unknown";
    }
  }
};

// src/utils/format-duration.ts
function formatDuration(milliseconds, options) {
  if (milliseconds == null || Number.isNaN(milliseconds)) {
    return "-";
  }
  const formatter = new Intl.NumberFormat();
  if (options?.convertToUnit) {
    let value;
    const unitChar = options.convertToUnit;
    switch (options.convertToUnit) {
      case "h":
        value = Math.floor(milliseconds / (1e3 * 60 * 60));
        break;
      case "m":
        value = Math.floor(milliseconds / (1e3 * 60));
        break;
      case "s":
        value = Math.floor(milliseconds / 1e3);
        break;
      default:
        return "-";
    }
    return `${formatter.format(value)}${unitChar}`;
  }
  const [rawDays, rawHours, rawMinutes, rawSeconds] = millisecondsToTimeArray(milliseconds);
  const totalHours = rawHours + rawDays * 24;
  const activeUnits = options?.displayUnits ?? ["h", "m", "s"];
  const potentialParts = [];
  const showZeroUnits = options?.showZeroUnits;
  if (activeUnits.includes("h") && (totalHours > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(totalHours)}h`);
  }
  if (activeUnits.includes("m") && (rawMinutes > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(rawMinutes)}m`);
  }
  if (activeUnits.includes("s") && (rawSeconds > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(rawSeconds)}s`);
  }
  let finalParts = potentialParts;
  if (finalParts.length === 0) {
    if (milliseconds === 0) {
      if (activeUnits.includes("s")) return "0s";
      if (activeUnits.includes("m")) return "0m";
      if (activeUnits.includes("h")) return "0h";
      return "0s";
    } else {
      return "";
    }
  }
  if (options?.maxUnits != null) {
    if (options.maxUnits <= 0) {
      finalParts = [];
    } else if (options.maxUnits < finalParts.length) {
      finalParts = finalParts.slice(0, options.maxUnits);
    }
  }
  return finalParts.join(" ");
}

// src/models/measurement-unit.ts
var MeasurementUnit = /* @__PURE__ */ ((MeasurementUnit2) => {
  MeasurementUnit2["METRIC"] = "metric";
  MeasurementUnit2["IMPERIAL"] = "imperial";
  return MeasurementUnit2;
})(MeasurementUnit || {});

// src/utils/get-default-measurement-unit.ts
var METRIC_LANGUAGE_PREFIXES = [
  "de",
  "fr",
  "it",
  "es",
  "pt",
  "nl",
  "sv",
  "no",
  "nb",
  "nn",
  "da",
  "fi",
  "pl",
  "cs",
  "sk",
  "hu",
  "el",
  "ro",
  "bg",
  "hr",
  "sl",
  "lt",
  "lv",
  "et",
  "ru",
  "uk",
  "ja",
  "ko",
  "zh",
  "th",
  "vi",
  "id",
  "ms",
  "tr",
  "he",
  "ar",
  "hi",
  "bn"
];
var METRIC_EN_VARIANTS = ["en-au", "en-nz", "en-ca", "en-ie", "en-in", "en-za"];
function isMetricLocale(locale) {
  const normalized = locale.toLowerCase();
  if (METRIC_EN_VARIANTS.some((v) => normalized.startsWith(v))) return true;
  const language = normalized.split("-")[0];
  return METRIC_LANGUAGE_PREFIXES.includes(language);
}
function getDefaultMeasurementUnit(localeHint) {
  if (localeHint) {
    return isMetricLocale(localeHint) ? "metric" /* METRIC */ : "imperial" /* IMPERIAL */;
  }
  if (typeof window !== "undefined") {
    const locale = navigator.language ?? "en-US";
    return isMetricLocale(locale) ? "metric" /* METRIC */ : "imperial" /* IMPERIAL */;
  }
  return "imperial" /* IMPERIAL */;
}

// src/utils/get-interval-logs.ts
import { differenceInMinutes } from "date-fns";
import { DateTime as DateTime2 } from "luxon";

// src/utils/inventory/audit-period.ts
import { DateTime as DateTime3 } from "luxon";

// src/utils/inventory/barcode.ts
import { format, isValid, parse } from "date-fns";

// src/utils/resolve-location.ts
var STALE_THRESHOLD_MS = 5 * 60 * 1e3;

// src/models/attachment.ts
var Attachment = class {
  id;
  sessionId;
  origin;
  type;
  filePath;
  thumbnailPath;
  displayName;
  status;
  contentType;
  fileSize;
  duration;
  dimensions;
  accessLevel;
  references;
  createdAt;
  updatedAt;
  deleted;
  constructor(params) {
    this.id = params.id;
    this.sessionId = params.sessionId;
    this.origin = params.origin;
    this.type = params.type;
    this.filePath = params.filePath;
    this.thumbnailPath = params.thumbnailPath;
    this.displayName = params.displayName;
    this.status = params.status;
    this.contentType = params.contentType;
    this.fileSize = params.fileSize;
    this.duration = params.duration;
    this.dimensions = params.dimensions;
    this.accessLevel = params.accessLevel;
    this.references = params.references ?? [];
    this.createdAt = toDate(params.createdAt) ?? /* @__PURE__ */ new Date();
    this.updatedAt = toDate(params.updatedAt);
    this.deleted = params.deleted ?? false;
  }
  toJSON() {
    return {
      id: this.id,
      sessionId: this.sessionId,
      origin: this.origin,
      type: this.type,
      filePath: this.filePath,
      thumbnailPath: this.thumbnailPath,
      displayName: this.displayName,
      status: this.status,
      contentType: this.contentType,
      fileSize: this.fileSize,
      duration: this.duration,
      dimensions: this.dimensions,
      accessLevel: this.accessLevel,
      references: this.references,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      deleted: this.deleted
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var AttachmentConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Attachment({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/attachment-access-level.ts
var AttachmentAccessLevel = /* @__PURE__ */ ((AttachmentAccessLevel2) => {
  AttachmentAccessLevel2["CHAT"] = "CHAT";
  AttachmentAccessLevel2["TIMELINE"] = "TIMELINE";
  AttachmentAccessLevel2["SESSION"] = "SESSION";
  return AttachmentAccessLevel2;
})(AttachmentAccessLevel || {});

// src/models/attachment-ref.ts
var AttachmentRef = class {
  attachmentId;
  type;
  fileName;
  thumbnailName;
  displayName;
  status;
  fileSize;
  duration;
  dimensions;
  createdAt;
  updatedAt;
  deleted;
  constructor(params) {
    this.attachmentId = params.attachmentId;
    this.type = params.type;
    this.fileName = params.fileName;
    this.thumbnailName = params.thumbnailName;
    this.displayName = params.displayName;
    this.status = params.status;
    this.fileSize = params.fileSize;
    this.duration = params.duration;
    this.dimensions = params.dimensions;
    this.createdAt = toDate(params.createdAt);
    this.updatedAt = toDate(params.updatedAt);
    this.deleted = params.deleted;
  }
  toJSON() {
    return {
      attachmentId: this.attachmentId,
      type: this.type,
      fileName: this.fileName,
      thumbnailName: this.thumbnailName,
      displayName: this.displayName,
      status: this.status,
      fileSize: this.fileSize,
      duration: this.duration,
      dimensions: this.dimensions,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      deleted: this.deleted
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/attachment-status.ts
var AttachmentStatus = /* @__PURE__ */ ((AttachmentStatus2) => {
  AttachmentStatus2["PENDING"] = "PENDING";
  AttachmentStatus2["PROCESSED"] = "PROCESSED";
  AttachmentStatus2["FAILED"] = "FAILED";
  return AttachmentStatus2;
})(AttachmentStatus || {});

// src/models/attachment-type.ts
var AttachmentType = /* @__PURE__ */ ((AttachmentType2) => {
  AttachmentType2["IMAGE"] = "IMAGE";
  AttachmentType2["VIDEO"] = "VIDEO";
  AttachmentType2["DOCUMENT"] = "DOCUMENT";
  return AttachmentType2;
})(AttachmentType || {});

// src/models/audit-log.ts
var AuditLog = class {
  id;
  userId;
  event;
  metadata;
  context;
  contextDate;
  timestampUser;
  timestampServer;
  timezone;
  language;
  locale;
  device;
  deviceIdentifier;
  appVersion;
  os;
  osVersion;
  constructor(params) {
    this.id = params.id;
    this.userId = params.userId;
    this.event = params.event;
    this.metadata = params.metadata;
    this.context = params.context;
    this.contextDate = typeof params.contextDate === "string" ? new Date(params.contextDate) : params.contextDate;
    this.timestampUser = typeof params.timestampUser === "string" ? new Date(params.timestampUser) : params.timestampUser;
    this.timestampServer = typeof params.timestampServer === "string" ? new Date(params.timestampServer) : params.timestampServer;
    this.timezone = params.timezone;
    this.language = params.language;
    this.locale = params.locale;
    this.device = params.device;
    this.deviceIdentifier = params.deviceIdentifier;
    this.appVersion = params.appVersion;
    this.os = params.os;
    this.osVersion = params.osVersion;
  }
  toJSON() {
    return {
      id: this.id,
      userId: this.userId,
      event: this.event,
      metadata: this.metadata,
      context: this.context,
      timestampUser: this.timestampUser,
      timestampServer: this.timestampServer,
      timezone: this.timezone,
      device: this.device,
      deviceIdentifier: this.deviceIdentifier,
      appVersion: this.appVersion,
      os: this.os,
      osVersion: this.osVersion
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var AuditLogConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new AuditLog({
      ...data,
      id: doc.id,
      contextDate: data.contextDate?.toDate(),
      timestampUser: data.timestampUser?.toDate(),
      timestampServer: data.timestampServer?.toDate()
    });
  }
};

// src/models/destination-type.ts
var DestinationType = /* @__PURE__ */ ((DestinationType3) => {
  DestinationType3["DONOR"] = "DONOR";
  DestinationType3["RECIPIENT"] = "RECIPIENT";
  DestinationType3["OPO"] = "OPO";
  return DestinationType3;
})(DestinationType || {});
var DestinationTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "DONOR" /* DONOR */:
        return "Donor Center";
      case "RECIPIENT" /* RECIPIENT */:
        return "Recipient Center";
      case "OPO" /* OPO */:
        return "OPO";
    }
  }
};

// src/models/organ-position-type.ts
var OrganPositionType = /* @__PURE__ */ ((OrganPositionType2) => {
  OrganPositionType2["LEFT"] = "LEFT";
  OrganPositionType2["RIGHT"] = "RIGHT";
  return OrganPositionType2;
})(OrganPositionType || {});
var OrganPositionTypeUtil = class {
  /**
   * Converts an OrganPositionType to a human-readable UI string.
   *
   * @param type - The organ position type to convert
   * @returns A formatted string representation
   *
   * @example
   * ```typescript
   * OrganPositionTypeUtil.toUIString(OrganPositionType.LEFT); // "Left"
   * OrganPositionTypeUtil.toUIString(OrganPositionType.RIGHT); // "Right"
   * ```
   */
  static toUIString(type) {
    switch (type) {
      case "LEFT" /* LEFT */:
        return "Left";
      case "RIGHT" /* RIGHT */:
        return "Right";
    }
  }
  /**
   * Converts an OrganPositionType to a short single-character abbreviation.
   *
   * @param type - The organ position type to convert
   * @returns A single-character abbreviation
   *
   * @example
   * ```typescript
   * OrganPositionTypeUtil.toShortString(OrganPositionType.LEFT); // "L"
   * OrganPositionTypeUtil.toShortString(OrganPositionType.RIGHT); // "R"
   * ```
   */
  static toShortString(type) {
    switch (type) {
      case "LEFT" /* LEFT */:
        return "L";
      case "RIGHT" /* RIGHT */:
        return "R";
    }
  }
};

// src/models/product-type.ts
var ProductType = /* @__PURE__ */ ((ProductType2) => {
  ProductType2["SHERPAPAK"] = "sherpapak-cts";
  ProductType2["LUNGGUARD"] = "lungguard";
  ProductType2["BAROGUARD"] = "baroguard";
  ProductType2["LIVERGUARD"] = "liverguard";
  ProductType2["KTS"] = "sherpapak-kts";
  ProductType2["PANCREASPAK"] = "pancreaspak";
  ProductType2["KIDNEYVAULT"] = "kidneyvault";
  ProductType2["VANTAGEPOINT"] = "vantage-point";
  return ProductType2;
})(ProductType || {});
var ProductTypeUtil = class {
  static toUIString(type, legalMark = true) {
    if (type == null) {
      return "No Device connected";
    }
    switch (type) {
      case "sherpapak-cts" /* SHERPAPAK */:
        return legalMark ? "SherpaPak\xAE" : "SherpaPak";
      case "lungguard" /* LUNGGUARD */:
        return legalMark ? "LUNGguard\xAE" : "LUNGguard";
      case "baroguard" /* BAROGUARD */:
        return legalMark ? "BAROguard\xAE" : "BAROguard";
      case "liverguard" /* LIVERGUARD */:
        return legalMark ? "LIVERguard\xAE" : "LIVERguard";
      case "sherpapak-kts" /* KTS */:
        return legalMark ? "Kidney Transport System" : "Kidney Transport System";
      case "kidneyvault" /* KIDNEYVAULT */:
        return legalMark ? "KidneyVault\u2122" : "KidneyVault";
      case "pancreaspak" /* PANCREASPAK */:
        return "PancreasPak\u2122";
      case "vantage-point" /* VANTAGEPOINT */:
        return "VantagePoint Tracking ";
      default:
        return "SherpaPak\xAE";
    }
  }
};

// src/models/organ-type.ts
var OrganType = /* @__PURE__ */ ((OrganType2) => {
  OrganType2["HEART"] = "heart";
  OrganType2["LUNG"] = "lung";
  OrganType2["LIVER"] = "liver";
  OrganType2["KIDNEY"] = "kidney";
  OrganType2["PANCREAS"] = "pancreas";
  return OrganType2;
})(OrganType || {});
var OrganTypeUtil = class {
  static toUIString(type, organPosition, capitalize = true) {
    switch (type) {
      case "heart" /* HEART */:
        return capitalize ? "Heart" : "heart";
      case "lung" /* LUNG */:
        return capitalize ? "Lung" : "lung";
      case "liver" /* LIVER */:
        return capitalize ? "Liver" : "liver";
      case "kidney" /* KIDNEY */:
        if (organPosition === "LEFT" /* LEFT */) {
          return capitalize ? "Left Kidney" : "left kidney";
        }
        if (organPosition === "RIGHT" /* RIGHT */) {
          return capitalize ? "Right Kidney" : "right kidney";
        }
        return capitalize ? "Kidneys" : "kidneys";
      case "pancreas" /* PANCREAS */:
        return capitalize ? "Pancreas" : "pancreas";
      default:
        return capitalize ? "Heart" : "heart";
    }
  }
  static fromProductType(productType) {
    switch (productType) {
      case "sherpapak-cts" /* SHERPAPAK */:
        return "heart" /* HEART */;
      case "lungguard" /* LUNGGUARD */:
      case "baroguard" /* BAROGUARD */:
        return "lung" /* LUNG */;
      case "liverguard" /* LIVERGUARD */:
        return "liver" /* LIVER */;
      case "sherpapak-kts" /* KTS */:
      case "kidneyvault" /* KIDNEYVAULT */:
        return "kidney" /* KIDNEY */;
      case "pancreaspak" /* PANCREASPAK */:
        return "pancreas" /* PANCREAS */;
      case "vantage-point" /* VANTAGEPOINT */:
        return null;
      default:
        return "heart" /* HEART */;
    }
  }
};

// src/models/timeline-entry-type.ts
var TimelineEntryType = /* @__PURE__ */ ((TimelineEntryType2) => {
  TimelineEntryType2["START_SESSION"] = "START_SESSION";
  TimelineEntryType2["STOP_SESSION"] = "STOP_SESSION";
  TimelineEntryType2["TIMEOUT"] = "TIMEOUT";
  TimelineEntryType2["LEAVE_TX_CENTER"] = "LEAVE_TX_CENTER";
  TimelineEntryType2["ARRIVAL_AT_DONOR"] = "ARRIVAL_AT_DONOR";
  TimelineEntryType2["LEAVE_DONOR"] = "LEAVE_DONOR";
  TimelineEntryType2["ARRIVE_AT_RECIPIENT"] = "ARRIVE_AT_RECIPIENT";
  TimelineEntryType2["LEAVE_OPO"] = "LEAVE_OPO";
  TimelineEntryType2["ARRIVE_AT_OPO"] = "ARRIVE_AT_OPO";
  TimelineEntryType2["DONOR_IN_ROOM"] = "DONOR_IN_ROOM";
  TimelineEntryType2["TIME_TO_INCISION"] = "TIME_TO_INCISION";
  TimelineEntryType2["TIME_OF_WITHDRAWAL"] = "TIME_OF_WITHDRAWAL";
  TimelineEntryType2["OBSERVATION_PERIOD_START"] = "OBSERVATION_PERIOD_START";
  TimelineEntryType2["TIME_OF_DEATH"] = "TIME_OF_DEATH";
  TimelineEntryType2["HEPARIN_ADMINISTERED"] = "HEPARIN_ADMINISTERED";
  TimelineEntryType2["WARM_ISCHEMIC_TIME_START"] = "WARM_ISCHEMIC_TIME_START";
  TimelineEntryType2["CANNULATION"] = "CANNULATION";
  TimelineEntryType2["COLD_PRESERVATION_FLUSH"] = "COLD_PRESERVATION_FLUSH";
  TimelineEntryType2["PERFUSION_START"] = "PERFUSION_START";
  TimelineEntryType2["PERFUSION_STOP"] = "PERFUSION_STOP";
  TimelineEntryType2["ORGAN_ACCEPTED"] = "ORGAN_ACCEPTED";
  TimelineEntryType2["ORGAN_DECLINED"] = "ORGAN_DECLINED";
  TimelineEntryType2["OFFER_ACKNOWLEDGED"] = "OFFER_ACKNOWLEDGED";
  TimelineEntryType2["PROVISIONAL_YES"] = "PROVISIONAL_YES";
  TimelineEntryType2["SURGEON_REVIEW"] = "SURGEON_REVIEW";
  TimelineEntryType2["OFFER_ACCEPTED"] = "OFFER_ACCEPTED";
  TimelineEntryType2["OFFER_DECLINED"] = "OFFER_DECLINED";
  TimelineEntryType2["CROSSCLAMP_AT_DONOR"] = "CROSSCLAMP_AT_DONOR";
  TimelineEntryType2["CROSSCLAMP_AT_RECIPIENT"] = "CROSSCLAMP_AT_RECIPIENT";
  TimelineEntryType2["HEAD_VESSEL_CLAMP"] = "HEAD_VESSEL_CLAMP";
  TimelineEntryType2["ORGAN_REMOVAL"] = "ORGAN_REMOVAL";
  TimelineEntryType2["HEART_PLACED_IN_SHERPAPAK"] = "HEART_PLACED_IN_SHERPAPAK";
  TimelineEntryType2["HEART_REMOVED_FROM_SHERPAPAK"] = "HEART_REMOVED_FROM_SHERPAPAK";
  TimelineEntryType2["ANASTOMOSIS"] = "ANASTOMOSIS";
  TimelineEntryType2["ON_CARDIO_PULMONARY_BYPASS_1"] = "ON_CARDIO_PULMONARY_BYPASS_1";
  TimelineEntryType2["START_WEAN_1"] = "START_WEAN_1";
  TimelineEntryType2["OFF_CARDIO_PULMONARY_BYPASS_1"] = "OFF_CARDIO_PULMONARY_BYPASS_1";
  TimelineEntryType2["ON_CARDIO_PULMONARY_BYPASS_2"] = "ON_CARDIO_PULMONARY_BYPASS_2";
  TimelineEntryType2["START_WEAN_2"] = "START_WEAN_2";
  TimelineEntryType2["OFF_CARDIO_PULMONARY_BYPASS_2"] = "OFF_CARDIO_PULMONARY_BYPASS_2";
  TimelineEntryType2["ORGAN_REMOVAL_LUNG"] = "ORGAN_REMOVAL_LUNG";
  TimelineEntryType2["AIR_LEAK_ASSESSMENT_COMPLETE"] = "AIR_LEAK_ASSESSMENT_COMPLETE";
  TimelineEntryType2["LUNG_PLACED_IN_LUNGGUARD"] = "LUNG_PLACED_IN_LUNGGUARD";
  TimelineEntryType2["LUNGS_REMOVED_FROM_LUNGGUARD"] = "LUNGS_REMOVED_FROM_LUNGGUARD";
  TimelineEntryType2["LEFT_LUNG_REMOVED_FROM_LUNGGUARD"] = "LEFT_LUNG_REMOVED_FROM_LUNGGUARD";
  TimelineEntryType2["RIGHT_LUNG_REMOVED_FROM_LUNGGUARD"] = "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD";
  TimelineEntryType2["LEFT_LUNG_ANASTOMOSIS_COMPLETE"] = "LEFT_LUNG_ANASTOMOSIS_COMPLETE";
  TimelineEntryType2["RIGHT_LUNG_ANASTOMOSIS_COMPLETE"] = "RIGHT_LUNG_ANASTOMOSIS_COMPLETE";
  TimelineEntryType2["RECIPIENT_LEFT_LUNG_OFF_CLAMP"] = "RECIPIENT_LEFT_LUNG_OFF_CLAMP";
  TimelineEntryType2["RECIPIENT_RIGHT_LUNG_OFF_CLAMP"] = "RECIPIENT_RIGHT_LUNG_OFF_CLAMP";
  TimelineEntryType2["EVLP_START"] = "EVLP_START";
  TimelineEntryType2["EVLP_STOP"] = "EVLP_STOP";
  TimelineEntryType2["ORGAN_REMOVAL_LIVER"] = "ORGAN_REMOVAL_LIVER";
  TimelineEntryType2["LIVER_PLACED_IN_LIVERGUARD"] = "LIVER_PLACED_IN_LIVERGUARD";
  TimelineEntryType2["LIVER_REMOVED_FROM_LIVERGUARD"] = "LIVER_REMOVED_FROM_LIVERGUARD";
  TimelineEntryType2["PORTAL_REPERFUSION"] = "PORTAL_REPERFUSION";
  TimelineEntryType2["ARTERIAL_REPERFUSION"] = "ARTERIAL_REPERFUSION";
  TimelineEntryType2["KIDNEY_DONOR_FINAL_BLOOD_UREA_NITROGEN"] = "KIDNEY_DONOR_FINAL_BLOOD_UREA_NITROGEN";
  TimelineEntryType2["KIDNEY_DONOR_FINAL_SERUM_CREATININE"] = "KIDNEY_DONOR_FINAL_SERUM_CREATININE";
  TimelineEntryType2["KIDNEY_DONOR_KIDNEY_DONOR_PROFILE_INDEX"] = "KIDNEY_DONOR_KIDNEY_DONOR_PROFILE_INDEX";
  TimelineEntryType2["KIDNEY_WITHDRAWAL_OF_SUPPORT"] = "KIDNEY_WITHDRAWAL_OF_SUPPORT";
  TimelineEntryType2["KIDNEY_START_AGONAL_PHASE"] = "KIDNEY_START_AGONAL_PHASE";
  TimelineEntryType2["KIDNEY_START_FLUSH"] = "KIDNEY_START_FLUSH";
  TimelineEntryType2["KIDNEY_X_CLAMP"] = "KIDNEY_X_CLAMP";
  TimelineEntryType2["LEFT_KIDNEY_REMOVAL"] = "LEFT_KIDNEY_REMOVAL";
  TimelineEntryType2["RIGHT_KIDNEY_REMOVAL"] = "RIGHT_KIDNEY_REMOVAL";
  TimelineEntryType2["LEFT_KIDNEY_PLACED_ON_ICE"] = "LEFT_KIDNEY_PLACED_ON_ICE";
  TimelineEntryType2["RIGHT_KIDNEY_PLACED_ON_ICE"] = "RIGHT_KIDNEY_PLACED_ON_ICE";
  TimelineEntryType2["LEFT_KIDNEY_PLACED_INTO_STORAGE"] = "LEFT_KIDNEY_PLACED_INTO_STORAGE";
  TimelineEntryType2["RIGHT_KIDNEY_PLACED_INTO_STORAGE"] = "RIGHT_KIDNEY_PLACED_INTO_STORAGE";
  TimelineEntryType2["LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE"] = "LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE";
  TimelineEntryType2["RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE"] = "RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE";
  TimelineEntryType2["LEFT_KIDNEY_HANDED_OFF_TO_COURIER"] = "LEFT_KIDNEY_HANDED_OFF_TO_COURIER";
  TimelineEntryType2["RIGHT_KIDNEY_HANDED_OFF_TO_COURIER"] = "RIGHT_KIDNEY_HANDED_OFF_TO_COURIER";
  TimelineEntryType2["LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO"] = "LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO";
  TimelineEntryType2["RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO"] = "RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO";
  TimelineEntryType2["LEFT_KIDNEY_OUT_OF_STORAGE"] = "LEFT_KIDNEY_OUT_OF_STORAGE";
  TimelineEntryType2["RIGHT_KIDNEY_OUT_OF_STORAGE"] = "RIGHT_KIDNEY_OUT_OF_STORAGE";
  TimelineEntryType2["LEFT_KIDNEY_REMOVED_FROM_ICE"] = "LEFT_KIDNEY_REMOVED_FROM_ICE";
  TimelineEntryType2["RIGHT_KIDNEY_REMOVED_FROM_ICE"] = "RIGHT_KIDNEY_REMOVED_FROM_ICE";
  TimelineEntryType2["LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE"] = "LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE";
  TimelineEntryType2["RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE"] = "RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE";
  TimelineEntryType2["LEFT_KIDNEY_ARTERY_ANASTOMOSIS"] = "LEFT_KIDNEY_ARTERY_ANASTOMOSIS";
  TimelineEntryType2["RIGHT_KIDNEY_ARTERY_ANASTOMOSIS"] = "RIGHT_KIDNEY_ARTERY_ANASTOMOSIS";
  TimelineEntryType2["LEFT_KIDNEY_VEIN_ANASTOMOSIS"] = "LEFT_KIDNEY_VEIN_ANASTOMOSIS";
  TimelineEntryType2["RIGHT_KIDNEY_VEIN_ANASTOMOSIS"] = "RIGHT_KIDNEY_VEIN_ANASTOMOSIS";
  TimelineEntryType2["LEFT_KIDNEY_URETER_ANASTOMOSIS"] = "LEFT_KIDNEY_URETER_ANASTOMOSIS";
  TimelineEntryType2["RIGHT_KIDNEY_URETER_ANASTOMOSIS"] = "RIGHT_KIDNEY_URETER_ANASTOMOSIS";
  TimelineEntryType2["LEFT_KIDNEY_ACCEPTED"] = "LEFT_KIDNEY_ACCEPTED";
  TimelineEntryType2["RIGHT_KIDNEY_ACCEPTED"] = "RIGHT_KIDNEY_ACCEPTED";
  TimelineEntryType2["LEFT_KIDNEY_ALLOCATION_COMPLETE"] = "LEFT_KIDNEY_ALLOCATION_COMPLETE";
  TimelineEntryType2["RIGHT_KIDNEY_ALLOCATION_COMPLETE"] = "RIGHT_KIDNEY_ALLOCATION_COMPLETE";
  TimelineEntryType2["LEFT_KIDNEY_NOT_RECOVERED"] = "LEFT_KIDNEY_NOT_RECOVERED";
  TimelineEntryType2["RIGHT_KIDNEY_NOT_RECOVERED"] = "RIGHT_KIDNEY_NOT_RECOVERED";
  TimelineEntryType2["LEFT_KIDNEY_LIST_EXHAUSTED"] = "LEFT_KIDNEY_LIST_EXHAUSTED";
  TimelineEntryType2["RIGHT_KIDNEY_LIST_EXHAUSTED"] = "RIGHT_KIDNEY_LIST_EXHAUSTED";
  TimelineEntryType2["ORGAN_REMOVAL_PANCREAS"] = "ORGAN_REMOVAL_PANCREAS";
  TimelineEntryType2["PANCREAS_PLACED_IN_PANCREASPAK"] = "PANCREAS_PLACED_IN_PANCREASPAK";
  TimelineEntryType2["PANCREAS_REMOVED_FROM_PANCREASPAK"] = "PANCREAS_REMOVED_FROM_PANCREASPAK";
  TimelineEntryType2["CUSTOM"] = "CUSTOM";
  TimelineEntryType2["UNKNOWN"] = "UNKNOWN";
  TimelineEntryType2["START_LOGGER"] = "START_LOGGER";
  TimelineEntryType2["STOP_LOGGER"] = "STOP_LOGGER";
  TimelineEntryType2["PLANE_DEPARTURE_RECIPIENT"] = "PLANE_DEPARTURE_RECIPIENT";
  TimelineEntryType2["PLANE_ARRIVAL_DONOR"] = "PLANE_ARRIVAL_DONOR";
  TimelineEntryType2["PLANE_DEPARTURE_DONOR"] = "PLANE_DEPARTURE_DONOR";
  TimelineEntryType2["PLANE_ARRIVAL_RECIPIENT"] = "PLANE_ARRIVAL_RECIPIENT";
  TimelineEntryType2["LEFT_KIDNEY_DECLINED"] = "LEFT_KIDNEY_DECLINED";
  TimelineEntryType2["RIGHT_KIDNEY_DECLINED"] = "RIGHT_KIDNEY_DECLINED";
  return TimelineEntryType2;
})(TimelineEntryType || {});
var TimelineEntryTypeUtil = class {
  /**
   * Converts a timeline entry type to a human-readable UI string.
   *
   * For entries involving devices (e.g., "Lung Placed into..."), the product name
   * is included when available. When product is undefined, "Device" is used as fallback.
   *
   * @param type - The timeline entry type to convert
   * @param product - Optional product type for device-specific strings
   * @param annotations - Optional annotations for position or cannulation details
   * @returns A formatted string representation for display
   *
   * @example
   * ```typescript
   * // With product
   * TimelineEntryTypeUtil.toUIString(TimelineEntryType.LUNG_PLACED_IN_LUNGGUARD, ProductType.BAROGUARD);
   * // => "Lung(s) Placed into BAROguard®"
   *
   * // Without product (v7 sessions)
   * TimelineEntryTypeUtil.toUIString(TimelineEntryType.LUNG_PLACED_IN_LUNGGUARD);
   * // => "Lung(s) Placed into Device"
   * ```
   */
  static toUIString(type, product, annotations = {}) {
    const productName = product ? ProductTypeUtil.toUIString(product) : "Device";
    const lungPositionTypeAnnotation = product === "baroguard" /* BAROGUARD */ && annotations?.position != null ? ` - ${LungPositionTypeUtil.toUIString(annotations.position)}` : "";
    const cannulationTypeAnnotation = annotations?.cannulation != null ? ` - ${CannulationTypeUtil.toUIString(annotations.cannulation)}` : "";
    switch (type) {
      case "START_SESSION" /* START_SESSION */:
        return "Case Started";
      case "STOP_SESSION" /* STOP_SESSION */:
        return "Case Stopped";
      case "LEAVE_TX_CENTER" /* LEAVE_TX_CENTER */:
        return "Departed Recipient Center";
      case "ARRIVAL_AT_DONOR" /* ARRIVAL_AT_DONOR */:
        return "Arrived at Donor Center";
      case "DONOR_IN_ROOM" /* DONOR_IN_ROOM */:
        return "Donor In-room";
      case "HEAD_VESSEL_CLAMP" /* HEAD_VESSEL_CLAMP */:
        return "Head Vessel Clamp/Ligate";
      case "TIME_TO_INCISION" /* TIME_TO_INCISION */:
        return "Time of Incision";
      case "ORGAN_ACCEPTED" /* ORGAN_ACCEPTED */:
        return "Organ Accepted";
      case "ORGAN_DECLINED" /* ORGAN_DECLINED */:
        return "Organ Declined";
      case "OFFER_ACKNOWLEDGED" /* OFFER_ACKNOWLEDGED */:
        return "Offer Acknowledged";
      case "PROVISIONAL_YES" /* PROVISIONAL_YES */:
        return "Provisional Yes";
      case "SURGEON_REVIEW" /* SURGEON_REVIEW */:
        return "Offer in Review";
      case "OFFER_ACCEPTED" /* OFFER_ACCEPTED */:
        return "Offer Accepted";
      case "OFFER_DECLINED" /* OFFER_DECLINED */:
        return "Offer Declined";
      case "TIME_OF_WITHDRAWAL" /* TIME_OF_WITHDRAWAL */:
        return "Time of Withdrawal";
      case "WARM_ISCHEMIC_TIME_START" /* WARM_ISCHEMIC_TIME_START */:
        return "Warm Ischemic Time Start";
      case "OBSERVATION_PERIOD_START" /* OBSERVATION_PERIOD_START */:
        return "Observation Period Start";
      case "TIME_OF_DEATH" /* TIME_OF_DEATH */:
        return "Time of Death";
      case "HEPARIN_ADMINISTERED" /* HEPARIN_ADMINISTERED */:
        return "Heparin Administered";
      case "CROSSCLAMP_AT_DONOR" /* CROSSCLAMP_AT_DONOR */:
        return "Donor Crossclamp";
      case "ORGAN_REMOVAL" /* ORGAN_REMOVAL */:
        return "Heart Removal";
      case "ORGAN_REMOVAL_LUNG" /* ORGAN_REMOVAL_LUNG */:
        return "Lung Removal";
      case "AIR_LEAK_ASSESSMENT_COMPLETE" /* AIR_LEAK_ASSESSMENT_COMPLETE */:
        return "Air Leak Assessment Complete";
      case "ORGAN_REMOVAL_LIVER" /* ORGAN_REMOVAL_LIVER */:
        return "Liver Removal";
      case "HEART_PLACED_IN_SHERPAPAK" /* HEART_PLACED_IN_SHERPAPAK */:
        return `Heart Placed into ${productName}`;
      case "LUNG_PLACED_IN_LUNGGUARD" /* LUNG_PLACED_IN_LUNGGUARD */:
        return `Lung(s) Placed into ${productName}${lungPositionTypeAnnotation}`;
      case "LIVER_PLACED_IN_LIVERGUARD" /* LIVER_PLACED_IN_LIVERGUARD */:
        return `Liver Placed into ${productName}`;
      case "LEAVE_DONOR" /* LEAVE_DONOR */:
        return "Departed Donor Center";
      case "ARRIVE_AT_RECIPIENT" /* ARRIVE_AT_RECIPIENT */:
        return "Arrived at Recipient Center";
      case "HEART_REMOVED_FROM_SHERPAPAK" /* HEART_REMOVED_FROM_SHERPAPAK */:
        return `Heart Removed from ${productName}`;
      case "LIVER_REMOVED_FROM_LIVERGUARD" /* LIVER_REMOVED_FROM_LIVERGUARD */:
        return `Liver Removed from ${productName}`;
      case "LUNGS_REMOVED_FROM_LUNGGUARD" /* LUNGS_REMOVED_FROM_LUNGGUARD */:
        return `Lungs Removed from ${productName}`;
      case "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return `Left Lung Removed from ${productName}`;
      case "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return `Right Lung Removed from ${productName}`;
      case "ANASTOMOSIS" /* ANASTOMOSIS */:
        return "Anastomosis";
      case "LEFT_LUNG_ANASTOMOSIS_COMPLETE" /* LEFT_LUNG_ANASTOMOSIS_COMPLETE */:
        return "Left Lung Anastomosis Complete";
      case "RIGHT_LUNG_ANASTOMOSIS_COMPLETE" /* RIGHT_LUNG_ANASTOMOSIS_COMPLETE */:
        return "Right Lung Anastomosis Complete";
      case "CROSSCLAMP_AT_RECIPIENT" /* CROSSCLAMP_AT_RECIPIENT */:
        return "Recipient Off Clamp";
      case "RECIPIENT_LEFT_LUNG_OFF_CLAMP" /* RECIPIENT_LEFT_LUNG_OFF_CLAMP */:
        return "Recipient Left Lung Off Clamp";
      case "RECIPIENT_RIGHT_LUNG_OFF_CLAMP" /* RECIPIENT_RIGHT_LUNG_OFF_CLAMP */:
        return "Recipient Right Lung Off Clamp";
      case "PORTAL_REPERFUSION" /* PORTAL_REPERFUSION */:
        return "Portal Reperfusion";
      case "ARTERIAL_REPERFUSION" /* ARTERIAL_REPERFUSION */:
        return "Arterial Reperfusion";
      case "PERFUSION_START" /* PERFUSION_START */:
        return "Start Aortic Flush";
      case "PERFUSION_STOP" /* PERFUSION_STOP */:
        return "End Aortic Flush";
      case "ON_CARDIO_PULMONARY_BYPASS_1" /* ON_CARDIO_PULMONARY_BYPASS_1 */:
        return "On Cardio Pulmonary Bypass 1";
      case "START_WEAN_1" /* START_WEAN_1 */:
        return "Start Wean 1";
      case "OFF_CARDIO_PULMONARY_BYPASS_1" /* OFF_CARDIO_PULMONARY_BYPASS_1 */:
        return "Off Cardio Pulmonary Bypass 1";
      case "ON_CARDIO_PULMONARY_BYPASS_2" /* ON_CARDIO_PULMONARY_BYPASS_2 */:
        return "On Cardio Pulmonary Bypass 2";
      case "START_WEAN_2" /* START_WEAN_2 */:
        return "Start Wean 2";
      case "OFF_CARDIO_PULMONARY_BYPASS_2" /* OFF_CARDIO_PULMONARY_BYPASS_2 */:
        return "Off Cardio Pulmonary Bypass 2";
      case "LEAVE_OPO" /* LEAVE_OPO */:
        return "Departed OPO";
      case "ARRIVE_AT_OPO" /* ARRIVE_AT_OPO */:
        return "Arrived at OPO";
      case "KIDNEY_DONOR_FINAL_BLOOD_UREA_NITROGEN" /* KIDNEY_DONOR_FINAL_BLOOD_UREA_NITROGEN */:
        return "Donor Final Blood Urea Nitrogen";
      case "KIDNEY_DONOR_FINAL_SERUM_CREATININE" /* KIDNEY_DONOR_FINAL_SERUM_CREATININE */:
        return "Donor Final Serum Creatinine";
      case "KIDNEY_DONOR_KIDNEY_DONOR_PROFILE_INDEX" /* KIDNEY_DONOR_KIDNEY_DONOR_PROFILE_INDEX */:
        return "Donor Kidney Donor Profile Index";
      case "KIDNEY_WITHDRAWAL_OF_SUPPORT" /* KIDNEY_WITHDRAWAL_OF_SUPPORT */:
        return "Withdrawal of Support";
      case "KIDNEY_START_AGONAL_PHASE" /* KIDNEY_START_AGONAL_PHASE */:
        return "Start of Agonal Phase";
      case "KIDNEY_START_FLUSH" /* KIDNEY_START_FLUSH */:
        return "Start Flush";
      case "KIDNEY_X_CLAMP" /* KIDNEY_X_CLAMP */:
        return "X Clamp";
      case "LEFT_KIDNEY_REMOVAL" /* LEFT_KIDNEY_REMOVAL */:
        return "Left Kidney Removal";
      case "RIGHT_KIDNEY_REMOVAL" /* RIGHT_KIDNEY_REMOVAL */:
        return "Right Kidney Removal";
      case "LEFT_KIDNEY_PLACED_ON_ICE" /* LEFT_KIDNEY_PLACED_ON_ICE */:
        return "Left Kidney Placed on Ice";
      case "RIGHT_KIDNEY_PLACED_ON_ICE" /* RIGHT_KIDNEY_PLACED_ON_ICE */:
        return "Right Kidney Placed on Ice";
      case "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */:
        return `Left Kidney Placed into ${productName}`;
      case "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */:
        return `Right Kidney Placed into ${productName}`;
      case "LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */:
        return "Left Kidney Placed into Non-Integrated Perfusion Device";
      case "RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */:
        return "Right Kidney Placed into Non-Integrated Perfusion Device";
      case "LEFT_KIDNEY_HANDED_OFF_TO_COURIER" /* LEFT_KIDNEY_HANDED_OFF_TO_COURIER */:
        return "Left Kidney Handed off to Courier/Air";
      case "RIGHT_KIDNEY_HANDED_OFF_TO_COURIER" /* RIGHT_KIDNEY_HANDED_OFF_TO_COURIER */:
        return "Right Kidney Handed off to Courier/Air";
      case "LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */:
        return "Left Kidney Arrived at TXC or OPO";
      case "RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */:
        return "Right Kidney Arrived at TXC or OPO";
      case "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */:
        return `Left Kidney Out of ${productName}`;
      case "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */:
        return `Right Kidney Out of ${productName}`;
      case "LEFT_KIDNEY_REMOVED_FROM_ICE" /* LEFT_KIDNEY_REMOVED_FROM_ICE */:
        return "Left Kidney Removed from Ice";
      case "RIGHT_KIDNEY_REMOVED_FROM_ICE" /* RIGHT_KIDNEY_REMOVED_FROM_ICE */:
        return "Right Kidney Removed from Ice";
      case "LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */:
        return "Left Kidney Removed from Non-Integrated Perfusion Device";
      case "RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */:
        return "Right Kidney Removed from Non-Integrated Perfusion Device";
      case "LEFT_KIDNEY_ARTERY_ANASTOMOSIS" /* LEFT_KIDNEY_ARTERY_ANASTOMOSIS */:
        return "Left Kidney Artery Anastomosis";
      case "RIGHT_KIDNEY_ARTERY_ANASTOMOSIS" /* RIGHT_KIDNEY_ARTERY_ANASTOMOSIS */:
        return "Right Kidney Artery Anastomosis";
      case "LEFT_KIDNEY_VEIN_ANASTOMOSIS" /* LEFT_KIDNEY_VEIN_ANASTOMOSIS */:
        return "Left Kidney Vein Anastomosis";
      case "RIGHT_KIDNEY_VEIN_ANASTOMOSIS" /* RIGHT_KIDNEY_VEIN_ANASTOMOSIS */:
        return "Right Kidney Vein Anastomosis";
      case "LEFT_KIDNEY_URETER_ANASTOMOSIS" /* LEFT_KIDNEY_URETER_ANASTOMOSIS */:
        return "Left Kidney Ureter Anastomosis";
      case "RIGHT_KIDNEY_URETER_ANASTOMOSIS" /* RIGHT_KIDNEY_URETER_ANASTOMOSIS */:
        return "Right Kidney Ureter Anastomosis";
      case "LEFT_KIDNEY_ACCEPTED" /* LEFT_KIDNEY_ACCEPTED */:
        return "Left Kidney Accepted for Recovery";
      case "RIGHT_KIDNEY_ACCEPTED" /* RIGHT_KIDNEY_ACCEPTED */:
        return "Right Kidney Accepted for Recovery";
      case "LEFT_KIDNEY_ALLOCATION_COMPLETE" /* LEFT_KIDNEY_ALLOCATION_COMPLETE */:
        return "Left Kidney Allocation Complete";
      case "RIGHT_KIDNEY_ALLOCATION_COMPLETE" /* RIGHT_KIDNEY_ALLOCATION_COMPLETE */:
        return "Right Kidney Allocation Complete";
      case "LEFT_KIDNEY_NOT_RECOVERED" /* LEFT_KIDNEY_NOT_RECOVERED */:
        return "Left Kidney Not Recovered";
      case "RIGHT_KIDNEY_NOT_RECOVERED" /* RIGHT_KIDNEY_NOT_RECOVERED */:
        return "Right Kidney Not Recovered";
      case "LEFT_KIDNEY_LIST_EXHAUSTED" /* LEFT_KIDNEY_LIST_EXHAUSTED */:
        return "List Exhausted (Left Kidney)";
      case "RIGHT_KIDNEY_LIST_EXHAUSTED" /* RIGHT_KIDNEY_LIST_EXHAUSTED */:
        return "List Exhausted (Right Kidney)";
      case "ORGAN_REMOVAL_PANCREAS" /* ORGAN_REMOVAL_PANCREAS */:
        return "Pancreas Removal";
      case "PANCREAS_PLACED_IN_PANCREASPAK" /* PANCREAS_PLACED_IN_PANCREASPAK */:
        return `Pancreas Placed into ${productName}`;
      case "PANCREAS_REMOVED_FROM_PANCREASPAK" /* PANCREAS_REMOVED_FROM_PANCREASPAK */:
        return `Pancreas Removed from ${productName}`;
      case "EVLP_START" /* EVLP_START */:
        return "EVLP Start";
      case "EVLP_STOP" /* EVLP_STOP */:
        return "EVLP Stop";
      case "CANNULATION" /* CANNULATION */:
        return `Cannulation${cannulationTypeAnnotation}`;
      case "COLD_PRESERVATION_FLUSH" /* COLD_PRESERVATION_FLUSH */:
        return "Cold Preservation Flush";
      case "TIMEOUT" /* TIMEOUT */:
        return "Timeout";
      case "CUSTOM" /* CUSTOM */:
        return "Custom Event";
      case "UNKNOWN" /* UNKNOWN */:
        return "Unknown Event";
      case "START_LOGGER" /* START_LOGGER */:
        return "Logger Started";
      case "STOP_LOGGER" /* STOP_LOGGER */:
        return "Logger Stopped";
      case "PLANE_DEPARTURE_RECIPIENT" /* PLANE_DEPARTURE_RECIPIENT */:
        return "Take-off at Recipient Airport";
      case "PLANE_ARRIVAL_DONOR" /* PLANE_ARRIVAL_DONOR */:
        return "Landed at Donor Airport";
      case "PLANE_DEPARTURE_DONOR" /* PLANE_DEPARTURE_DONOR */:
        return "Take-off at Donor Airport";
      case "PLANE_ARRIVAL_RECIPIENT" /* PLANE_ARRIVAL_RECIPIENT */:
        return "Landed at Recipient Airport";
      case "LEFT_KIDNEY_DECLINED" /* LEFT_KIDNEY_DECLINED */:
        return "Left Kidney Declined";
      case "RIGHT_KIDNEY_DECLINED" /* RIGHT_KIDNEY_DECLINED */:
        return "Right Kidney Declined";
      default:
        return "Unknown Event";
    }
  }
  static getOrganPosition(type) {
    const byPositions = {
      ["LEFT" /* LEFT */]: [
        "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */,
        "LEFT_LUNG_ANASTOMOSIS_COMPLETE" /* LEFT_LUNG_ANASTOMOSIS_COMPLETE */,
        "RECIPIENT_LEFT_LUNG_OFF_CLAMP" /* RECIPIENT_LEFT_LUNG_OFF_CLAMP */,
        "LEFT_KIDNEY_REMOVAL" /* LEFT_KIDNEY_REMOVAL */,
        "LEFT_KIDNEY_PLACED_ON_ICE" /* LEFT_KIDNEY_PLACED_ON_ICE */,
        "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */,
        "LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */,
        "LEFT_KIDNEY_HANDED_OFF_TO_COURIER" /* LEFT_KIDNEY_HANDED_OFF_TO_COURIER */,
        "LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */,
        "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */,
        "LEFT_KIDNEY_REMOVED_FROM_ICE" /* LEFT_KIDNEY_REMOVED_FROM_ICE */,
        "LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */,
        "LEFT_KIDNEY_ARTERY_ANASTOMOSIS" /* LEFT_KIDNEY_ARTERY_ANASTOMOSIS */,
        "LEFT_KIDNEY_VEIN_ANASTOMOSIS" /* LEFT_KIDNEY_VEIN_ANASTOMOSIS */,
        "LEFT_KIDNEY_URETER_ANASTOMOSIS" /* LEFT_KIDNEY_URETER_ANASTOMOSIS */,
        "LEFT_KIDNEY_ACCEPTED" /* LEFT_KIDNEY_ACCEPTED */,
        "LEFT_KIDNEY_ALLOCATION_COMPLETE" /* LEFT_KIDNEY_ALLOCATION_COMPLETE */,
        "LEFT_KIDNEY_NOT_RECOVERED" /* LEFT_KIDNEY_NOT_RECOVERED */,
        "LEFT_KIDNEY_LIST_EXHAUSTED" /* LEFT_KIDNEY_LIST_EXHAUSTED */,
        "LEFT_KIDNEY_DECLINED" /* LEFT_KIDNEY_DECLINED */
      ],
      ["RIGHT" /* RIGHT */]: [
        "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */,
        "RIGHT_LUNG_ANASTOMOSIS_COMPLETE" /* RIGHT_LUNG_ANASTOMOSIS_COMPLETE */,
        "RECIPIENT_RIGHT_LUNG_OFF_CLAMP" /* RECIPIENT_RIGHT_LUNG_OFF_CLAMP */,
        "RIGHT_KIDNEY_REMOVAL" /* RIGHT_KIDNEY_REMOVAL */,
        "RIGHT_KIDNEY_PLACED_ON_ICE" /* RIGHT_KIDNEY_PLACED_ON_ICE */,
        "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */,
        "RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */,
        "RIGHT_KIDNEY_HANDED_OFF_TO_COURIER" /* RIGHT_KIDNEY_HANDED_OFF_TO_COURIER */,
        "RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */,
        "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */,
        "RIGHT_KIDNEY_REMOVED_FROM_ICE" /* RIGHT_KIDNEY_REMOVED_FROM_ICE */,
        "RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */,
        "RIGHT_KIDNEY_ARTERY_ANASTOMOSIS" /* RIGHT_KIDNEY_ARTERY_ANASTOMOSIS */,
        "RIGHT_KIDNEY_VEIN_ANASTOMOSIS" /* RIGHT_KIDNEY_VEIN_ANASTOMOSIS */,
        "RIGHT_KIDNEY_URETER_ANASTOMOSIS" /* RIGHT_KIDNEY_URETER_ANASTOMOSIS */,
        "RIGHT_KIDNEY_ACCEPTED" /* RIGHT_KIDNEY_ACCEPTED */,
        "RIGHT_KIDNEY_ALLOCATION_COMPLETE" /* RIGHT_KIDNEY_ALLOCATION_COMPLETE */,
        "RIGHT_KIDNEY_NOT_RECOVERED" /* RIGHT_KIDNEY_NOT_RECOVERED */,
        "RIGHT_KIDNEY_LIST_EXHAUSTED" /* RIGHT_KIDNEY_LIST_EXHAUSTED */,
        "RIGHT_KIDNEY_DECLINED" /* RIGHT_KIDNEY_DECLINED */
      ]
    };
    if (byPositions["LEFT" /* LEFT */].includes(type)) {
      return "LEFT" /* LEFT */;
    }
    if (byPositions["RIGHT" /* RIGHT */].includes(type)) {
      return "RIGHT" /* RIGHT */;
    }
    return null;
  }
  static attributes(type) {
    switch (type) {
      case "HEART_PLACED_IN_SHERPAPAK" /* HEART_PLACED_IN_SHERPAPAK */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LUNG_PLACED_IN_LUNGGUARD" /* LUNG_PLACED_IN_LUNGGUARD */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LIVER_PLACED_IN_LIVERGUARD" /* LIVER_PLACED_IN_LIVERGUARD */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "PANCREAS_PLACED_IN_PANCREASPAK" /* PANCREAS_PLACED_IN_PANCREASPAK */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "HEART_REMOVED_FROM_SHERPAPAK" /* HEART_REMOVED_FROM_SHERPAPAK */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LUNGS_REMOVED_FROM_LUNGGUARD" /* LUNGS_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LIVER_REMOVED_FROM_LIVERGUARD" /* LIVER_REMOVED_FROM_LIVERGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "PANCREAS_REMOVED_FROM_PANCREASPAK" /* PANCREAS_REMOVED_FROM_PANCREASPAK */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      default:
        return [];
    }
  }
};

// src/models/audit-log-type.ts
var AuditLogType = /* @__PURE__ */ ((AuditLogType2) => {
  AuditLogType2["SESSION_CREATED"] = "SESSION_CREATED";
  AuditLogType2["SESSION_JOINED"] = "SESSION_JOINED";
  AuditLogType2["SESSION_LEFT"] = "SESSION_LEFT";
  AuditLogType2["SESSION_STOPPED"] = "SESSION_STOPPED";
  AuditLogType2["LOGGER_STARTED"] = "LOGGER_STARTED";
  AuditLogType2["LOGGER_CONNECTED"] = "LOGGER_CONNECTED";
  AuditLogType2["LOGGER_DISCONNECTED"] = "LOGGER_DISCONNECTED";
  AuditLogType2["LOGGER_STOPPED"] = "LOGGER_STOPPED";
  AuditLogType2["LOGGER_UNLINKED"] = "LOGGER_UNLINKED";
  AuditLogType2["TIMELINEENTRY_ADDED"] = "TIMELINEENTRY_ADDED";
  AuditLogType2["TIMELINEENTRY_UPDATED"] = "TIMELINEENTRY_UPDATED";
  AuditLogType2["TIMELINEENTRY_DELETED"] = "TIMELINEENTRY_DELETED";
  AuditLogType2["ETA_SET"] = "ETA_SET";
  AuditLogType2["ETA_UPDATED"] = "ETA_UPDATED";
  AuditLogType2["ETA_DELETED"] = "ETA_DELETED";
  AuditLogType2["FLIGHT_ADDED"] = "FLIGHT_ADDED";
  AuditLogType2["FLIGHT_DELETED"] = "FLIGHT_DELETED";
  AuditLogType2["ADVANCED_TRACKING_ACTIVATED"] = "ADVANCED_TRACKING_ACTIVATED";
  AuditLogType2["ADVANCED_TRACKING_DEACTIVATED"] = "ADVANCED_TRACKING_DEACTIVATED";
  AuditLogType2["ADVANCED_TRACKING_UNLINKED"] = "ADVANCED_TRACKING_UNLINKED";
  AuditLogType2["UNOSID_SET"] = "UNOSID_SET";
  AuditLogType2["UNOSID_DELETED"] = "UNOSID_DELETED";
  AuditLogType2["DONOR_CENTER_SET"] = "DONOR_CENTER_SET";
  AuditLogType2["DONOR_CENTER_UPDATED"] = "DONOR_CENTER_UPDATED";
  AuditLogType2["DONOR_CENTER_DELETED"] = "DONOR_CENTER_DELETED";
  AuditLogType2["DESTINATION_SET"] = "DESTINATION_SET";
  AuditLogType2["DESTINATION_UPDATED"] = "DESTINATION_UPDATED";
  AuditLogType2["DESTINATION_DELETED"] = "DESTINATION_DELETED";
  AuditLogType2["SHARED_SESSION_CREATED"] = "SHARED_SESSION_CREATED";
  AuditLogType2["BREAKOUT_SESSION_CREATED"] = "BREAKOUT_SESSION_CREATED";
  AuditLogType2["BREAKOUT_SESSION_CANCELLED"] = "BREAKOUT_SESSION_CANCELLED";
  AuditLogType2["MATCHRUNID_SET"] = "MATCHRUNID_SET";
  AuditLogType2["MATCHRUNID_DELETED"] = "MATCHRUNID_DELETED";
  AuditLogType2["MANUAL_IMPORT_SESSION_LOGS"] = "MANUAL_IMPORT_SESSION_LOGS";
  return AuditLogType2;
})(AuditLogType || {});
var AuditLogTypeUtil = class {
  static toUIString(auditLog, context) {
    switch (auditLog.event) {
      case "SESSION_CREATED" /* SESSION_CREATED */:
        return "Case Started";
      case "SESSION_JOINED" /* SESSION_JOINED */:
        return "Case Joined";
      case "SESSION_LEFT" /* SESSION_LEFT */:
        return "Case Left";
      case "SESSION_STOPPED" /* SESSION_STOPPED */:
        return "Case Ended";
      case "LOGGER_STARTED" /* LOGGER_STARTED */:
        return `Logger Started (${auditLog.context ?? "Unknown"})`;
      case "LOGGER_CONNECTED" /* LOGGER_CONNECTED */:
        return `Logger Connected (${auditLog.context ?? "Unknown"})`;
      case "LOGGER_DISCONNECTED" /* LOGGER_DISCONNECTED */:
        return `Logger Disconnected (${auditLog.context ?? "Unknown"})`;
      case "LOGGER_STOPPED" /* LOGGER_STOPPED */:
        return `Logger Stopped (${auditLog.context ?? "Unknown"})`;
      case "LOGGER_UNLINKED" /* LOGGER_UNLINKED */:
        return `Logger Unlinked (${auditLog.context ?? "Unknown"})`;
      case "TIMELINEENTRY_ADDED" /* TIMELINEENTRY_ADDED */:
        const newEntry = context.timeline.find((e) => e.id === auditLog.context);
        if (!newEntry) {
          return "Unknown timeline entry set";
        }
        return `${TimelineEntryTypeUtil.toUIString(newEntry.type, context.session.product, newEntry.annotations)} set to ${formatDateTime(
          newEntry.timestamp,
          {
            hour24: context.user.settings.force24hours,
            timezone: {
              show: context.user.settings.includeTimezone,
              offset: context.user.timezone,
              name: context.user.timezoneName
            }
          }
        )}`;
      case "TIMELINEENTRY_UPDATED" /* TIMELINEENTRY_UPDATED */:
        const updatedEntry = context.timeline.find((e) => e.id === auditLog.context);
        if (!updatedEntry) {
          return "Unknown timeline entry changed";
        }
        return `${TimelineEntryTypeUtil.toUIString(updatedEntry.type, context.session.product, updatedEntry.annotations)} changed to ${formatDateTime(
          updatedEntry.timestamp,
          {
            hour24: context.user.settings.force24hours,
            timezone: {
              show: context.user.settings.includeTimezone,
              offset: context.user.timezone,
              name: context.user.timezoneName
            }
          }
        )}`;
      case "TIMELINEENTRY_DELETED" /* TIMELINEENTRY_DELETED */:
        const deletedEntry = context.timeline.find((e) => e.id === auditLog.context);
        if (!deletedEntry) {
          return "Unknown timeline entry deleted";
        }
        return `${TimelineEntryTypeUtil.toUIString(deletedEntry.type, context.session.product, deletedEntry.annotations)} deleted`;
      case "ETA_SET" /* ETA_SET */: {
        const setEtaTimestamp = auditLog.contextDate ?? context.eta.find((e) => e.destinationType === auditLog.context)?.timestamp;
        if (!setEtaTimestamp || !auditLog.context) {
          return "Unknown ETA set";
        }
        return `ETA to ${DestinationTypeUtil.toUIString(auditLog.context)} set to ${formatDateTime(setEtaTimestamp, {
          hour24: context.user.settings.force24hours,
          timezone: {
            show: context.user.settings.includeTimezone,
            offset: context.user.timezone,
            name: context.user.timezoneName
          }
        })}`;
      }
      case "ETA_UPDATED" /* ETA_UPDATED */: {
        const updatedEtaTimestamp = auditLog.contextDate ?? context.eta.find((e) => e.destinationType === auditLog.context)?.timestamp;
        if (!updatedEtaTimestamp || !auditLog.context) {
          return "Unknown ETA changed";
        }
        return `ETA to ${DestinationTypeUtil.toUIString(auditLog.context)} changed to ${formatDateTime(updatedEtaTimestamp, {
          hour24: context.user.settings.force24hours,
          timezone: {
            show: context.user.settings.includeTimezone,
            offset: context.user.timezone,
            name: context.user.timezoneName
          }
        })}`;
      }
      case "ETA_DELETED" /* ETA_DELETED */: {
        const deletedEtaType = auditLog.context ?? context.eta.find((e) => e.deleted)?.destinationType;
        if (!deletedEtaType) {
          return "Unknown ETA deleted";
        }
        return `ETA to ${DestinationTypeUtil.toUIString(deletedEtaType)} deleted`;
      }
      case "FLIGHT_ADDED" /* FLIGHT_ADDED */:
        const addedFlight = context.flights.find((f) => f.id === auditLog.context);
        if (!addedFlight) {
          return "Unknown Flight added";
        }
        const addedDestinationTypeString = addedFlight.destinationType ? `to ${DestinationTypeUtil.toUIString(addedFlight.destinationType)}` : "";
        const addedDetailsString = addedFlight.origin?.code && addedFlight.destination?.code ? `(${addedFlight.ident}, ${addedFlight.origin.code} > ${addedFlight.destination.code})` : `(${addedFlight.ident})`;
        return `Added Flight ${addedDestinationTypeString} ${addedDetailsString}`;
      case "FLIGHT_DELETED" /* FLIGHT_DELETED */:
        const deletedFlight = context.flights.find((f) => f.id === auditLog.context);
        if (!deletedFlight) {
          return "Unknown Flight deleted";
        }
        const deletedDestinationTypeString = deletedFlight.destinationType ? `to ${DestinationTypeUtil.toUIString(deletedFlight.destinationType)}` : "";
        const deletedDetailsString = deletedFlight.origin?.code && deletedFlight.destination?.code ? `(${deletedFlight.ident}, ${deletedFlight.origin.code} > ${deletedFlight.destination.code})` : `(${deletedFlight.ident})`;
        return `Deleted Flight ${deletedDestinationTypeString} ${deletedDetailsString}`;
      case "ADVANCED_TRACKING_ACTIVATED" /* ADVANCED_TRACKING_ACTIVATED */:
        return `VantagePoint Tracker activated (${auditLog.context ?? "Unknown"})`;
      case "ADVANCED_TRACKING_DEACTIVATED" /* ADVANCED_TRACKING_DEACTIVATED */:
        return `VantagePoint Tracker deactivated (${auditLog.context ?? "Unknown"})`;
      case "UNOSID_SET" /* UNOSID_SET */:
        return `Set Donor ID (${auditLog.context ?? "Unknown"})`;
      case "UNOSID_DELETED" /* UNOSID_DELETED */:
        return "Deleted Donor ID";
      case "DONOR_CENTER_SET" /* DONOR_CENTER_SET */:
        return `Donor Center set to ${auditLog.context ?? "Unknown"}`;
      case "DONOR_CENTER_UPDATED" /* DONOR_CENTER_UPDATED */:
        return `Donor Center changed to ${auditLog.context ?? "Unknown"}`;
      case "DONOR_CENTER_DELETED" /* DONOR_CENTER_DELETED */:
        return "Donor Center removed";
      case "DESTINATION_SET" /* DESTINATION_SET */:
        return `Recipient Transplant Center set to ${context.destination?.name ?? "Unknown"}`;
      case "DESTINATION_UPDATED" /* DESTINATION_UPDATED */:
        return `Recipient Transplant Center set to ${context.destination?.name ?? "Unknown"}`;
      case "DESTINATION_DELETED" /* DESTINATION_DELETED */:
        return "Recipient Transplant Center removed";
      case "SHARED_SESSION_CREATED" /* SHARED_SESSION_CREATED */:
        return "Offer Link created";
      case "BREAKOUT_SESSION_CREATED" /* BREAKOUT_SESSION_CREATED */:
        const createdOrgan = "kidney" /* KIDNEY */;
        const createdOrganPosition = (() => {
          if (auditLog.metadata?.organPosition) {
            return auditLog.metadata.organPosition;
          }
          if (!auditLog.context) {
            return null;
          }
          if (context.session.childSessionIds?.LEFT === auditLog.context) {
            return "LEFT" /* LEFT */;
          }
          if (context.session.childSessionIds?.RIGHT === auditLog.context) {
            return "RIGHT" /* RIGHT */;
          }
          return null;
        })();
        if (!createdOrganPosition) {
          return "Breakout Case created";
        }
        return `${OrganTypeUtil.toUIString(createdOrgan, createdOrganPosition)} Breakout Case created`;
      case "BREAKOUT_SESSION_CANCELLED" /* BREAKOUT_SESSION_CANCELLED */:
        const cancelledOrgan = "kidney" /* KIDNEY */;
        const cancelledOrganPosition = auditLog.metadata?.organPosition;
        if (!cancelledOrganPosition) {
          return "Breakout Case cancelled";
        }
        return `${OrganTypeUtil.toUIString(cancelledOrgan, cancelledOrganPosition)} Breakout Case cancelled`;
      case "MATCHRUNID_SET" /* MATCHRUNID_SET */:
        return `Set Match Run Value (${auditLog.context ?? "Unknown"})`;
      case "MATCHRUNID_DELETED" /* MATCHRUNID_DELETED */:
        return "Deleted Match Run Value";
      case "MANUAL_IMPORT_SESSION_LOGS" /* MANUAL_IMPORT_SESSION_LOGS */:
        return "Manually Imported Session Logs";
      default:
        return "unknown";
    }
  }
};

// src/models/barcode-record.ts
var BarcodeInputMethod = /* @__PURE__ */ ((BarcodeInputMethod2) => {
  BarcodeInputMethod2["MANUAL"] = "MANUAL";
  BarcodeInputMethod2["SCAN"] = "SCAN";
  BarcodeInputMethod2["OCR"] = "OCR";
  return BarcodeInputMethod2;
})(BarcodeInputMethod || {});
var BarcodeInputMethodUtil = class {
  static toUIString(method) {
    switch (method) {
      case "MANUAL" /* MANUAL */:
        return "Manual";
      case "SCAN" /* SCAN */:
        return "Scan";
      case "OCR" /* OCR */:
        return "OCR";
      default:
        return "Unknown";
    }
  }
};

// src/models/birth-sex.ts
var BirthSex = /* @__PURE__ */ ((BirthSex2) => {
  BirthSex2["MALE"] = "MALE";
  BirthSex2["FEMALE"] = "FEMALE";
  BirthSex2["OTHER"] = "OTHER";
  return BirthSex2;
})(BirthSex || {});
var BirthSexUtil = class {
  static fromString(sexStr) {
    const normalized = sexStr.trim().toUpperCase();
    switch (normalized) {
      case "MALE":
      case "M":
        return "MALE" /* MALE */;
      case "FEMALE":
      case "F":
        return "FEMALE" /* FEMALE */;
      case "OTHER":
      case "O":
      case "X":
        return "OTHER" /* OTHER */;
      default:
        return void 0;
    }
  }
  static toUIString(sex) {
    switch (sex) {
      case "MALE" /* MALE */:
        return "Male";
      case "FEMALE" /* FEMALE */:
        return "Female";
      case "OTHER" /* OTHER */:
        return "Other";
      default:
        return "Unknown";
    }
  }
};

// src/models/blood-type.ts
var BloodType = /* @__PURE__ */ ((BloodType2) => {
  BloodType2["A_POSITIVE"] = "A+";
  BloodType2["A_NEGATIVE"] = "A-";
  BloodType2["A"] = "A";
  BloodType2["A1"] = "A1";
  BloodType2["A2"] = "A2";
  BloodType2["B_POSITIVE"] = "B+";
  BloodType2["B_NEGATIVE"] = "B-";
  BloodType2["B"] = "B";
  BloodType2["AB_POSITIVE"] = "AB+";
  BloodType2["AB_NEGATIVE"] = "AB-";
  BloodType2["AB"] = "AB";
  BloodType2["O_POSITIVE"] = "O+";
  BloodType2["O_NEGATIVE"] = "O-";
  BloodType2["O"] = "O";
  return BloodType2;
})(BloodType || {});
var BloodTypeUtil = class {
  /**
   * Parse a blood type string from PDF to BloodType enum
   * Handles variations like "A+", "A", "A2", etc.
   */
  static fromString(bloodTypeStr) {
    const normalized = bloodTypeStr.trim().toUpperCase();
    const exactMatch = Object.values(BloodType).find((bt) => bt === bloodTypeStr.trim());
    if (exactMatch) {
      return exactMatch;
    }
    switch (normalized) {
      case "A+":
      case "A POSITIVE":
        return "A+" /* A_POSITIVE */;
      case "A-":
      case "A NEGATIVE":
        return "A-" /* A_NEGATIVE */;
      case "A":
        return "A" /* A */;
      case "A1":
        return "A1" /* A1 */;
      case "A2":
        return "A2" /* A2 */;
      case "B+":
      case "B POSITIVE":
        return "B+" /* B_POSITIVE */;
      case "B-":
      case "B NEGATIVE":
        return "B-" /* B_NEGATIVE */;
      case "B":
        return "B" /* B */;
      case "AB+":
      case "AB POSITIVE":
        return "AB+" /* AB_POSITIVE */;
      case "AB-":
      case "AB NEGATIVE":
        return "AB-" /* AB_NEGATIVE */;
      case "AB":
        return "AB" /* AB */;
      case "O+":
      case "O POSITIVE":
        return "O+" /* O_POSITIVE */;
      case "O-":
      case "O NEGATIVE":
        return "O-" /* O_NEGATIVE */;
      case "O":
        return "O" /* O */;
      default:
        return void 0;
    }
  }
  static toUIString(type) {
    return type;
  }
};

// src/models/breakout-session-cancellation-reason.ts
var BreakoutSessionCancellationReason = /* @__PURE__ */ ((BreakoutSessionCancellationReason2) => {
  BreakoutSessionCancellationReason2["REALLOCATION"] = "REALLOCATION";
  BreakoutSessionCancellationReason2["WRONG_SETUP"] = "WRONG_SETUP";
  return BreakoutSessionCancellationReason2;
})(BreakoutSessionCancellationReason || {});

// src/models/cannulation-type.ts
var CannulationType = /* @__PURE__ */ ((CannulationType2) => {
  CannulationType2["TANRP"] = "TANRP";
  CannulationType2["ANRP"] = "ANRP";
  return CannulationType2;
})(CannulationType || {});
var CannulationTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "TANRP" /* TANRP */:
        return "TA-NRP";
      case "ANRP" /* ANRP */:
        return "A-NRP";
      default:
        return "unknown";
    }
  }
};

// src/models/messages/message-preview.ts
var MessagePreview = class {
  id;
  content;
  senderId;
  timestamp;
  attachments;
  constructor(params) {
    this.id = params.id;
    this.content = params.content;
    this.senderId = params.senderId;
    this.timestamp = toDate(params.timestamp) || /* @__PURE__ */ new Date();
    this.attachments = params.attachments;
  }
  toJSON() {
    return {
      id: this.id,
      content: this.content,
      senderId: this.senderId,
      timestamp: this.timestamp,
      attachments: this.attachments
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/chat/chat-settings.ts
var ChatSettings = class {
  allowNewMembers;
  adminOnly;
  disappearingMessages;
  constructor(params) {
    this.allowNewMembers = params.allowNewMembers;
    this.adminOnly = params.adminOnly;
    this.disappearingMessages = params.disappearingMessages;
  }
  toJSON() {
    return {
      allowNewMembers: this.allowNewMembers,
      adminOnly: this.adminOnly,
      disappearingMessages: this.disappearingMessages
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/chat/chat.ts
var Chat = class {
  id;
  sessionId;
  type;
  name;
  description;
  avatar;
  memberIds;
  activeMemberIds;
  createdAt;
  createdBy;
  updatedAt;
  lastMessage;
  settings;
  adminIds;
  archived;
  mutedBy;
  organizationId;
  meta;
  constructor(params) {
    this.id = params.id;
    this.sessionId = params.sessionId;
    this.type = params.type;
    this.name = params.name;
    this.description = params.description;
    this.avatar = params.avatar;
    this.memberIds = params.memberIds ?? [];
    this.activeMemberIds = params.activeMemberIds ?? [];
    this.createdBy = params.createdBy;
    this.adminIds = params.adminIds ?? [];
    this.archived = params.archived ?? false;
    this.mutedBy = params.mutedBy ?? [];
    this.organizationId = params.organizationId;
    this.meta = params.meta;
    this.lastMessage = params.lastMessage ? new MessagePreview(params.lastMessage) : void 0;
    this.settings = params.settings ? new ChatSettings(params.settings) : new ChatSettings({
      allowNewMembers: true,
      adminOnly: false,
      disappearingMessages: void 0
    });
    this.createdAt = toDate(params.createdAt) || /* @__PURE__ */ new Date();
    this.updatedAt = toDate(params.updatedAt);
  }
  toJSON() {
    return {
      id: this.id,
      sessionId: this.sessionId,
      type: this.type,
      name: this.name,
      description: this.description,
      avatar: this.avatar,
      memberIds: this.memberIds,
      activeMemberIds: this.activeMemberIds,
      createdAt: this.createdAt,
      createdBy: this.createdBy,
      updatedAt: this.updatedAt,
      lastMessage: this.lastMessage?.toJSON(),
      settings: this.settings.toJSON(),
      adminIds: this.adminIds,
      archived: this.archived,
      mutedBy: this.mutedBy,
      organizationId: this.organizationId,
      meta: this.meta
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var ChatConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Chat({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      lastMessage: data.lastMessage ? {
        ...data.lastMessage,
        timestamp: data.lastMessage.timestamp?.toDate()
      } : void 0
    });
  }
};

// src/models/chat/chat-member.ts
var ChatMember = class {
  userId;
  firstname;
  lastname;
  organizationId;
  organizationName;
  organizationRole;
  chatRole;
  joinedAt;
  leftAt;
  addedBy;
  constructor(params) {
    this.userId = params.userId;
    this.firstname = params.firstname;
    this.lastname = params.lastname;
    this.organizationId = params.organizationId;
    this.organizationName = params.organizationName;
    this.organizationRole = params.organizationRole;
    this.chatRole = params.chatRole;
    this.addedBy = params.addedBy;
    this.joinedAt = toDate(params.joinedAt) || /* @__PURE__ */ new Date();
    this.leftAt = toDate(params.leftAt);
  }
  toJSON() {
    return {
      userId: this.userId,
      firstname: this.firstname,
      lastname: this.lastname,
      organizationId: this.organizationId,
      organizationName: this.organizationName,
      organizationRole: this.organizationRole,
      chatRole: this.chatRole,
      joinedAt: this.joinedAt,
      leftAt: this.leftAt,
      addedBy: this.addedBy
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var ChatMemberConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new ChatMember({
      ...data,
      userId: doc.id,
      joinedAt: data.joinedAt?.toDate(),
      leftAt: data.leftAt?.toDate()
    });
  }
};

// src/models/chat/chat-role.ts
var ChatRole = /* @__PURE__ */ ((ChatRole2) => {
  ChatRole2["ADMIN"] = "ADMIN";
  ChatRole2["MEMBER"] = "MEMBER";
  return ChatRole2;
})(ChatRole || {});

// src/models/chat/chat-type.ts
var ChatType = /* @__PURE__ */ ((ChatType2) => {
  ChatType2["DIRECT"] = "DIRECT";
  ChatType2["GROUP"] = "GROUP";
  ChatType2["PUBLIC"] = "PUBLIC";
  ChatType2["ORGANIZATION"] = "ORGANIZATION";
  return ChatType2;
})(ChatType || {});

// src/models/cursor.ts
var ReadCursor = class {
  documentId;
  readAt;
  constructor(params) {
    this.documentId = params.documentId;
    this.readAt = toDate(params.readAt);
  }
  toJSON() {
    return {
      documentId: this.documentId,
      readAt: this.readAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/chat/user-chat.ts
var UserChat = class {
  userId;
  chatId;
  sessionId;
  type;
  name;
  displayName;
  avatar;
  memberIds;
  activeMemberIds;
  lastMessage;
  lastActivity;
  lastActivityAt;
  unreadCount;
  lastReadCursor;
  muted;
  mutedUntil;
  pinned;
  archived;
  organizationId;
  createdAt;
  updatedAt;
  constructor(params) {
    this.userId = params.userId;
    this.chatId = params.chatId;
    this.sessionId = params.sessionId;
    this.type = params.type;
    this.name = params.name;
    this.displayName = params.displayName;
    this.avatar = params.avatar;
    this.memberIds = params.memberIds ?? [];
    this.activeMemberIds = params.activeMemberIds ?? [];
    this.lastActivity = params.lastActivity;
    this.lastActivityAt = toDate(params.lastActivityAt);
    this.unreadCount = params.unreadCount ?? 0;
    this.lastReadCursor = params.lastReadCursor ? new ReadCursor(params.lastReadCursor) : void 0;
    this.muted = params.muted ?? false;
    this.pinned = params.pinned ?? false;
    this.archived = params.archived ?? false;
    this.organizationId = params.organizationId;
    this.lastMessage = params.lastMessage ? new MessagePreview(params.lastMessage) : void 0;
    this.mutedUntil = toDate(params.mutedUntil);
    this.createdAt = toDate(params.createdAt) || /* @__PURE__ */ new Date();
    this.updatedAt = toDate(params.updatedAt);
  }
  toJSON() {
    return {
      userId: this.userId,
      chatId: this.chatId,
      sessionId: this.sessionId,
      type: this.type,
      name: this.name,
      displayName: this.displayName,
      avatar: this.avatar,
      memberIds: this.memberIds,
      activeMemberIds: this.activeMemberIds,
      lastMessage: this.lastMessage?.toJSON(),
      lastActivity: this.lastActivity,
      lastActivityAt: this.lastActivityAt,
      unreadCount: this.unreadCount,
      lastReadCursor: this.lastReadCursor?.toJSON(),
      muted: this.muted,
      mutedUntil: this.mutedUntil,
      pinned: this.pinned,
      archived: this.archived,
      organizationId: this.organizationId,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var UserChatConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new UserChat({
      ...data,
      chatId: doc.id,
      lastReadCursor: data.lastReadCursor ? { documentId: data.lastReadCursor.documentId, readAt: data.lastReadCursor.readAt?.toDate() } : void 0,
      mutedUntil: data.mutedUntil?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      lastActivityAt: data.lastActivityAt?.toDate(),
      lastMessage: data.lastMessage ? {
        ...data.lastMessage,
        timestamp: data.lastMessage.timestamp?.toDate()
      } : void 0
    });
  }
};

// src/models/component-type.ts
var ComponentType = /* @__PURE__ */ ((ComponentType2) => {
  ComponentType2["SHIPPER"] = "shipper";
  ComponentType2["CANISTER"] = "canister";
  ComponentType2["SHERPACOOL"] = "sherpacool";
  ComponentType2["STERILE_GOODS"] = "sterile-goods";
  return ComponentType2;
})(ComponentType || {});
var ComponentTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "shipper" /* SHIPPER */:
        return "Shipper";
      case "canister" /* CANISTER */:
        return "Canister";
      case "sherpacool" /* SHERPACOOL */:
        return "SherpaCool";
      case "sterile-goods" /* STERILE_GOODS */:
        return "Sterile Goods";
      default:
        return "Component";
    }
  }
};

// src/models/logger-log-entry.ts
var LoggerLogEntry2 = class {
  /** Timestamp of the log entry */
  timestamp;
  /** Event type (e.g., 'reading', 'started', 'stopped') */
  event;
  /** Sensor data */
  data;
  constructor(params) {
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.event = params.event;
    this.data = params.data ?? {};
  }
  /** Whether this is a sensor data reading (vs event like started/stopped) */
  get isReading() {
    return this.event === "reading" || this.event == null;
  }
  toJSON() {
    return {
      timestamp: this.timestamp,
      event: this.event,
      data: this.data
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-reading.ts
var OrganReading = class {
  data;
  event;
  timestamp;
  constructor(params) {
    this.data = params.data;
    this.event = params.event;
    this.timestamp = typeof params.timestamp === "string" ? new Date(params.timestamp) : params.timestamp;
  }
  get isReading() {
    return this.event === "reading";
  }
  get isStartedEvent() {
    return this.event === "started";
  }
  get isStoppedEvent() {
    return this.event === "stopped";
  }
  /** Converts this deprecated OrganReading to a v7 LoggerLogEntry. */
  toLoggerLogEntry() {
    return new LoggerLogEntry2({
      timestamp: this.timestamp,
      event: this.event,
      data: {
        probe: this.data?.probe,
        ambient: this.data?.ambient,
        pressure: this.data?.pressure,
        pumpVoltage: this.data?.pumpVoltage,
        fluidPressure: this.data?.fluidPressure,
        flow: this.data?.flow,
        resistance: this.data?.resistance,
        averageFluidPressure: this.data?.averageFluidPressure,
        averageFlow: this.data?.averageFlow,
        averageResistance: this.data?.averageResistance,
        rssi: this.data?.rssi
      }
    });
  }
  toJSON() {
    return {
      data: this.data,
      event: this.event,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/reading.ts
var Reading = class {
  id;
  event;
  ambient;
  probe;
  pressure;
  pumpVoltage;
  timestamp;
  constructor(params) {
    this.id = params.id;
    this.event = params.event;
    this.ambient = params.ambient;
    this.probe = params.probe;
    this.pressure = params.pressure;
    this.pumpVoltage = params.pumpVoltage;
    this.timestamp = typeof params.timestamp === "string" ? new Date(params.timestamp) : params.timestamp;
  }
  get isReading() {
    return this.event === "reading";
  }
  get isStartedEvent() {
    return this.event === "started";
  }
  get isStoppedEvent() {
    return this.event === "stopped";
  }
  toOrganReading() {
    return new OrganReading({
      data: {
        ambient: this.ambient,
        probe: this.probe,
        pressure: this.pressure,
        pumpVoltage: this.pumpVoltage
      },
      event: this.event,
      timestamp: this.timestamp
    });
  }
  /** Converts this deprecated Reading to a v7 LoggerLogEntry. */
  toLoggerLogEntry() {
    return new LoggerLogEntry2({
      timestamp: this.timestamp,
      event: this.event,
      data: {
        probe: this.probe,
        ambient: this.ambient,
        pressure: this.pressure,
        pumpVoltage: this.pumpVoltage
      }
    });
  }
  toJSON() {
    return {
      event: this.event,
      ambient: this.ambient,
      probe: this.probe,
      pressure: this.pressure,
      pumpVoltage: this.pumpVoltage,
      timestamp: this.timestamp
    };
  }
  toReadingCompressed() {
    return new ReadingCompressed({
      e: this.event,
      a: this.ambient,
      p: this.probe,
      pr: this.pressure,
      pv: this.pumpVoltage,
      t: this.timestamp
    });
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var ReadingConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Reading({ ...data, id: doc.id, timestamp: data.timestamp?.toDate() });
  }
};

// src/models/deprecated/reading-compressed.ts
var ReadingCompressed = class {
  e;
  a;
  p;
  pr;
  pv;
  t;
  constructor(params) {
    this.e = params.e;
    this.a = params.a;
    this.p = params.p;
    this.pr = params.pr;
    this.pv = params.pv;
    this.t = typeof params.t === "string" ? new Date(params.t) : params.t;
  }
  toJSON() {
    return {
      e: this.e,
      a: this.a,
      p: this.p,
      pr: this.pr,
      pv: this.pv,
      t: this.t
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toReading() {
    return new Reading({
      event: this.e,
      ambient: this.a,
      probe: this.p,
      pressure: this.pr,
      pumpVoltage: this.pv,
      timestamp: this.t
    });
  }
};
var ReadingCompressedConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new ReadingCompressed({ ...data, t: data.t?.toDate() });
  }
};

// src/models/deprecated/log.ts
var Log = class {
  readings;
  constructor(params) {
    this.readings = params.readings ? params.readings.map((reading) => new ReadingCompressed(reading).toReading()) : [];
  }
  toJSON() {
    return {
      readings: this.readings.map((reading) => reading.toJSON())
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LogConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Log({
      readings: data.log?.map((reading) => new ReadingCompressed({ ...reading, t: reading.t?.toDate() })) ?? []
    });
  }
};

// src/models/logger-app-location.ts
var LoggerAppLocation = class {
  /** Document ID */
  id;
  /** Timestamp of the location reading */
  timestamp;
  /** Latitude in decimal degrees */
  latitude;
  /** Longitude in decimal degrees */
  longitude;
  /** Horizontal accuracy in meters */
  horizontalAccuracy;
  /** Vertical accuracy in meters */
  verticalAccuracy;
  /** Course/heading in degrees (0-360) */
  course;
  /** Speed in meters per second */
  speed;
  constructor(params) {
    this.id = params.id;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.latitude = params.latitude;
    this.longitude = params.longitude;
    this.horizontalAccuracy = params.horizontalAccuracy;
    this.verticalAccuracy = params.verticalAccuracy;
    this.course = params.course;
    this.speed = params.speed;
  }
  toJSON() {
    return {
      id: this.id,
      timestamp: this.timestamp,
      latitude: this.latitude,
      longitude: this.longitude,
      horizontalAccuracy: this.horizontalAccuracy,
      verticalAccuracy: this.verticalAccuracy,
      course: this.course,
      speed: this.speed
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerAppLocationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new LoggerAppLocation({
      ...data,
      id: doc.id,
      timestamp: data.timestamp?.toDate()
    });
  }
};

// src/models/deprecated/logger-location.ts
var LoggerLocation = class {
  latitude;
  longitude;
  horizontalAccuracy;
  verticalAccuracy;
  timestamp;
  constructor(params) {
    this.latitude = params.latitude;
    this.longitude = params.longitude;
    this.horizontalAccuracy = params.horizontalAccuracy ?? -1;
    this.verticalAccuracy = params.verticalAccuracy ?? -1;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
  }
  /** Converts this deprecated LoggerLocation to a v7 LoggerAppLocation. */
  toLoggerAppLocation() {
    return new LoggerAppLocation({
      latitude: this.latitude,
      longitude: this.longitude,
      horizontalAccuracy: this.horizontalAccuracy,
      verticalAccuracy: this.verticalAccuracy,
      timestamp: this.timestamp
    });
  }
  toJSON() {
    return {
      latitude: this.latitude,
      longitude: this.longitude,
      horizontalAccuracy: this.horizontalAccuracy,
      verticalAccuracy: this.verticalAccuracy,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerLocationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new LoggerLocation({ ...data, id: doc.id, timestamp: data.timestamp?.toDate() });
  }
};

// src/models/logger-connection.ts
var LoggerConnection = class {
  /** User ID of the connected user */
  userId;
  /** Session ID where the logger is connected */
  sessionId;
  /** UUID of the paired mobile device */
  pairedDeviceIdentifier;
  /** App version used for connection */
  appVersion;
  /** Device model name */
  device;
  /** Operating system name */
  os;
  /** Operating system version */
  osVersion;
  constructor(params) {
    this.userId = params.userId ?? params.connectedBy ?? "";
    this.sessionId = params.sessionId;
    this.pairedDeviceIdentifier = params.pairedDeviceIdentifier;
    this.appVersion = params.appVersion;
    this.device = params.device;
    this.os = params.os;
    this.osVersion = params.osVersion;
  }
  /**
   * Converts logger connection to a plain JSON object.
   * @returns Plain object representation of the connection state
   */
  toJSON() {
    return {
      userId: this.userId,
      sessionId: this.sessionId,
      pairedDeviceIdentifier: this.pairedDeviceIdentifier,
      appVersion: this.appVersion,
      device: this.device,
      os: this.os,
      osVersion: this.osVersion
    };
  }
  /**
   * Serializes logger connection to a JSON-safe object (removes class instances).
   * @returns JSON-safe plain object
   */
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-data.ts
var LoggerData = class {
  /** Probe temperature in degrees Celsius */
  probe;
  /** Ambient temperature in degrees Celsius */
  ambient;
  /** Barometric pressure in hPa/mbar (BAROguard only) */
  pressure;
  /** Battery voltage reading */
  batteryVoltage;
  /** Pump voltage reading (lung/BAROguard) */
  pumpVoltage;
  /** Raw fluid pressure reading (kidney) */
  fluidPressure;
  /** Raw flow rate reading (kidney) */
  flow;
  /** Raw resistance reading (kidney) */
  resistance;
  /** Averaged fluid pressure reading (kidney) */
  averageFluidPressure;
  /** Averaged flow rate reading (kidney) */
  averageFlow;
  /** Averaged resistance reading (kidney) */
  averageResistance;
  /** Bluetooth signal strength */
  rssi;
  /** Timestamp of the reading */
  timestamp;
  constructor(params) {
    this.probe = params.probe;
    this.ambient = params.ambient;
    this.pressure = params.pressure;
    this.batteryVoltage = params.batteryVoltage;
    this.pumpVoltage = params.pumpVoltage;
    this.fluidPressure = params.fluidPressure;
    this.flow = params.flow;
    this.resistance = params.resistance;
    this.averageFluidPressure = params.averageFluidPressure;
    this.averageFlow = params.averageFlow;
    this.averageResistance = params.averageResistance;
    this.rssi = params.rssi;
    this.timestamp = params.timestamp ? params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp) : void 0;
  }
  /**
   * Converts logger data to a plain JSON object.
   * @returns Plain object representation of the sensor data
   */
  toJSON() {
    return {
      probe: this.probe,
      ambient: this.ambient,
      pressure: this.pressure,
      batteryVoltage: this.batteryVoltage,
      pumpVoltage: this.pumpVoltage,
      fluidPressure: this.fluidPressure,
      flow: this.flow,
      resistance: this.resistance,
      averageFluidPressure: this.averageFluidPressure,
      averageFlow: this.averageFlow,
      averageResistance: this.averageResistance,
      rssi: this.rssi,
      timestamp: this.timestamp
    };
  }
  /**
   * Serializes logger data to a JSON-safe object (removes class instances).
   * @returns JSON-safe plain object
   */
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-data-range-edit-bound.ts
var LoggerDataRangeEditBound = class {
  /** Timeline entry type this bound is anchored to */
  timelineEntryType;
  /** Original timestamp from the timeline entry */
  timestamp;
  /** Manual override timestamp (takes precedence over timestamp) */
  overrideTimestamp;
  constructor(params) {
    this.timelineEntryType = params.timelineEntryType;
    this.timestamp = params.timestamp ? params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp) : void 0;
    this.overrideTimestamp = params.overrideTimestamp instanceof Date ? params.overrideTimestamp : params.overrideTimestamp ? new Date(params.overrideTimestamp) : void 0;
  }
  toJSON() {
    return {
      timelineEntryType: this.timelineEntryType,
      timestamp: this.timestamp,
      overrideTimestamp: this.overrideTimestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-data-range-edit-trim.ts
var LoggerDataRangeEditTrim = class {
  /** Start of the excluded time range (inclusive) */
  from;
  /** End of the excluded time range (inclusive) */
  to;
  constructor(params) {
    this.from = params.from ? params.from instanceof Date ? params.from : new Date(params.from) : void 0;
    this.to = params.to ? params.to instanceof Date ? params.to : new Date(params.to) : void 0;
  }
  toJSON() {
    return {
      from: this.from,
      to: this.to
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-data-range-edit.ts
var LoggerDataRangeEdit = class {
  /** Start and end bounds for the visible data range */
  bounds;
  /** Time ranges to exclude from report calculations */
  trims;
  /** Sensor reading interval override in seconds (overrides product-based default) */
  intervalOverride;
  constructor(params) {
    this.bounds = params.bounds ? {
      from: params.bounds.from.map((bound) => new LoggerDataRangeEditBound(bound)),
      to: params.bounds.to.map((bound) => new LoggerDataRangeEditBound(bound))
    } : {
      from: [],
      to: []
    };
    this.trims = params.trims?.map((trim) => new LoggerDataRangeEditTrim(trim)) ?? [];
    this.intervalOverride = params.intervalOverride;
  }
  /**
   * Resolves the outermost effective start and end bounds.
   *
   * Filters out bounds without any timestamp, then sorts ascending
   * to return the earliest bound from each side. Override timestamps
   * take precedence over original timestamps.
   *
   * @returns The earliest `from` and `to` bounds with valid timestamps
   */
  get outerBounds() {
    const filteredFromBounds = this.bounds.from.filter((b) => b.timestamp != null || b.overrideTimestamp != null);
    const filteredToBounds = this.bounds.to.filter((b) => b.timestamp != null || b.overrideTimestamp != null);
    const from = filteredFromBounds.sort((a, b) => {
      const aTime = (a.overrideTimestamp ?? a.timestamp).getTime();
      const bTime = (b.overrideTimestamp ?? b.timestamp).getTime();
      return aTime - bTime;
    })[0];
    const to = filteredToBounds.sort((a, b) => {
      const aTime = (a.overrideTimestamp ?? a.timestamp).getTime();
      const bTime = (b.overrideTimestamp ?? b.timestamp).getTime();
      return aTime - bTime;
    })[0];
    return {
      from,
      to
    };
  }
  toJSON() {
    return {
      bounds: {
        from: this.bounds.from.map((bound) => bound.toJSON()),
        to: this.bounds.to.map((bound) => bound.toJSON())
      },
      trims: this.trims.map((trim) => trim.toJSON()),
      intervalOverride: this.intervalOverride
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger-header.ts
var LoggerHeader = class {
  /** Launcher application identifier */
  launcher;
  /** Logging interval in seconds */
  interval;
  /** Start delay in seconds */
  delay;
  /** Time offset */
  offset;
  /** Whether daylight saving time was active */
  isDst;
  /** Firmware version (number from Firestore, string for display) */
  firmwareVersion;
  /** Number of times this logger has been deployed */
  deploymentCount;
  /** Time zone identifier (e.g. 'America/New_York') */
  timeZone;
  /** Date the logger configuration was created */
  creationDate;
  /** Date the logger started logging */
  startDate;
  constructor(params) {
    this.launcher = params.launcher;
    this.interval = params.interval;
    this.delay = params.delay;
    this.offset = params.offset;
    this.isDst = params.isDst;
    this.firmwareVersion = params.firmwareVersion;
    this.deploymentCount = params.deploymentCount;
    this.timeZone = params.timeZone;
    this.creationDate = typeof params.creationDate === "string" ? new Date(params.creationDate) : params.creationDate;
    this.startDate = typeof params.startDate === "string" ? new Date(params.startDate) : params.startDate;
  }
  /**
   * Converts logger header to a plain JSON object.
   * @returns Plain object representation of the logger header
   */
  toJSON() {
    return {
      launcher: this.launcher,
      interval: this.interval,
      delay: this.delay,
      offset: this.offset,
      isDst: this.isDst,
      firmwareVersion: this.firmwareVersion,
      deploymentCount: this.deploymentCount,
      timeZone: this.timeZone,
      creationDate: this.creationDate,
      startDate: this.startDate
    };
  }
  /**
   * Serializes logger header to a JSON-safe object (removes class instances).
   * @returns JSON-safe plain object
   */
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/session-report-data.ts
var SessionReportData = class {
  ambientAverage;
  ambientMaximum;
  ambientMinimum;
  probeAverage;
  probeMaximum;
  probeMinimum;
  pressureAverage;
  pressureMaximum;
  pressureMinimum;
  averageFluidPressureAverage;
  averageFluidPressureMaximum;
  averageFluidPressureMinimum;
  averageFlowAverage;
  averageFlowMinimum;
  averageFlowMaximum;
  averageResistanceAverage;
  averageResistanceMinimum;
  averageResistanceMaximum;
  pumpVoltageStart;
  pumpVoltageEnd;
  distance;
  distanceTotal;
  distanceDataSource;
  readingsCount;
  readingsCountTotal;
  loggerStartedAt;
  loggerStoppedAt;
  loggingDurationMillis;
  firstReading;
  lastReading;
  timeOnPump;
  timeInTransport;
  constructor(params) {
    this.ambientAverage = params.ambientAverage;
    this.ambientMaximum = params.ambientMaximum;
    this.ambientMinimum = params.ambientMinimum;
    this.probeAverage = params.probeAverage;
    this.probeMaximum = params.probeMaximum;
    this.probeMinimum = params.probeMinimum;
    this.pressureAverage = params.pressureAverage;
    this.pressureMaximum = params.pressureMaximum;
    this.pressureMinimum = params.pressureMinimum;
    this.averageFluidPressureAverage = params.averageFluidPressureAverage;
    this.averageFluidPressureMaximum = params.averageFluidPressureMaximum;
    this.averageFluidPressureMinimum = params.averageFluidPressureMinimum;
    this.averageFlowAverage = params.averageFlowAverage;
    this.averageFlowMaximum = params.averageFlowMaximum;
    this.averageFlowMinimum = params.averageFlowMinimum;
    this.averageResistanceAverage = params.averageResistanceAverage;
    this.averageResistanceMaximum = params.averageResistanceMaximum;
    this.averageResistanceMinimum = params.averageResistanceMinimum;
    this.pumpVoltageStart = params.pumpVoltageStart;
    this.pumpVoltageEnd = params.pumpVoltageEnd;
    this.distance = params.distance;
    this.distanceTotal = params.distanceTotal;
    this.distanceDataSource = params.distanceDataSource;
    this.readingsCount = params.readingsCount;
    this.readingsCountTotal = params.readingsCountTotal;
    this.loggingDurationMillis = params.loggingDurationMillis;
    this.timeInTransport = params.timeInTransport;
    this.loggerStartedAt = params.loggerStartedAt instanceof Date ? params.loggerStartedAt : params.loggerStartedAt ? new Date(params.loggerStartedAt) : void 0;
    this.loggerStoppedAt = params.loggerStoppedAt instanceof Date ? params.loggerStoppedAt : params.loggerStoppedAt ? new Date(params.loggerStoppedAt) : void 0;
    this.firstReading = params.firstReading instanceof Date ? params.firstReading : params.firstReading ? new Date(params.firstReading) : void 0;
    this.lastReading = params.lastReading instanceof Date ? params.lastReading : params.lastReading ? new Date(params.lastReading) : void 0;
    if (params.timeOnPump) {
      const startTimestamp = params.timeOnPump.startTimestamp instanceof Date ? params.timeOnPump.startTimestamp : new Date(params.timeOnPump.startTimestamp);
      const endTimestamp = params.timeOnPump.endTimestamp instanceof Date ? params.timeOnPump.endTimestamp : params.timeOnPump.endTimestamp ? new Date(params.timeOnPump.endTimestamp) : void 0;
      this.timeOnPump = {
        ...params.timeOnPump,
        startTimestamp,
        endTimestamp
      };
    }
  }
  toJSON() {
    return {
      ambientAverage: this.ambientAverage,
      ambientMaximum: this.ambientMaximum,
      ambientMinimum: this.ambientMinimum,
      probeAverage: this.probeAverage,
      probeMaximum: this.probeMaximum,
      probeMinimum: this.probeMinimum,
      pressureAverage: this.pressureAverage,
      pressureMaximum: this.pressureMaximum,
      pressureMinimum: this.pressureMinimum,
      averageFluidPressureAverage: this.averageFluidPressureAverage,
      averageFluidPressureMaximum: this.averageFluidPressureMaximum,
      averageFluidPressureMinimum: this.averageFluidPressureMinimum,
      averageFlowAverage: this.averageFlowAverage,
      averageFlowMaximum: this.averageFlowMaximum,
      averageFlowMinimum: this.averageFlowMinimum,
      averageResistanceAverage: this.averageResistanceAverage,
      averageResistanceMaximum: this.averageResistanceMaximum,
      averageResistanceMinimum: this.averageResistanceMinimum,
      pumpVoltageStart: this.pumpVoltageStart,
      pumpVoltageEnd: this.pumpVoltageEnd,
      distance: this.distance,
      distanceTotal: this.distanceTotal,
      distanceDataSource: this.distanceDataSource,
      readingsCount: this.readingsCount,
      readingsCountTotal: this.readingsCountTotal,
      loggerStartedAt: this.loggerStartedAt,
      loggerStoppedAt: this.loggerStoppedAt,
      loggingDurationMillis: this.loggingDurationMillis,
      firstReading: this.firstReading,
      lastReading: this.lastReading,
      timeOnPump: this.timeOnPump,
      timeInTransport: this.timeInTransport
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/logger.ts
var LoggerState = /* @__PURE__ */ ((LoggerState2) => {
  LoggerState2["LINKED"] = "LINKED";
  LoggerState2["STARTED"] = "STARTED";
  LoggerState2["PENDING_LAST_READ"] = "PENDING_LAST_READ";
  LoggerState2["STOPPED"] = "STOPPED";
  return LoggerState2;
})(LoggerState || {});
var Logger = class {
  /** Unique logger identifier (Firestore document ID) */
  id;
  /** Logger serial number (from physical device) */
  serialNo;
  /** Logger type (CX402, MX1105, CX460) */
  type;
  /** Passkey for logger pairing */
  passkey;
  /** Whether the logger data is publicly accessible via shared session */
  shared;
  /** Logger state */
  state;
  /** Product type */
  product;
  /** Logger configuration and firmware info */
  header;
  /** Current/last sensor data */
  data;
  /** Array of session IDs this logger has been used with */
  linkedSessions;
  /** Denormalized user IDs from all linked sessions (for security rules) */
  userIds;
  /** Denormalized organization IDs from all linked sessions (for security rules) */
  organizationIds;
  /** Denormalized area IDs from all linked sessions (for security rules) */
  areaIds;
  /** Denormalized territory IDs from all linked sessions (for security rules) */
  territoryIds;
  /** Active connection state (undefined when disconnected) */
  connection;
  /** Tive tracker ID for advanced tracking (v7+) */
  advancedTrackerId;
  /** Report data for external/customer reports */
  reportData;
  /** Report data for internal Paragonix use */
  internalReportData;
  /** Manual data range edits (trims, bounds) applied to readings */
  dataRangeEdit;
  /** Document creation timestamp */
  createdAt;
  /** Document last update timestamp */
  updatedAt;
  constructor(params) {
    this.id = params.id;
    this.serialNo = params.serialNo;
    this.type = params.type;
    this.passkey = params.passkey;
    this.shared = params.shared;
    this.state = params.state;
    this.product = params.product;
    this.header = params.header ? new LoggerHeader(params.header) : void 0;
    this.data = params.data ? new LoggerData(params.data) : void 0;
    this.linkedSessions = params.linkedSessions ?? [];
    this.userIds = params.userIds ?? [];
    this.organizationIds = params.organizationIds ?? [];
    this.areaIds = params.areaIds ?? [];
    this.territoryIds = params.territoryIds ?? [];
    this.connection = params.connection ? new LoggerConnection(params.connection) : void 0;
    this.advancedTrackerId = params.advancedTrackerId;
    this.reportData = params.reportData ? new SessionReportData(params.reportData) : void 0;
    this.internalReportData = params.internalReportData ? new SessionReportData(params.internalReportData) : void 0;
    this.dataRangeEdit = params.dataRangeEdit ? new LoggerDataRangeEdit(params.dataRangeEdit) : void 0;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : new Date(params.createdAt);
    this.updatedAt = params.updatedAt ? params.updatedAt instanceof Date ? params.updatedAt : new Date(params.updatedAt) : void 0;
  }
  toJSON() {
    return {
      id: this.id,
      serialNo: this.serialNo,
      type: this.type,
      passkey: this.passkey,
      shared: this.shared,
      state: this.state,
      product: this.product,
      header: this.header?.toJSON(),
      data: this.data?.toJSON(),
      linkedSessions: this.linkedSessions,
      userIds: this.userIds,
      organizationIds: this.organizationIds,
      areaIds: this.areaIds,
      territoryIds: this.territoryIds,
      connection: this.connection?.toJSON(),
      advancedTrackerId: this.advancedTrackerId,
      reportData: this.reportData?.toJSON(),
      internalReportData: this.internalReportData?.toJSON(),
      dataRangeEdit: this.dataRangeEdit?.toJSON(),
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Logger({
      ...data,
      id: doc.id,
      data: data.data ? {
        ...data.data,
        timestamp: data.data.timestamp?.toDate?.() ?? data.data.timestamp
      } : void 0,
      header: data.header ? {
        ...data.header,
        creationDate: data.header.creationDate?.toDate?.() ?? data.header.creationDate,
        startDate: data.header.startDate?.toDate?.() ?? data.header.startDate
      } : void 0,
      connection: data.connection ? {
        ...data.connection
      } : void 0,
      reportData: parseSessionReportData(data.reportData),
      internalReportData: parseSessionReportData(data.internalReportData),
      dataRangeEdit: parseDataRangeEdit(data.dataRangeEdit),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};
function parseSessionReportData(data) {
  return data ? {
    ...data,
    loggerStartedAt: data.loggerStartedAt?.toDate(),
    loggerStoppedAt: data.loggerStoppedAt?.toDate(),
    firstReading: data.firstReading?.toDate(),
    lastReading: data.lastReading?.toDate(),
    timeOnPump: data.timeOnPump ? {
      ...data.timeOnPump,
      startTimestamp: data.timeOnPump.startTimestamp?.toDate(),
      endTimestamp: data.timeOnPump.endTimestamp?.toDate()
    } : void 0
  } : void 0;
}
function parseDataRangeEdit(data) {
  return data ? {
    ...data,
    bounds: {
      from: data.bounds.from ? parseDataRangeEditBounds(data.bounds.from) : [],
      to: data.bounds.to ? parseDataRangeEditBounds(data.bounds.to) : []
    },
    trims: data.trims ? parseDataRangeEditTrims(data.trims) : void 0
  } : void 0;
}
function parseDataRangeEditBounds(data) {
  return data ? data.map((bound) => parseDataRangeEditBound(bound)) : void 0;
}
function parseDataRangeEditBound(data) {
  return data ? {
    ...data,
    timestamp: data.timestamp?.toDate(),
    overrideTimestamp: data.overrideTimestamp?.toDate()
  } : void 0;
}
function parseDataRangeEditTrims(data) {
  return data ? data.map((trim) => parseDataRangeEditTrim(trim)) : void 0;
}
function parseDataRangeEditTrim(data) {
  return data ? {
    ...data,
    from: data.from?.toDate(),
    to: data.to?.toDate()
  } : void 0;
}

// src/models/deprecated/organ-data-range-edit-bound.ts
var OrganDataRangeEditBound = class {
  timelineEntryType;
  timestamp;
  overrideTimestamp;
  constructor(params) {
    this.timelineEntryType = params.timelineEntryType;
    this.timestamp = params.timestamp ? params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp) : void 0;
    this.overrideTimestamp = params.overrideTimestamp instanceof Date ? params.overrideTimestamp : params.overrideTimestamp ? new Date(params.overrideTimestamp) : void 0;
  }
  toJSON() {
    return {
      timelineEntryType: this.timelineEntryType,
      timestamp: this.timestamp,
      overrideTimestamp: this.overrideTimestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-data-range-edit-trim.ts
var OrganDataRangeEditTrim = class {
  from;
  to;
  constructor(params) {
    this.from = params.from ? params.from instanceof Date ? params.from : new Date(params.from) : void 0;
    this.to = params.to ? params.to instanceof Date ? params.to : new Date(params.to) : void 0;
  }
  toJSON() {
    return {
      from: this.from,
      to: this.to
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-data-range-edit.ts
var OrganDataRangeEdit = class {
  bounds;
  trims;
  intervalOverride;
  constructor(params) {
    this.bounds = params.bounds ? {
      from: params.bounds.from.map((bound) => new OrganDataRangeEditBound(bound)),
      to: params.bounds.to.map((bound) => new OrganDataRangeEditBound(bound))
    } : {
      from: [],
      to: []
    };
    this.trims = params.trims?.map((trim) => new OrganDataRangeEditTrim(trim)) ?? [];
    this.intervalOverride = params.intervalOverride;
  }
  get outerBounds() {
    const filteredFromBounds = this.bounds.from.filter((b) => b.timestamp != null || b.overrideTimestamp != null);
    const filteredToBounds = this.bounds.to.filter((b) => b.timestamp != null || b.overrideTimestamp != null);
    const from = filteredFromBounds.sort((a, b) => {
      const aTime = (a.overrideTimestamp ?? a.timestamp).getTime();
      const bTime = (b.overrideTimestamp ?? b.timestamp).getTime();
      return aTime - bTime;
    })[0];
    const to = filteredToBounds.sort((a, b) => {
      const aTime = (a.overrideTimestamp ?? a.timestamp).getTime();
      const bTime = (b.overrideTimestamp ?? b.timestamp).getTime();
      return aTime - bTime;
    })[0];
    return {
      from,
      to
    };
  }
  toJSON() {
    return {
      bounds: {
        from: this.bounds.from.map((bound) => bound.toJSON()),
        to: this.bounds.to.map((bound) => bound.toJSON())
      },
      trims: this.trims.map((trim) => trim.toJSON()),
      intervalOverride: this.intervalOverride
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/organ-logger-header.ts
var OrganLoggerHeader = class {
  launcher;
  interval;
  delay;
  offset;
  isDst;
  firmwareVersion;
  deploymentCount;
  timeZone;
  creationDate;
  startDate;
  constructor(params) {
    this.launcher = params.launcher;
    this.interval = params.interval;
    this.delay = params.delay;
    this.offset = params.offset;
    this.isDst = params.isDst;
    this.firmwareVersion = params.firmwareVersion;
    this.deploymentCount = params.deploymentCount;
    this.timeZone = params.timeZone;
    this.creationDate = typeof params.creationDate === "string" ? new Date(params.creationDate) : params.creationDate;
    this.startDate = typeof params.startDate === "string" ? new Date(params.startDate) : params.startDate;
  }
  toJSON() {
    return {
      launcher: this.launcher,
      interval: this.interval,
      delay: this.delay,
      offset: this.offset,
      isDst: this.isDst,
      firmwareVersion: this.firmwareVersion,
      deploymentCount: this.deploymentCount,
      timeZone: this.timeZone,
      creationDate: this.creationDate,
      startDate: this.startDate
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-logger-connection.ts
var OrganLoggerConnection = class {
  connectedBy;
  pairedDeviceIdentifier;
  constructor(params) {
    this.connectedBy = params.connectedBy;
    this.pairedDeviceIdentifier = params.pairedDeviceIdentifier;
  }
  toJSON() {
    return {
      connectedBy: this.connectedBy,
      pairedDeviceIdentifier: this.pairedDeviceIdentifier
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-logger-data.ts
var OrganLoggerData = class {
  probe;
  ambient;
  pressure;
  pumpVoltage;
  fluidPressure;
  flow;
  resistance;
  averageFluidPressure;
  averageFlow;
  averageResistance;
  batteryVoltage;
  rssi;
  timestamp;
  constructor(params) {
    this.probe = params.probe;
    this.ambient = params.ambient;
    this.pressure = params.pressure;
    this.pumpVoltage = params.pumpVoltage;
    this.fluidPressure = params.fluidPressure;
    this.flow = params.flow;
    this.resistance = params.resistance;
    this.averageFluidPressure = params.averageFluidPressure;
    this.averageFlow = params.averageFlow;
    this.averageResistance = params.averageResistance;
    this.batteryVoltage = params.batteryVoltage;
    this.rssi = params.rssi;
    this.timestamp = typeof params.timestamp === "string" ? new Date(params.timestamp) : params.timestamp;
  }
  toJSON() {
    return {
      probe: this.probe,
      ambient: this.ambient,
      pressure: this.pressure,
      pumpVoltage: this.pumpVoltage,
      fluidPressure: this.fluidPressure,
      flow: this.flow,
      resistance: this.resistance,
      averageFluidPressure: this.averageFluidPressure,
      averageFlow: this.averageFlow,
      averageResistance: this.averageResistance,
      batteryVoltage: this.batteryVoltage,
      rssi: this.rssi,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ-logger.ts
var OrganLogger = class {
  serialNo;
  passkey;
  type;
  state;
  connection;
  data;
  header;
  constructor(params) {
    this.serialNo = params.serialNo;
    this.passkey = params.passkey;
    this.type = params.type;
    this.state = params.state;
    this.connection = params.connection ? new OrganLoggerConnection(params.connection) : void 0;
    this.data = params.data ? new OrganLoggerData(params.data) : void 0;
    this.header = params.header ? new OrganLoggerHeader(params.header) : void 0;
  }
  toJSON() {
    return {
      serialNo: this.serialNo,
      passkey: this.passkey,
      type: this.type,
      state: this.state,
      connection: this.connection?.toJSON(),
      data: this.data?.toJSON(),
      header: this.header?.toJSON()
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/deprecated/organ.ts
var Organ = class {
  id;
  referenceId;
  sessionId;
  product;
  organ;
  position;
  advancedTrackerId;
  logger;
  reportData;
  internalReportData;
  dataRangeEdit;
  constructor(params) {
    this.id = params.id;
    this.referenceId = params.referenceId;
    this.sessionId = params.sessionId;
    if (!params.product && !params.organ) {
      this.product = "sherpapak-cts" /* SHERPAPAK */;
      this.organ = "heart" /* HEART */;
    } else if (params.product && !params.organ) {
      this.product = params.product;
      this.organ = OrganTypeUtil.fromProductType(params.product) ?? "heart" /* HEART */;
    } else {
      this.product = params.product;
      this.organ = params.organ;
    }
    this.position = params.position;
    this.logger = params.logger ? new OrganLogger(params.logger) : void 0;
    this.advancedTrackerId = params.advancedTrackerId;
    this.reportData = params.reportData ? new SessionReportData(params.reportData) : void 0;
    this.internalReportData = params.internalReportData ? new SessionReportData(params.internalReportData) : void 0;
    this.dataRangeEdit = params.dataRangeEdit ? new OrganDataRangeEdit(params.dataRangeEdit) : void 0;
  }
  /** Converts this deprecated Organ to a v7 Logger with default values for irrelevant fields. */
  toLogger() {
    return new Logger({
      id: this.id,
      serialNo: this.logger?.serialNo ?? "",
      type: this.logger?.type ?? "",
      passkey: this.logger?.passkey ?? "",
      shared: false,
      product: this.product,
      linkedSessions: [this.sessionId],
      userIds: [],
      organizationIds: [],
      areaIds: [],
      territoryIds: [],
      data: this.logger?.data?.toJSON(),
      header: this.logger?.header?.toJSON(),
      connection: this.logger?.connection ? { ...this.logger.connection.toJSON(), sessionId: this.sessionId } : void 0,
      advancedTrackerId: this.advancedTrackerId,
      reportData: this.reportData?.toJSON(),
      internalReportData: this.internalReportData?.toJSON(),
      dataRangeEdit: this.dataRangeEdit?.toJSON(),
      createdAt: /* @__PURE__ */ new Date(0)
    });
  }
  toJSON() {
    return {
      id: this.id,
      referenceId: this.referenceId,
      sessionId: this.sessionId,
      product: this.product,
      organ: this.organ,
      position: this.position,
      logger: this.logger?.toJSON(),
      advancedTrackerId: this.advancedTrackerId,
      reportData: this.reportData?.toJSON(),
      internalReportData: this.internalReportData?.toJSON(),
      dataRangeEdit: this.dataRangeEdit?.toJSON()
    };
  }
};
var OrganConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Organ({
      ...data,
      id: doc.id,
      logger: parseOrganLogger(data.logger),
      reportData: parseSessionReportData2(data.reportData),
      internalReportData: parseSessionReportData2(data.internalReportData),
      dataRangeEdit: parseOrganDataRangeEdit(data.dataRangeEdit)
    });
  }
};
function parseOrganLogger(data) {
  return data ? {
    ...data,
    data: data.data ? parseOrganLoggerData(data.data) : void 0,
    header: data.header ? parseOrganLoggerHeader(data.header) : void 0
  } : void 0;
}
function parseOrganLoggerHeader(data) {
  return data ? {
    ...data,
    creationDate: data.creationDate?.toDate?.() ?? data.creationDate,
    startDate: data.startDate?.toDate?.() ?? data.startDate
  } : void 0;
}
function parseOrganLoggerData(data) {
  return data ? {
    ...data,
    timestamp: data.timestamp?.toDate()
  } : void 0;
}
function parseSessionReportData2(data) {
  return data ? {
    ...data,
    loggerStartedAt: data.loggerStartedAt?.toDate(),
    loggerStoppedAt: data.loggerStoppedAt?.toDate(),
    firstReading: data.firstReading?.toDate(),
    lastReading: data.lastReading?.toDate(),
    timeOnPump: data.timeOnPump ? {
      ...data.timeOnPump,
      startTimestamp: data.timeOnPump.startTimestamp?.toDate(),
      endTimestamp: data.timeOnPump.endTimestamp?.toDate()
    } : void 0
  } : void 0;
}
function parseOrganDataRangeEdit(data) {
  return data ? {
    ...data,
    bounds: {
      from: data.bounds.from ? parseOrganDataRangeEditBounds(data.bounds.from) : [],
      to: data.bounds.to ? parseOrganDataRangeEditBounds(data.bounds.to) : []
    },
    trims: data.trims ? parseOrganDataRangeEditTrims(data.trims) : void 0
  } : void 0;
}
function parseOrganDataRangeEditBounds(data) {
  return data ? data.map((bound) => parseOrganDataRangeEditBound(bound)) : void 0;
}
function parseOrganDataRangeEditBound(data) {
  return data ? {
    ...data,
    timestamp: data.timestamp?.toDate(),
    overrideTimestamp: data.overrideTimestamp?.toDate()
  } : void 0;
}
function parseOrganDataRangeEditTrims(data) {
  return data ? data.map((trim) => parseOrganDataRangeEditTrim(trim)) : void 0;
}
function parseOrganDataRangeEditTrim(data) {
  return data ? {
    ...data,
    from: data.from?.toDate(),
    to: data.to?.toDate()
  } : void 0;
}

// src/models/deprecated/organ-readings-page.ts
var OrganReadingsPage = class {
  id;
  logs;
  start;
  end;
  numberOfLogs;
  pageSize;
  order;
  constructor(params) {
    this.id = params.id;
    this.logs = params.logs?.map((log) => new OrganReading(log)) ?? [];
    this.numberOfLogs = params.numberOfLogs;
    this.pageSize = params.pageSize;
    this.order = params.order;
    this.start = typeof params.start === "string" ? new Date(params.start) : params.start;
    this.end = typeof params.end === "string" ? new Date(params.end) : params.end;
  }
  toJSON() {
    return {
      id: this.id,
      logs: this.logs.map((log) => log.toJSON()),
      start: this.start,
      end: this.end,
      numberOfLogs: this.numberOfLogs,
      pageSize: this.pageSize,
      order: this.order
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var OrganReadingsPageConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new OrganReadingsPage({
      ...data,
      logs: data.logs?.map((log) => new OrganReading({ ...log, timestamp: log.timestamp?.toDate() })) ?? [],
      start: data.start?.toDate(),
      end: data.end?.toDate()
    });
  }
};

// src/models/logger-app-pressure.ts
var LoggerAppPressure = class {
  /** Document ID */
  id;
  /** Timestamp of the pressure reading */
  timestamp;
  /** Barometric pressure in hPa/mbar */
  pressure;
  constructor(params) {
    this.id = params.id;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.pressure = params.pressure;
  }
  toJSON() {
    return {
      id: this.id,
      timestamp: this.timestamp,
      pressure: this.pressure
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerAppPressureConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new LoggerAppPressure({
      ...data,
      id: doc.id,
      timestamp: data.timestamp?.toDate()
    });
  }
};

// src/models/deprecated/pressure.ts
var Pressure = class {
  pressure;
  timestamp;
  constructor({ pressure, timestamp }) {
    this.pressure = pressure;
    this.timestamp = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  }
  /** Converts this deprecated Pressure to a v7 LoggerAppPressure. */
  toLoggerAppPressure() {
    return new LoggerAppPressure({
      timestamp: this.timestamp,
      pressure: this.pressure
    });
  }
  toJSON() {
    return {
      pressure: this.pressure,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var PressureConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Pressure({ ...data, timestamp: data.timestamp?.toDate() });
  }
};

// src/models/destination.ts
var Destination = class {
  id;
  name;
  handle;
  organs;
  types;
  isSessionOption;
  isOrganizationOption;
  /**
   * @deprecated Use DESTINATION_OWNERS data file for email notifications instead.
   */
  owner;
  placeId;
  location;
  address;
  addressComponents;
  country;
  region;
  countryCode;
  subdivisionCode;
  subdivisionName;
  areaId;
  territoryId;
  timezone;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.handle = params.handle;
    this.organs = params.organs ?? [];
    this.types = params.types ?? [];
    this.isSessionOption = params.isSessionOption ?? false;
    this.isOrganizationOption = params.isOrganizationOption ?? false;
    this.owner = params.owner;
    this.placeId = params.placeId;
    this.location = params.location;
    this.address = params.address;
    this.addressComponents = params.addressComponents;
    this.country = params.country;
    this.region = params.region;
    this.countryCode = params.countryCode;
    this.subdivisionCode = params.subdivisionCode;
    this.subdivisionName = params.subdivisionName;
    this.areaId = params.areaId;
    this.territoryId = params.territoryId;
    this.timezone = params.timezone;
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      handle: this.handle,
      organs: this.organs,
      types: this.types,
      isSessionOption: this.isSessionOption,
      isOrganizationOption: this.isOrganizationOption,
      owner: this.owner,
      placeId: this.placeId,
      location: this.location,
      address: this.address,
      addressComponents: this.addressComponents,
      country: this.country,
      region: this.region,
      countryCode: this.countryCode,
      subdivisionCode: this.subdivisionCode,
      subdivisionName: this.subdivisionName,
      areaId: this.areaId,
      territoryId: this.territoryId,
      timezone: this.timezone
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  /**
   * Converts this Destination to a RecipientCenter object.
   *
   * Extracts only the essential fields needed for storing recipient center
   * information on session documents.
   *
   * @returns A RecipientCenter with core location and identification fields
   */
  toRecipientCenter() {
    return {
      name: this.name,
      handle: this.handle,
      placeId: this.placeId,
      location: this.location,
      address: this.address,
      addressComponents: this.addressComponents,
      timezone: this.timezone
    };
  }
};
var DestinationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Destination({ ...data, id: doc.id });
  }
};

// src/models/distance-calculation-method-type.ts
var DistanceCalculationMethodType = /* @__PURE__ */ ((DistanceCalculationMethodType2) => {
  DistanceCalculationMethodType2["CENTER_TO_CENTER"] = "CENTER_TO_CENTER";
  DistanceCalculationMethodType2["GPS_TRACES"] = "GPS_TRACES";
  return DistanceCalculationMethodType2;
})(DistanceCalculationMethodType || {});
var DistanceCalculationMethodTypeUtil = class {
  static toUIString(method) {
    switch (method) {
      case "CENTER_TO_CENTER" /* CENTER_TO_CENTER */:
        return "Center to Center";
      case "GPS_TRACES" /* GPS_TRACES */:
        return "GPS Traces";
      default:
        return "Unknown";
    }
  }
  static toShortString(method) {
    switch (method) {
      case "CENTER_TO_CENTER" /* CENTER_TO_CENTER */:
        return "CTC";
      case "GPS_TRACES" /* GPS_TRACES */:
        return "GPS";
      default:
        return "Unknown";
    }
  }
};

// src/models/document.ts
var Document = class {
  id;
  name;
  sort;
  storageRef;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.sort = params.sort ?? 0;
    this.storageRef = params.storageRef;
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      sort: this.sort,
      storageRef: this.storageRef
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var DocumentConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Document({ ...data, id: doc.id });
  }
};

// src/models/donation-type.ts
var DonationType = /* @__PURE__ */ ((DonationType2) => {
  DonationType2["DBD"] = "DBD";
  DonationType2["DCD"] = "DCD";
  return DonationType2;
})(DonationType || {});
var DonationTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "DBD" /* DBD */:
        return "DBD";
      case "DCD" /* DCD */:
        return "DCD";
      default:
        return "Unknown";
    }
  }
};

// src/models/donor-data.ts
var DonorData = class {
  height;
  weight;
  age;
  ageRedacted;
  bmi;
  sex;
  bloodType;
  ethnicity;
  race;
  kdpi;
  causeOfDeath;
  mechanismOfInjury;
  circumstanceOfDeath;
  additionalDetails;
  createdAt;
  updatedAt;
  constructor(params) {
    this.height = params.height;
    this.weight = params.weight;
    this.age = params.age;
    this.ageRedacted = params.ageRedacted;
    this.bmi = params.bmi;
    this.sex = params.sex;
    this.bloodType = params.bloodType;
    this.ethnicity = params.ethnicity;
    this.race = params.race;
    this.kdpi = params.kdpi;
    this.causeOfDeath = params.causeOfDeath;
    this.mechanismOfInjury = params.mechanismOfInjury;
    this.circumstanceOfDeath = params.circumstanceOfDeath;
    this.additionalDetails = params.additionalDetails;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : void 0;
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : params.updatedAt ? new Date(params.updatedAt) : void 0;
  }
  toJSON() {
    return {
      height: this.height,
      weight: this.weight,
      age: this.age,
      ageRedacted: this.ageRedacted,
      bmi: this.bmi,
      sex: this.sex,
      bloodType: this.bloodType,
      ethnicity: this.ethnicity,
      race: this.race,
      kdpi: this.kdpi,
      causeOfDeath: this.causeOfDeath,
      mechanismOfInjury: this.mechanismOfInjury,
      circumstanceOfDeath: this.circumstanceOfDeath,
      additionalDetails: this.additionalDetails,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var DonorDataConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new DonorData({
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/draft-state-type.ts
var DraftStateType = /* @__PURE__ */ ((DraftStateType2) => {
  DraftStateType2["WAITING_FOR_UPLOAD"] = "WAITING_FOR_UPLOAD";
  DraftStateType2["PROCESSING"] = "PROCESSING";
  DraftStateType2["COMPLETE"] = "COMPLETE";
  DraftStateType2["ERROR"] = "ERROR";
  return DraftStateType2;
})(DraftStateType || {});

// src/models/offer-draft-type.ts
var OfferDraftType = /* @__PURE__ */ ((OfferDraftType2) => {
  OfferDraftType2["DONOR_SUMMARY_EXTRACTION"] = "DONOR_SUMMARY_EXTRACTION";
  OfferDraftType2["MANUAL"] = "MANUAL";
  return OfferDraftType2;
})(OfferDraftType || {});
var OfferDraftTypeUtil = class {
  /**
   * Converts an OfferDraftType to a human-readable UI string.
   *
   * @param type - The offer draft type to convert
   * @param capitalize - Whether to capitalize the first letter of each word (default: true)
   * @returns A formatted string representation of the draft type
   *
   * @example
   * ```typescript
   * OfferDraftTypeUtil.toUIString(OfferDraftType.DONOR_SUMMARY_EXTRACTION); // "Donor Summary Extraction"
   * OfferDraftTypeUtil.toUIString(OfferDraftType.DONOR_SUMMARY_EXTRACTION, false); // "donor summary extraction"
   * ```
   */
  static toUIString(type, capitalize = true) {
    switch (type) {
      case "DONOR_SUMMARY_EXTRACTION" /* DONOR_SUMMARY_EXTRACTION */:
        return capitalize ? "Donor Summary Extraction" : "donor summary extraction";
      case "MANUAL" /* MANUAL */:
        return capitalize ? "Manual" : "manual";
      default:
        return capitalize ? "Unknown" : "unknown";
    }
  }
};

// src/models/draft-state-validation.ts
var VALID_DRAFT_STATES = {
  ["DONOR_SUMMARY_EXTRACTION" /* DONOR_SUMMARY_EXTRACTION */]: [
    "WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */,
    "PROCESSING" /* PROCESSING */,
    "COMPLETE" /* COMPLETE */,
    "ERROR" /* ERROR */
  ],
  ["MANUAL" /* MANUAL */]: ["COMPLETE" /* COMPLETE */]
};
var VALID_STATE_TRANSITIONS = {
  ["WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */]: ["PROCESSING" /* PROCESSING */, "ERROR" /* ERROR */],
  ["PROCESSING" /* PROCESSING */]: ["COMPLETE" /* COMPLETE */, "ERROR" /* ERROR */],
  ["COMPLETE" /* COMPLETE */]: [],
  // Terminal state - no transitions allowed
  ["ERROR" /* ERROR */]: ["WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */]
  // Can retry from error
};
function isValidStateForDraftType(draftType, state) {
  const validStates = VALID_DRAFT_STATES[draftType];
  return validStates.includes(state);
}
function assertValidStateForDraftType(draftType, state) {
  if (!isValidStateForDraftType(draftType, state)) {
    const validStates = VALID_DRAFT_STATES[draftType].join(", ");
    throw new Error(`Invalid state "${state}" for draft type "${draftType}". Valid states: ${validStates}`);
  }
}
function isValidStateTransition(currentState, nextState) {
  const allowedTransitions = VALID_STATE_TRANSITIONS[currentState];
  return allowedTransitions.includes(nextState);
}
function getValidStatesForDraftType(draftType) {
  return VALID_DRAFT_STATES[draftType];
}

// src/models/extraction.ts
var Extraction = class {
  /** When a file was uploaded (optional) */
  uploadedAt;
  /** When processing completed (optional) */
  processedAt;
  /** Results from extraction process (optional) */
  result;
  /** Available snippet field names for PDF image previews */
  snippetFields;
  constructor(params) {
    this.uploadedAt = params.uploadedAt instanceof Date ? params.uploadedAt : params.uploadedAt ? new Date(params.uploadedAt) : void 0;
    this.processedAt = params.processedAt instanceof Date ? params.processedAt : params.processedAt ? new Date(params.processedAt) : void 0;
    this.result = params.result;
    this.snippetFields = params.snippetFields;
  }
  /**
   * Converts the Extraction instance to a plain JSON object.
   *
   * @returns Plain object representation of the extraction data
   */
  toJSON() {
    return {
      uploadedAt: this.uploadedAt,
      processedAt: this.processedAt,
      result: this.result,
      snippetFields: this.snippetFields
    };
  }
};

// src/models/draft.ts
var Draft = class _Draft {
  // ─────────────────────────────────────────────────────────────────────────────
  // Document ID
  // ─────────────────────────────────────────────────────────────────────────────
  id;
  // ─────────────────────────────────────────────────────────────────────────────
  // Session-Level Fields (ROOT - copied to session)
  // ─────────────────────────────────────────────────────────────────────────────
  organ;
  /** Session type (e.g., CLINICAL, TRAINING) */
  sessionType;
  donationType;
  opoHandle;
  opo;
  donorCenter;
  recipientCenter;
  matchRunId;
  offerStatus;
  rank;
  sequence;
  unosId;
  attachments;
  donorOr;
  recipientOr;
  preservationMethods;
  product;
  // ─────────────────────────────────────────────────────────────────────────────
  // Draft Lifecycle Fields (ROOT - NOT copied to session)
  // ─────────────────────────────────────────────────────────────────────────────
  type;
  state;
  userId;
  converted;
  sessionId;
  convertedAt;
  createdAt;
  // ─────────────────────────────────────────────────────────────────────────────
  // Extraction Data (NOT copied to session)
  // ─────────────────────────────────────────────────────────────────────────────
  extraction;
  constructor(params) {
    this.id = params.id;
    this.organ = params.organ;
    this.sessionType = params.sessionType;
    this.donationType = params.donationType;
    this.opoHandle = params.opoHandle;
    this.opo = params.opo;
    this.donorCenter = params.donorCenter;
    this.recipientCenter = params.recipientCenter;
    this.matchRunId = params.matchRunId;
    this.offerStatus = params.offerStatus;
    this.rank = params.rank;
    this.sequence = params.sequence;
    this.unosId = params.unosId;
    this.attachments = params.attachments?.filter((att) => att != null).map((att) => new AttachmentRef(att));
    this.donorOr = params.donorOr;
    this.recipientOr = params.recipientOr;
    this.preservationMethods = params.preservationMethods;
    this.product = params.product;
    this.type = params.type;
    this.state = params.state;
    this.userId = params.userId;
    this.converted = params.converted;
    this.sessionId = params.sessionId;
    this.convertedAt = params.convertedAt instanceof Date ? params.convertedAt : params.convertedAt ? new Date(params.convertedAt) : void 0;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : void 0;
    this.extraction = params.extraction ? new Extraction(params.extraction) : void 0;
    assertValidStateForDraftType(this.type, this.state);
  }
  toJSON() {
    return {
      id: this.id,
      // Session-level fields
      organ: this.organ,
      sessionType: this.sessionType,
      donationType: this.donationType,
      opoHandle: this.opoHandle,
      opo: this.opo,
      donorCenter: this.donorCenter,
      recipientCenter: this.recipientCenter,
      matchRunId: this.matchRunId,
      offerStatus: this.offerStatus,
      rank: this.rank,
      sequence: this.sequence,
      unosId: this.unosId,
      attachments: this.attachments?.map((attachment) => attachment.toJSON()),
      donorOr: this.donorOr,
      recipientOr: this.recipientOr,
      preservationMethods: this.preservationMethods,
      product: this.product,
      // Draft lifecycle fields
      type: this.type,
      state: this.state,
      userId: this.userId,
      converted: this.converted,
      sessionId: this.sessionId,
      convertedAt: this.convertedAt,
      createdAt: this.createdAt,
      // Extraction data (returns plain object or undefined)
      extraction: this.extraction?.toJSON()
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  /**
   * Creates a new Draft instance with an updated state.
   * Validates that the state transition is valid and that the new state
   * is allowed for this draft type.
   *
   * @param newState - The state to transition to
   * @returns A new Draft instance with the updated state
   * @throws {Error} If the state transition is invalid or the state is not allowed for this draft type
   *
   * @example
   * ```typescript
   * const draft = new Draft({
   *   id: '123',
   *   organ: OrganType.HEART,
   *   type: OfferDraftType.DONOR_SUMMARY_EXTRACTION,
   *   state: DraftStateType.WAITING_FOR_UPLOAD,
   *   userId: 'user-456',
   * });
   *
   * // Valid transition
   * const processingDraft = draft.withState(DraftStateType.PROCESSING);
   *
   * // Invalid transition - throws error
   * const completeDraft = draft.withState(DraftStateType.COMPLETE);
   * ```
   */
  withState(newState) {
    if (!isValidStateTransition(this.state, newState)) {
      throw new Error(
        `Invalid state transition from "${this.state}" to "${newState}". This transition is not allowed in the state machine.`
      );
    }
    return new _Draft({
      id: this.id,
      // Session-level fields
      organ: this.organ,
      sessionType: this.sessionType,
      donationType: this.donationType,
      opoHandle: this.opoHandle,
      opo: this.opo,
      donorCenter: this.donorCenter,
      recipientCenter: this.recipientCenter,
      matchRunId: this.matchRunId,
      offerStatus: this.offerStatus,
      rank: this.rank,
      sequence: this.sequence,
      unosId: this.unosId,
      attachments: this.attachments,
      donorOr: this.donorOr,
      recipientOr: this.recipientOr,
      preservationMethods: this.preservationMethods,
      product: this.product,
      // Draft lifecycle fields with updated state
      type: this.type,
      state: newState,
      userId: this.userId,
      converted: this.converted,
      sessionId: this.sessionId,
      convertedAt: this.convertedAt,
      createdAt: this.createdAt,
      // Extraction data
      extraction: this.extraction
    });
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Convenience accessors for extraction fields
  // ─────────────────────────────────────────────────────────────────────────────
  /** When the PDF was uploaded */
  get uploadedAt() {
    return this.extraction?.uploadedAt;
  }
  /** When the draft was processed */
  get processedAt() {
    return this.extraction?.processedAt;
  }
  /** Extraction result from PDF processing */
  get extractionResult() {
    return this.extraction?.result;
  }
  /** Confidence level of extraction */
  get confidence() {
    return this.extraction?.result?.confidence;
  }
  /** PDF type that was processed */
  get pdfType() {
    return this.extraction?.result?.pdfType;
  }
  /** Error message if processing failed */
  get errorMessage() {
    return this.extraction?.result?.error;
  }
};
var DraftConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    const extraction = data.extraction || {};
    return new Draft({
      id: doc.id,
      // Session-level fields from root
      organ: data.organ,
      sessionType: data.sessionType,
      donationType: data.donationType,
      opoHandle: data.opoHandle,
      opo: data.opo,
      donorCenter: data.donorCenter,
      recipientCenter: data.recipientCenter,
      matchRunId: data.matchRunId,
      offerStatus: data.offerStatus,
      rank: data.rank,
      sequence: data.sequence,
      unosId: data.unosId,
      attachments: data.attachments,
      donorOr: data.donorOr ? { ...data.donorOr, time: toDate(data.donorOr.time) } : void 0,
      recipientOr: data.recipientOr ? { ...data.recipientOr, time: toDate(data.recipientOr.time) } : void 0,
      preservationMethods: data.preservationMethods,
      product: data.product,
      // Draft lifecycle fields from root
      type: data.type,
      state: data.state,
      userId: data.userId,
      converted: data.converted,
      sessionId: data.sessionId,
      convertedAt: toDate(data.convertedAt),
      createdAt: toDate(data.createdAt),
      // Extraction data (only if any extraction fields exist)
      extraction: data.extraction ? {
        uploadedAt: toDate(extraction.uploadedAt),
        processedAt: toDate(extraction.processedAt),
        result: extraction.result,
        snippetFields: extraction.snippetFields
      } : void 0
    });
  }
};

// src/models/feature-type.ts
var FeatureType = /* @__PURE__ */ ((FeatureType2) => {
  FeatureType2["DEMO"] = "DEMO";
  FeatureType2["INV"] = "INV";
  FeatureType2["INV_ADMIN"] = "INV_ADMIN";
  FeatureType2["REPORTS"] = "REPORTS";
  return FeatureType2;
})(FeatureType || {});
var FeatureTypeUtil = class {
  static toUIString(featureType) {
    switch (featureType) {
      case "DEMO" /* DEMO */:
        return "Demo";
      case "INV" /* INV */:
        return "Inventory";
      case "INV_ADMIN" /* INV_ADMIN */:
        return "Inventory Admin";
      case "REPORTS" /* REPORTS */:
        return "Reports";
      default:
        return "Unknown";
    }
  }
};

// src/models/flight.ts
var Flight = class {
  id;
  flightId;
  ident;
  airline;
  state;
  cancelled;
  progressPercent;
  arrivalDelay;
  actualArrival;
  actualDeparture;
  departureDelay;
  estimatedDeparture;
  estimatedArrival;
  plannedArrival;
  plannedDeparture;
  origin;
  destination;
  destinationType;
  dataTimestamp;
  deleted;
  deletedAt;
  constructor(params) {
    this.id = params.id;
    this.flightId = params.flightId;
    this.ident = params.ident;
    this.airline = params.airline;
    this.state = params.state;
    this.cancelled = params.cancelled;
    this.progressPercent = params.progressPercent;
    this.arrivalDelay = params.arrivalDelay;
    this.departureDelay = params.departureDelay;
    this.origin = params.origin ? new Airport(params.origin) : void 0;
    this.destination = params.destination ? new Airport(params.destination) : void 0;
    this.destinationType = params.destinationType;
    this.dataTimestamp = params.dataTimestamp;
    this.deleted = params.deleted ?? false;
    this.deletedAt = params.deletedAt;
    this.actualArrival = params.actualArrival instanceof Date ? params.actualArrival : params.actualArrival ? new Date(params.actualArrival) : void 0;
    this.actualDeparture = params.actualDeparture instanceof Date ? params.actualDeparture : params.actualDeparture ? new Date(params.actualDeparture) : void 0;
    this.estimatedDeparture = params.estimatedDeparture instanceof Date ? params.estimatedDeparture : params.estimatedDeparture ? new Date(params.estimatedDeparture) : void 0;
    this.estimatedArrival = params.estimatedArrival instanceof Date ? params.estimatedArrival : params.estimatedArrival ? new Date(params.estimatedArrival) : void 0;
    this.plannedArrival = params.plannedArrival instanceof Date ? params.plannedArrival : params.plannedArrival ? new Date(params.plannedArrival) : void 0;
    this.plannedDeparture = params.plannedDeparture instanceof Date ? params.plannedDeparture : params.plannedDeparture ? new Date(params.plannedDeparture) : void 0;
  }
  toJSON() {
    return {
      id: this.id,
      flightId: this.flightId,
      ident: this.ident,
      airline: this.airline,
      state: this.state,
      cancelled: this.cancelled,
      progressPercent: this.progressPercent,
      arrivalDelay: this.arrivalDelay,
      actualArrival: this.actualArrival,
      actualDeparture: this.actualDeparture,
      departureDelay: this.departureDelay,
      estimatedDeparture: this.estimatedDeparture,
      estimatedArrival: this.estimatedArrival,
      plannedArrival: this.plannedArrival,
      plannedDeparture: this.plannedDeparture,
      origin: this.origin?.toJSON(),
      destination: this.destination?.toJSON(),
      destinationType: this.destinationType,
      dataTimestamp: this.dataTimestamp,
      deleted: this.deleted,
      deletedAt: this.deletedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var FlightConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Flight({
      ...data,
      id: doc.id,
      actualArrival: data.actualArrival?.toDate(),
      actualDeparture: data.actualDeparture?.toDate(),
      estimatedDeparture: data.estimatedDeparture?.toDate(),
      estimatedArrival: data.estimatedArrival?.toDate(),
      plannedArrival: data.plannedArrival?.toDate(),
      plannedDeparture: data.plannedDeparture?.toDate(),
      dataTimestamp: data.dataTimestamp?.toDate()
    });
  }
};

// src/models/flight-position.ts
var FlightPosition = class {
  flightId;
  latitude;
  longitude;
  heading;
  altitude;
  groundspeed;
  timestamp;
  constructor(params) {
    this.flightId = params.flightId;
    this.latitude = params.latitude;
    this.longitude = params.longitude;
    this.heading = params.heading;
    this.altitude = params.altitude;
    this.groundspeed = params.groundspeed;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : params.timestamp ? new Date(params.timestamp) : void 0;
  }
  toJSON() {
    return {
      flightId: this.flightId,
      latitude: this.latitude,
      longitude: this.longitude,
      heading: this.heading,
      altitude: this.altitude,
      groundspeed: this.groundspeed,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var FlightPositionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new FlightPosition({ ...data, id: doc.id, timestamp: data.timestamp?.toDate() });
  }
};

// src/models/flight-state.ts
var FlightState = /* @__PURE__ */ ((FlightState2) => {
  FlightState2["SCHEDULED"] = "SCHEDULED";
  FlightState2["IN_AIR"] = "IN_AIR";
  FlightState2["LANDED"] = "LANDED";
  FlightState2["CANCELLED"] = "CANCELLED";
  FlightState2["MANUAL"] = "MANUAL";
  return FlightState2;
})(FlightState || {});
var FlightStateUtil = class {
  static toUIString(state) {
    switch (state) {
      case "SCHEDULED" /* SCHEDULED */:
        return "scheduled";
      case "IN_AIR" /* IN_AIR */:
        return "in air";
      case "LANDED" /* LANDED */:
        return "landed";
      case "CANCELLED" /* CANCELLED */:
        return "cancelled";
      case "MANUAL" /* MANUAL */:
        return "manual";
      default:
        return "unknown";
    }
  }
};

// src/models/inventory-audit.ts
var InventoryAudit = class {
  id;
  locationId;
  status;
  openedBy;
  completedBy;
  timestamp;
  scannedBarcodes;
  newBarcodes;
  missingComponents;
  auditPeriod;
  openedAt;
  testRecord;
  constructor(params) {
    this.id = params.id;
    this.locationId = params.locationId;
    this.status = params.status;
    this.openedBy = params.openedBy;
    this.completedBy = params.completedBy;
    this.scannedBarcodes = params.scannedBarcodes ?? [];
    this.newBarcodes = params.newBarcodes ?? [];
    this.missingComponents = params.missingComponents ?? [];
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : params.timestamp ? new Date(params.timestamp) : /* @__PURE__ */ new Date();
    this.auditPeriod = params.auditPeriod;
    this.openedAt = params.openedAt instanceof Date ? params.openedAt : params.openedAt ? new Date(params.openedAt) : void 0;
    this.testRecord = params.testRecord ?? false;
  }
  toJSON() {
    return {
      id: this.id,
      locationId: this.locationId,
      status: this.status,
      openedBy: this.openedBy,
      completedBy: this.completedBy,
      timestamp: this.timestamp,
      scannedBarcodes: this.scannedBarcodes,
      newBarcodes: this.newBarcodes,
      missingComponents: this.missingComponents,
      auditPeriod: this.auditPeriod,
      openedAt: this.openedAt,
      testRecord: this.testRecord
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var InventoryAuditConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(snapshot) {
    const data = snapshot.data();
    const params = {
      id: snapshot.id,
      locationId: data.locationId,
      status: data.status,
      // Fall back so historical audits still read correctly.
      openedBy: data.openedBy,
      completedBy: data.completedBy ?? data.userId,
      timestamp: data.timestamp?.toDate(),
      scannedBarcodes: data.scannedBarcodes,
      newBarcodes: data.newBarcodes,
      missingComponents: data.missingComponents,
      auditPeriod: data.auditPeriod,
      openedAt: data.openedAt?.toDate(),
      testRecord: data.testRecord
    };
    return new InventoryAudit(params);
  }
};

// src/models/inventory-audit-status.ts
var InventoryAuditStatus = /* @__PURE__ */ ((InventoryAuditStatus2) => {
  InventoryAuditStatus2["OPEN"] = "OPEN";
  InventoryAuditStatus2["COMPLETED"] = "COMPLETED";
  InventoryAuditStatus2["SKIPPED"] = "SKIPPED";
  return InventoryAuditStatus2;
})(InventoryAuditStatus || {});

// src/models/inventory-component-state.ts
var DisposalReason = /* @__PURE__ */ ((DisposalReason2) => {
  DisposalReason2["LOST"] = "LOST";
  DisposalReason2["AUDIT"] = "AUDIT";
  return DisposalReason2;
})(DisposalReason || {});
var ComponentStateRecord = class {
  componentId;
  state;
  timestamp;
  transactionId;
  userId;
  locationId;
  reason;
  constructor(params) {
    this.componentId = params.componentId;
    this.state = params.state;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.transactionId = params.transactionId;
    this.userId = params.userId;
    this.locationId = params.locationId;
    this.reason = params.reason;
  }
  toJSON() {
    return {
      componentId: this.componentId,
      state: this.state,
      timestamp: this.timestamp,
      transactionId: this.transactionId,
      userId: this.userId,
      locationId: this.locationId,
      reason: this.reason
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var ComponentStateRecordConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(snapshot) {
    const data = snapshot.data();
    const params = {
      componentId: data.componentId,
      state: data.state,
      timestamp: data.timestamp?.toDate(),
      transactionId: data.transactionId,
      userId: data.userId,
      locationId: data.locationId,
      reason: data.reason
    };
    return new ComponentStateRecord(params);
  }
};

// src/models/inventory-component.ts
var InventoryComponent = class {
  id;
  product;
  component;
  serialNumber;
  lotNumber;
  manufacturingDate;
  expirationDate;
  addedAt;
  deployableAt;
  sku;
  /**
   * Current state of the component. Optional while the component has not yet been persisted.
   * Must be provided when constructing an `InventoryComponent` instance that represents
   * a record stored in Firestore.
   */
  currentState;
  testRecord;
  constructor(params) {
    this.id = params.id;
    this.product = params.product;
    this.component = params.component;
    this.serialNumber = params.serialNumber;
    this.lotNumber = params.lotNumber;
    this.sku = params.sku;
    this.manufacturingDate = params.manufacturingDate instanceof Date ? params.manufacturingDate : params.manufacturingDate ? new Date(params.manufacturingDate) : /* @__PURE__ */ new Date();
    this.expirationDate = params.expirationDate instanceof Date ? params.expirationDate : params.expirationDate ? new Date(params.expirationDate) : /* @__PURE__ */ new Date();
    this.addedAt = params.addedAt instanceof Date ? params.addedAt : params.addedAt ? new Date(params.addedAt) : /* @__PURE__ */ new Date();
    this.deployableAt = params.deployableAt ? params.deployableAt instanceof Date ? params.deployableAt : new Date(params.deployableAt) : void 0;
    this.currentState = new ComponentStateRecord({
      ...params.currentState,
      componentId: params.id,
      timestamp: params.currentState.timestamp instanceof Date ? params.currentState.timestamp : params.currentState.timestamp ? new Date(params.currentState.timestamp) : /* @__PURE__ */ new Date()
    });
    this.testRecord = params.testRecord ?? false;
  }
  toJSON() {
    const result = {
      id: this.id,
      product: this.product,
      component: this.component,
      serialNumber: this.serialNumber,
      lotNumber: this.lotNumber,
      manufacturingDate: this.manufacturingDate.toISOString(),
      expirationDate: this.expirationDate.toISOString(),
      addedAt: this.addedAt.toISOString(),
      ...this.deployableAt && { deployableAt: this.deployableAt.toISOString() },
      sku: this.sku
    };
    return {
      ...result,
      currentState: this.currentState?.toJSON(),
      testRecord: this.testRecord
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var InventoryComponentConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(snapshot) {
    const data = snapshot.data();
    if (!data.currentState || !data.currentState.state || !data.currentState.timestamp || !data.currentState.transactionId || !data.currentState.userId || data.currentState.locationId === void 0) {
      throw new Error("Current state or essential fields are missing in Firestore data.");
    }
    const params = {
      id: snapshot.id,
      product: data.product,
      component: data.component,
      serialNumber: data.serialNumber,
      lotNumber: data.lotNumber,
      manufacturingDate: data.manufacturingDate?.toDate(),
      expirationDate: data.expirationDate?.toDate(),
      addedAt: data.addedAt?.toDate(),
      ...data.deployableAt && { deployableAt: data.deployableAt.toDate() },
      sku: data.sku,
      currentState: {
        state: data.currentState.state,
        timestamp: data.currentState.timestamp.toDate(),
        transactionId: data.currentState.transactionId,
        userId: data.currentState.userId,
        locationId: data.currentState.locationId,
        componentId: snapshot.id,
        ...data.currentState.reason !== void 0 && { reason: data.currentState.reason }
      },
      testRecord: data.testRecord
    };
    return new InventoryComponent(params);
  }
};

// src/models/inventory-component-state-type.ts
var ComponentStateType = /* @__PURE__ */ ((ComponentStateType2) => {
  ComponentStateType2["STORED"] = "STORED";
  ComponentStateType2["IN_TRANSIT"] = "IN_TRANSIT";
  ComponentStateType2["DEPLOYED"] = "DEPLOYED";
  ComponentStateType2["DISPOSED"] = "DISPOSED";
  ComponentStateType2["RETURNING"] = "RETURNING";
  return ComponentStateType2;
})(ComponentStateType || {});
var ComponentStateTypeUtil = class {
  static toUIString(state) {
    switch (state) {
      case "STORED" /* STORED */:
        return "Stored";
      case "IN_TRANSIT" /* IN_TRANSIT */:
        return "In Transit";
      case "DEPLOYED" /* DEPLOYED */:
        return "Deployed";
      case "DISPOSED" /* DISPOSED */:
        return "Disposed";
      case "RETURNING" /* RETURNING */:
        return "Returning";
      default:
        return "Unknown";
    }
  }
};

// src/models/inventory-location.ts
var InventoryLocation = class {
  id;
  name;
  type;
  state;
  placeId;
  address;
  addressComponents;
  location;
  userIds;
  openAuditPeriod;
  openAuditId;
  createdAt;
  testRecord;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.placeId = params.placeId;
    this.address = params.address;
    this.addressComponents = params.addressComponents;
    this.location = params.location;
    this.type = params.type;
    this.state = params.state;
    this.userIds = params.userIds ?? [];
    this.openAuditPeriod = params.openAuditPeriod;
    this.openAuditId = params.openAuditId;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.testRecord = params.testRecord ?? false;
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      placeId: this.placeId,
      address: this.address,
      addressComponents: this.addressComponents,
      location: this.location,
      type: this.type,
      state: this.state,
      userIds: this.userIds,
      openAuditPeriod: this.openAuditPeriod,
      openAuditId: this.openAuditId,
      createdAt: this.createdAt,
      testRecord: this.testRecord
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var InventoryLocationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new InventoryLocation({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate(),
      testRecord: data.testRecord
    });
  }
};

// src/models/inventory-location-state.ts
var InventoryLocationState = /* @__PURE__ */ ((InventoryLocationState2) => {
  InventoryLocationState2["ACTIVE"] = "ACTIVE";
  InventoryLocationState2["INACTIVE"] = "INACTIVE";
  return InventoryLocationState2;
})(InventoryLocationState || {});
var InventoryLocationStateUtil = class {
  static toUIString(state) {
    switch (state) {
      case "ACTIVE" /* ACTIVE */:
        return "Active";
      case "INACTIVE" /* INACTIVE */:
        return "Inactive";
      default:
        return "Unknown";
    }
  }
};

// src/models/inventory-location-type.ts
var InventoryLocationType = /* @__PURE__ */ ((InventoryLocationType2) => {
  InventoryLocationType2["EMPLOYEE"] = "EMPLOYEE";
  InventoryLocationType2["AMEX"] = "AMEX";
  InventoryLocationType2["LOCKER"] = "LOCKER";
  return InventoryLocationType2;
})(InventoryLocationType || {});
var InventoryLocationTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "EMPLOYEE" /* EMPLOYEE */:
        return "Employee";
      case "AMEX" /* AMEX */:
        return "AMEX";
      case "LOCKER" /* LOCKER */:
        return "Locker";
      default:
        return "Unknown";
    }
  }
};

// src/models/inventory-transaction.ts
var InventoryTransaction = class {
  id;
  timestamp;
  type;
  userId;
  sourceLocationId;
  targetLocationId;
  codes;
  componentIds;
  state;
  confirmation;
  donorId;
  testRecord;
  trigger;
  constructor(params) {
    this.id = params.id;
    this.type = params.type;
    this.userId = params.userId;
    this.sourceLocationId = params.sourceLocationId;
    this.targetLocationId = params.targetLocationId;
    this.codes = params.codes;
    this.componentIds = params.componentIds;
    this.state = params.state;
    this.confirmation = params.confirmation;
    this.donorId = params.donorId;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.testRecord = params.testRecord ?? false;
    this.trigger = params.trigger;
  }
  toJSON() {
    return {
      id: this.id,
      timestamp: this.timestamp,
      type: this.type,
      userId: this.userId,
      sourceLocationId: this.sourceLocationId,
      targetLocationId: this.targetLocationId,
      codes: this.codes,
      componentIds: this.componentIds,
      state: this.state,
      confirmation: this.confirmation,
      donorId: this.donorId,
      testRecord: this.testRecord,
      trigger: this.trigger
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var InventoryTransactionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(snapshot) {
    const data = snapshot.data();
    const params = {
      id: snapshot.id,
      timestamp: data.timestamp?.toDate(),
      type: data.type,
      userId: data.userId,
      sourceLocationId: data.sourceLocationId,
      targetLocationId: data.targetLocationId,
      codes: data.codes,
      componentIds: data.componentIds,
      state: data.state,
      confirmation: data.confirmation,
      donorId: data.donorId,
      testRecord: data.testRecord,
      trigger: data.trigger
    };
    return new InventoryTransaction(params);
  }
};

// src/models/inventory-transaction-state-type.ts
var InventoryTransactionStateType = /* @__PURE__ */ ((InventoryTransactionStateType2) => {
  InventoryTransactionStateType2["IN_TRANSIT"] = "IN_TRANSIT";
  InventoryTransactionStateType2["CANCELLED"] = "CANCELLED";
  InventoryTransactionStateType2["CONFIRMED"] = "CONFIRMED";
  return InventoryTransactionStateType2;
})(InventoryTransactionStateType || {});
var InventoryTransactionStateTypeUtil = class {
  static toUIString(state) {
    switch (state) {
      case "IN_TRANSIT" /* IN_TRANSIT */:
        return "In Transit";
      case "CONFIRMED" /* CONFIRMED */:
        return "Confirmed";
      case "CANCELLED" /* CANCELLED */:
        return "Cancelled";
      default:
        return "Unknown";
    }
  }
};

// src/models/inventory-transaction-trigger.ts
var InventoryTransactionTrigger = /* @__PURE__ */ ((InventoryTransactionTrigger2) => {
  InventoryTransactionTrigger2["APP"] = "APP";
  InventoryTransactionTrigger2["CORRECTION"] = "CORRECTION";
  InventoryTransactionTrigger2["AUDIT"] = "AUDIT";
  return InventoryTransactionTrigger2;
})(InventoryTransactionTrigger || {});
var InventoryTransactionTriggerUtil = class {
  static toUIString(trigger) {
    switch (trigger) {
      case "APP" /* APP */:
        return "App";
      case "CORRECTION" /* CORRECTION */:
        return "Correction";
      case "AUDIT" /* AUDIT */:
        return "Audit";
      default:
        return "Unknown";
    }
  }
};

// src/models/inventory-transaction-type.ts
var InventoryTransactionType = /* @__PURE__ */ ((InventoryTransactionType2) => {
  InventoryTransactionType2["RECEIVE"] = "RECEIVE";
  InventoryTransactionType2["TRANSFER"] = "TRANSFER";
  InventoryTransactionType2["DEPLOY"] = "DEPLOY";
  InventoryTransactionType2["DISPOSE"] = "DISPOSE";
  return InventoryTransactionType2;
})(InventoryTransactionType || {});
var InventoryTransactionTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "RECEIVE" /* RECEIVE */:
        return "Receive";
      case "TRANSFER" /* TRANSFER */:
        return "Transfer";
      case "DEPLOY" /* DEPLOY */:
        return "Deploy";
      case "DISPOSE" /* DISPOSE */:
        return "Dispose";
      default:
        return "Unknown";
    }
  }
};

// src/models/logger-app-log-page.ts
var LoggerAppLogPage = class {
  /** Document ID (page identifier) */
  id;
  /** Page order number (0-indexed) */
  order;
  /** Timestamp of the first log entry in this page */
  start;
  /** Timestamp of the last log entry in this page */
  end;
  /** Maximum entries per page (typically 500) */
  pageSize;
  /** Actual number of log entries in this page */
  numberOfLogs;
  /** Array of log entries */
  logs;
  constructor(params) {
    this.id = params.id;
    this.order = params.order;
    this.start = params.start instanceof Date ? params.start : new Date(params.start);
    this.end = params.end instanceof Date ? params.end : new Date(params.end);
    this.pageSize = params.pageSize;
    this.numberOfLogs = params.numberOfLogs;
    this.logs = params.logs?.map((log) => new LoggerLogEntry2(log)) ?? [];
  }
  toJSON() {
    return {
      id: this.id,
      order: this.order,
      start: this.start,
      end: this.end,
      pageSize: this.pageSize,
      numberOfLogs: this.numberOfLogs,
      logs: this.logs.map((log) => log.toJSON())
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var LoggerAppLogPageConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new LoggerAppLogPage({
      ...data,
      id: doc.id,
      start: data.start?.toDate(),
      end: data.end?.toDate(),
      logs: data.logs?.map((log) => ({
        ...log,
        timestamp: log.timestamp?.toDate()
      })) ?? []
    });
  }
};

// src/models/logger-chart.ts
var LoggerChartPoint = class {
  timestamp;
  probe;
  ambient;
  airPressure;
  fluidPressure;
  flow;
  resistance;
  constructor(params) {
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.probe = params.probe;
    this.ambient = params.ambient;
    this.airPressure = params.airPressure;
    this.fluidPressure = params.fluidPressure;
    this.flow = params.flow;
    this.resistance = params.resistance;
  }
};
var LoggerChartData = class {
  start;
  end;
  pointCount;
  points;
  updatedAt;
  constructor(params) {
    this.start = params.start instanceof Date ? params.start : new Date(params.start);
    this.end = params.end instanceof Date ? params.end : new Date(params.end);
    this.pointCount = params.pointCount;
    this.points = params.points?.map((p) => new LoggerChartPoint(p)) ?? [];
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : new Date(params.updatedAt);
  }
};
var LoggerChartDataConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new LoggerChartData({
      start: data.start?.toDate(),
      end: data.end?.toDate(),
      pointCount: data.pointCount,
      updatedAt: data.updatedAt?.toDate(),
      points: data.points?.map((p) => ({
        ...p,
        timestamp: p.timestamp?.toDate()
      })) ?? []
    });
  }
};

// src/models/lung-position.ts
var LungPositionType = /* @__PURE__ */ ((LungPositionType2) => {
  LungPositionType2["SUPINE"] = "SUPINE";
  LungPositionType2["PRONE"] = "PRONE";
  return LungPositionType2;
})(LungPositionType || {});
var LungPositionTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "SUPINE" /* SUPINE */:
        return "Supine";
      case "PRONE" /* PRONE */:
        return "Prone";
      default:
        return "unknown";
    }
  }
};

// src/models/messages/message-mention.ts
var MessageMentionType = /* @__PURE__ */ ((MessageMentionType2) => {
  MessageMentionType2["USER"] = "USER";
  MessageMentionType2["ALL"] = "ALL";
  return MessageMentionType2;
})(MessageMentionType || {});
var MessageMention = class {
  type;
  userId;
  start;
  length;
  constructor(params) {
    this.type = params.type;
    this.userId = params.userId;
    this.start = params.start;
    this.length = params.length;
  }
  toJSON() {
    return {
      type: this.type,
      // Only include userId when set so @all entries stay clean in Firestore.
      ...this.userId !== void 0 && { userId: this.userId },
      start: this.start,
      length: this.length
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/messages/message-reactions.ts
var MessageReaction = class {
  emoji;
  updatedAt;
  constructor(params) {
    this.emoji = params.emoji;
    this.updatedAt = toDate(params.updatedAt) || /* @__PURE__ */ new Date();
  }
  toJSON() {
    return {
      emoji: this.emoji,
      updatedAt: this.updatedAt
    };
  }
};
var MessageReactions = class {
  reactions;
  constructor(params = {}) {
    this.reactions = {};
    for (const [userId, reaction] of Object.entries(params ?? {})) {
      if (!reaction?.emoji) continue;
      this.reactions[userId] = new MessageReaction(reaction);
    }
  }
  has(userId) {
    return this.reactions?.[userId] != null;
  }
  get(userId) {
    return this.reactions?.[userId];
  }
  toJSON() {
    return Object.fromEntries(
      Object.entries(this.reactions).map(([userId, reaction]) => [userId, reaction.toJSON()])
    );
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/messages/message-type.ts
var MessageType = /* @__PURE__ */ ((MessageType2) => {
  MessageType2["TEXT"] = "TEXT";
  MessageType2["SYSTEM"] = "SYSTEM";
  return MessageType2;
})(MessageType || {});

// src/models/messages/message.ts
var Message = class {
  id;
  parentId;
  senderId;
  createdAt;
  content;
  attachments;
  mentionRanges;
  reactions;
  editedAt;
  deletedAt;
  deletedBy;
  deleted;
  deliveryStatus;
  type;
  systemEvent;
  timestamp;
  constructor(params) {
    this.id = params.id;
    this.parentId = params.parentId;
    this.senderId = params.senderId;
    this.createdAt = toDate(params.createdAt);
    this.content = params.content;
    this.attachments = params.attachments?.filter((a) => a != null).map((a) => new AttachmentRef(a)) ?? [];
    this.mentionRanges = params.mentionRanges?.filter((m) => m != null).map((m) => new MessageMention(m)) ?? [];
    this.reactions = new MessageReactions(params.reactions);
    this.editedAt = toDate(params.editedAt);
    this.deletedAt = toDate(params.deletedAt);
    this.deletedBy = params.deletedBy;
    this.deleted = params.deleted;
    this.deliveryStatus = params.deliveryStatus;
    this.type = params.type;
    this.systemEvent = params.systemEvent;
    this.timestamp = toDate(params.timestamp) || toDate(params.createdAt) || /* @__PURE__ */ new Date();
  }
  toPreview() {
    const attachments = this.attachments.length > 0 ? {
      images: this.attachments.filter((a) => a.type === "IMAGE" /* IMAGE */).length,
      videos: this.attachments.filter((a) => a.type === "VIDEO" /* VIDEO */).length,
      documents: this.attachments.filter((a) => a.type === "DOCUMENT" /* DOCUMENT */).length
    } : void 0;
    return new MessagePreview({
      id: this.id,
      content: this.content,
      senderId: this.senderId,
      timestamp: this.timestamp,
      attachments
    });
  }
  toJSON() {
    return {
      id: this.id,
      parentId: this.parentId,
      senderId: this.senderId,
      createdAt: this.createdAt,
      content: this.content,
      attachments: this.attachments.map((a) => a.toJSON()),
      mentionRanges: this.mentionRanges.map((m) => m.toJSON()),
      reactions: this.reactions.toJSON(),
      editedAt: this.editedAt,
      deletedAt: this.deletedAt,
      deletedBy: this.deletedBy,
      deleted: this.deleted,
      deliveryStatus: this.deliveryStatus,
      type: this.type,
      systemEvent: this.systemEvent,
      timestamp: this.timestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var MessageConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    const createdAt = data.createdAt?.toDate();
    const timestamp = data.timestamp?.toDate() || createdAt;
    return new Message({
      ...data,
      id: doc.id,
      timestamp,
      createdAt,
      type: data.type ?? "TEXT" /* TEXT */,
      editedAt: data.editedAt?.toDate(),
      deletedAt: data.deletedAt?.toDate()
    });
  }
};

// src/models/messages/message-delivery-status.ts
var MessageDeliveryStatus = /* @__PURE__ */ ((MessageDeliveryStatus2) => {
  MessageDeliveryStatus2["PENDING"] = "PENDING";
  MessageDeliveryStatus2["SENT"] = "SENT";
  MessageDeliveryStatus2["DELIVERED"] = "DELIVERED";
  MessageDeliveryStatus2["READ"] = "READ";
  return MessageDeliveryStatus2;
})(MessageDeliveryStatus || {});

// src/models/messages/message-receipt.ts
var MessageReceipt = class {
  userId;
  receivedAt;
  readAt;
  constructor(params) {
    this.userId = params.userId;
    this.receivedAt = toDate(params.receivedAt);
    this.readAt = toDate(params.readAt);
  }
  toJSON() {
    return {
      userId: this.userId,
      receivedAt: this.receivedAt,
      readAt: this.readAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var MessageReceiptConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new MessageReceipt({
      userId: doc.id,
      receivedAt: data.receivedAt?.toDate(),
      readAt: data.readAt?.toDate()
    });
  }
};

// src/models/messages/system-event.ts
var SystemEvent = /* @__PURE__ */ ((SystemEvent2) => {
  SystemEvent2["MEMBER_ADDED"] = "MEMBER_ADDED";
  SystemEvent2["MEMBER_REMOVED"] = "MEMBER_REMOVED";
  SystemEvent2["MEMBER_JOINED"] = "MEMBER_JOINED";
  SystemEvent2["MEMBER_LEFT"] = "MEMBER_LEFT";
  SystemEvent2["MEMBER_LEFT_SESSION"] = "MEMBER_LEFT_SESSION";
  SystemEvent2["CHAT_NAME_UPDATED"] = "CHAT_NAME_UPDATED";
  SystemEvent2["CHAT_DESCRIPTION_UPDATED"] = "CHAT_DESCRIPTION_UPDATED";
  return SystemEvent2;
})(SystemEvent || {});
var SYSTEM_SENDER_ID = "SYSTEM";

// src/models/meta.ts
var META_REQUIRED_FIELDS = [
  "userId",
  "deviceIdentifier",
  "timestampUser",
  "timezone",
  "language",
  "locale",
  "device",
  "os",
  "appVersion",
  "osVersion",
  "appId"
];
var META_OPTIONAL_FIELDS = ["viaCloudFunction", "cloudFunctionVersion"];
var META_ALL_FIELDS = [...META_REQUIRED_FIELDS, ...META_OPTIONAL_FIELDS];
function validateMeta(meta) {
  const missingFields = [];
  const invalidFields = [];
  if (meta == null) {
    return {
      valid: false,
      missingFields: [...META_REQUIRED_FIELDS],
      invalidFields: [],
      error: "Meta object is required but was null or undefined"
    };
  }
  if (typeof meta !== "object") {
    return {
      valid: false,
      missingFields: [...META_REQUIRED_FIELDS],
      invalidFields: [],
      error: `Meta must be an object, received ${typeof meta}`
    };
  }
  const metaObj = meta;
  for (const field of META_REQUIRED_FIELDS) {
    const value = metaObj[field];
    if (value === void 0 || value === null) {
      missingFields.push(field);
      continue;
    }
    if (field === "timestampUser") {
      if (typeof value !== "number" || Number.isNaN(value)) {
        invalidFields.push({
          field,
          expected: "number",
          received: typeof value
        });
      }
      continue;
    }
    if (typeof value !== "string") {
      invalidFields.push({
        field,
        expected: "string",
        received: typeof value
      });
      continue;
    }
    if (value.trim() === "") {
      invalidFields.push({
        field,
        expected: "non-empty string",
        received: "empty string"
      });
    }
  }
  for (const field of META_OPTIONAL_FIELDS) {
    const value = metaObj[field];
    if (value !== void 0 && value !== null && typeof value !== "string") {
      invalidFields.push({
        field,
        expected: "string",
        received: typeof value
      });
    }
  }
  const valid = missingFields.length === 0 && invalidFields.length === 0;
  let error;
  if (!valid) {
    const parts = [];
    if (missingFields.length > 0) {
      parts.push(`Missing required fields: ${missingFields.join(", ")}`);
    }
    if (invalidFields.length > 0) {
      const invalidDescriptions = invalidFields.map((f) => `${f.field} (expected ${f.expected}, got ${f.received})`);
      parts.push(`Invalid field types: ${invalidDescriptions.join(", ")}`);
    }
    error = parts.join(". ");
  }
  return {
    valid,
    missingFields,
    invalidFields,
    error
  };
}
function isCompleteMeta(meta) {
  return validateMeta(meta).valid;
}
function hasCloudFunctionContext(meta) {
  if (!isCompleteMeta(meta)) return false;
  const enriched = meta;
  return typeof enriched.viaCloudFunction === "string" && enriched.viaCloudFunction.length > 0;
}
function isMetaStale(beforeMeta, afterMeta) {
  if (afterMeta == null) {
    return {
      isStale: true,
      reason: "no_after_meta",
      message: "No meta present in after data"
    };
  }
  if (!isCompleteMeta(afterMeta)) {
    return {
      isStale: true,
      reason: "after_meta_incomplete",
      message: "Meta in after data is incomplete"
    };
  }
  if (beforeMeta == null) {
    return {
      isStale: false,
      message: "No before meta to compare - trusting after meta"
    };
  }
  const beforeObj = beforeMeta;
  const afterObj = afterMeta;
  const beforeTimestamp = beforeObj.timestampUser;
  const afterTimestamp = afterObj.timestampUser;
  if (typeof beforeTimestamp !== "number" || Number.isNaN(beforeTimestamp)) {
    return {
      isStale: false,
      message: "Before meta has no numeric timestampUser - trusting after meta"
    };
  }
  if (beforeTimestamp === afterTimestamp) {
    return {
      isStale: true,
      reason: "timestamp_unchanged",
      message: `Meta timestampUser unchanged (${afterTimestamp}) - client did not update meta with this write`
    };
  }
  return {
    isStale: false,
    message: "Meta timestampUser changed - meta is fresh"
  };
}
function isFreshCompleteMeta(beforeMeta, afterMeta) {
  const stalenessResult = isMetaStale(beforeMeta, afterMeta);
  return !stalenessResult.isStale;
}
function enrichMeta(params) {
  const { meta, functionName, functionVersion } = params;
  return {
    ...meta,
    viaCloudFunction: functionName,
    ...functionVersion && { cloudFunctionVersion: functionVersion }
  };
}
function createTrackedWriteMeta(params) {
  const { uid, meta, functionName, functionVersion } = params;
  const candidate = {
    userId: uid,
    deviceIdentifier: meta.deviceIdentifier,
    timestampUser: meta.timestampUser,
    timezone: meta.timezone,
    language: meta.language,
    locale: meta.locale,
    device: meta.device,
    os: meta.os,
    appVersion: meta.appVersion,
    osVersion: meta.osVersion,
    appId: meta.appId,
    customType: meta.customType
  };
  if (!isCompleteMeta(candidate)) {
    return void 0;
  }
  return enrichMeta({
    meta: candidate,
    functionName,
    functionVersion
  });
}
function createSystemMeta(userId, functionName, functionVersion) {
  const now = /* @__PURE__ */ new Date();
  return {
    userId,
    deviceIdentifier: "server",
    timestampUser: now.getTime(),
    timezone: "UTC",
    language: "en",
    locale: "en-US",
    device: "Cloud Functions",
    os: "Node.js",
    appVersion: functionVersion ?? "1.0.0",
    osVersion: process.version,
    appId: "com.paragonix.functions",
    viaCloudFunction: functionName,
    ...functionVersion && { cloudFunctionVersion: functionVersion }
  };
}

// src/models/mutation-history.ts
var MUTATION_HISTORY_DIFF_VERSION = 1;
var MutationHistory = class {
  id;
  sessionId;
  scopeType;
  scopeId;
  sourceDocumentPath;
  operation;
  capturedAt;
  meta;
  beforeData;
  changes;
  changedPaths;
  diffVersion;
  userId;
  auditIgnoredFields;
  customType;
  constructor(params) {
    this.id = params.id;
    this.sessionId = params.sessionId;
    this.scopeType = params.scopeType;
    this.scopeId = params.scopeId;
    this.sourceDocumentPath = params.sourceDocumentPath;
    this.operation = params.operation;
    this.capturedAt = params.capturedAt instanceof Date ? params.capturedAt : params.capturedAt.toDate();
    this.meta = params.meta;
    this.beforeData = params.beforeData;
    this.changes = params.changes;
    this.changedPaths = params.changedPaths;
    this.diffVersion = params.diffVersion;
    this.userId = params.userId;
    this.auditIgnoredFields = params.auditIgnoredFields;
    this.customType = params.customType;
  }
  toJSON() {
    return {
      id: this.id,
      sessionId: this.sessionId,
      scopeType: this.scopeType,
      scopeId: this.scopeId,
      sourceDocumentPath: this.sourceDocumentPath,
      operation: this.operation,
      capturedAt: this.capturedAt,
      meta: this.meta,
      beforeData: this.beforeData,
      changes: this.changes,
      changedPaths: this.changedPaths,
      diffVersion: this.diffVersion,
      userId: this.userId,
      auditIgnoredFields: this.auditIgnoredFields,
      customType: this.customType
    };
  }
};
function isHistoryPatchEmpty(patch) {
  return patch.operations.length === 0;
}
var MutationHistoryConverter = {
  toFirestore(history) {
    return {
      sessionId: history.sessionId,
      scopeType: history.scopeType,
      scopeId: history.scopeId,
      sourceDocumentPath: history.sourceDocumentPath,
      operation: history.operation,
      capturedAt: history.capturedAt,
      meta: history.meta,
      beforeData: history.beforeData,
      changes: history.changes,
      changedPaths: history.changedPaths,
      diffVersion: history.diffVersion,
      userId: history.userId,
      auditIgnoredFields: history.auditIgnoredFields,
      customType: history.customType
    };
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new MutationHistory({
      id: doc.id,
      sessionId: data.sessionId,
      scopeType: data.scopeType ?? "session",
      scopeId: data.scopeId ?? data.sessionId,
      sourceDocumentPath: data.sourceDocumentPath,
      operation: data.operation,
      capturedAt: data.capturedAt?.toDate?.() ?? data.capturedAt,
      meta: data.meta,
      beforeData: data.beforeData ?? null,
      changes: data.changes,
      changedPaths: data.changedPaths,
      diffVersion: data.diffVersion,
      userId: data.userId,
      auditIgnoredFields: data.auditIgnoredFields,
      customType: data.customType
    });
  }
};

// src/models/notes-data.ts
var NotesData = class {
  highlights;
  critical;
  createdAt;
  updatedAt;
  constructor(params) {
    this.highlights = params.highlights;
    this.critical = params.critical;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : void 0;
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : params.updatedAt ? new Date(params.updatedAt) : void 0;
  }
  toJSON() {
    return {
      highlights: this.highlights,
      critical: this.critical,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var NotesDataConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new NotesData({
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/offer-status-type.ts
var OfferStatusType = /* @__PURE__ */ ((OfferStatusType2) => {
  OfferStatusType2["PRIMARY"] = "PRIMARY";
  OfferStatusType2["STRONG_BACKUP"] = "STRONG_BACKUP";
  OfferStatusType2["BACKUP"] = "BACKUP";
  OfferStatusType2["PAUSED"] = "PAUSED";
  OfferStatusType2["ACCEPTED"] = "ACCEPTED";
  OfferStatusType2["DECLINED"] = "DECLINED";
  return OfferStatusType2;
})(OfferStatusType || {});
var OfferStatusTypeUtil = class {
  /**
   * Converts an OfferStatusType to a human-readable UI string.
   *
   * @param type - The offer status type to convert
   * @param capitalize - Whether to capitalize the first letter (default: true)
   * @returns A formatted string representation of the status
   *
   * @example
   * ```typescript
   * OfferStatusTypeUtil.toUIString(OfferStatusType.PRIMARY); // "Primary"
   * OfferStatusTypeUtil.toUIString(OfferStatusType.STRONG_BACKUP); // "Strong Backup"
   * OfferStatusTypeUtil.toUIString(OfferStatusType.BACKUP, false); // "backup"
   * ```
   */
  static toUIString(type, capitalize = true) {
    switch (type) {
      case "PRIMARY" /* PRIMARY */:
        return capitalize ? "Primary" : "primary";
      case "STRONG_BACKUP" /* STRONG_BACKUP */:
        return capitalize ? "Strong Backup" : "strong backup";
      case "BACKUP" /* BACKUP */:
        return capitalize ? "Backup" : "backup";
      case "PAUSED" /* PAUSED */:
        return capitalize ? "Paused" : "paused";
      case "ACCEPTED" /* ACCEPTED */:
        return capitalize ? "Accepted" : "accepted";
      case "DECLINED" /* DECLINED */:
        return capitalize ? "Declined" : "declined";
      default:
        return capitalize ? "Unknown" : "unknown";
    }
  }
};

// src/models/or-schedule.ts
var OrScheduleType = /* @__PURE__ */ ((OrScheduleType2) => {
  OrScheduleType2["SCHEDULED"] = "SCHEDULED";
  OrScheduleType2["TENTATIVE"] = "TENTATIVE";
  return OrScheduleType2;
})(OrScheduleType || {});
var OrScheduleTypeUtil = class {
  /**
   * Converts an OrScheduleType to a human-readable UI string.
   *
   * @param type - The OR schedule type to convert
   * @returns A formatted string representation of the OR schedule type
   *
   * @example
   * ```typescript
   * OrScheduleTypeUtil.toUIString(OrScheduleType.SCHEDULED); // "Scheduled"
   * OrScheduleTypeUtil.toUIString(OrScheduleType.TENTATIVE); // "Tentative"
   * ```
   */
  static toUIString(type) {
    switch (type) {
      case "SCHEDULED" /* SCHEDULED */:
        return "Scheduled";
      case "TENTATIVE" /* TENTATIVE */:
        return "Tentative";
      default:
        return "";
    }
  }
};
function normalizeOrScheduleTime(schedule) {
  if (!schedule.time || typeof schedule.time !== "string") {
    return schedule;
  }
  const d = new Date(schedule.time);
  if (isNaN(d.getTime())) {
    return { ...schedule, time: void 0 };
  }
  return { ...schedule, time: d };
}

// src/models/organization.ts
var Organization = class {
  id;
  name;
  handle;
  adminIds;
  userIds;
  status;
  organTypes;
  types;
  isInternal;
  placeId;
  areaId;
  territoryId;
  address;
  addressComponents;
  location;
  country;
  timezone;
  logoFileName;
  logoUrl;
  domains;
  areDomainsWhitelisted;
  createdAt;
  updatedAt;
  validationStartedAt;
  activatedAt;
  pilot;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.handle = params.handle;
    this.adminIds = params.adminIds ?? [];
    this.userIds = params.userIds ?? [];
    this.status = params.status;
    this.organTypes = params.organTypes ?? [];
    this.types = params.types ?? [];
    this.isInternal = params.isInternal ?? false;
    this.placeId = params.placeId;
    this.areaId = params.areaId;
    this.territoryId = params.territoryId;
    this.address = params.address;
    this.addressComponents = params.addressComponents;
    this.location = params.location;
    this.country = params.country;
    this.timezone = params.timezone;
    this.logoFileName = params.logoFileName;
    this.logoUrl = params.logoUrl;
    this.domains = params.domains ?? [];
    this.areDomainsWhitelisted = params.areDomainsWhitelisted ?? false;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.updatedAt = typeof params.updatedAt === "string" ? new Date(params.updatedAt) : params.updatedAt;
    this.validationStartedAt = typeof params.validationStartedAt === "string" ? new Date(params.validationStartedAt) : params.validationStartedAt;
    this.activatedAt = typeof params.activatedAt === "string" ? new Date(params.activatedAt) : params.activatedAt;
    this.pilot = params.pilot;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      handle: this.handle,
      adminIds: this.adminIds,
      userIds: this.userIds,
      status: this.status,
      organTypes: this.organTypes,
      types: this.types,
      isInternal: this.isInternal,
      placeId: this.placeId,
      areaId: this.areaId,
      territoryId: this.territoryId,
      address: this.address,
      addressComponents: this.addressComponents,
      location: this.location,
      country: this.country,
      timezone: this.timezone,
      logoFileName: this.logoFileName,
      logoUrl: this.logoUrl,
      domains: this.domains,
      areDomainsWhitelisted: this.areDomainsWhitelisted,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      validationStartedAt: this.validationStartedAt,
      activatedAt: this.activatedAt,
      pilot: this.pilot
    };
  }
};
var OrganizationConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Organization({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      validationStartedAt: data.validationStartedAt?.toDate(),
      activatedAt: data.activatedAt?.toDate()
    });
  }
};

// src/models/organization-member-permissions.ts
var OrganizationMemberPermissions = class {
  canViewAllSessions;
  canCreateSessions;
  constructor(params) {
    this.canViewAllSessions = params?.canViewAllSessions ?? true;
    this.canCreateSessions = params?.canCreateSessions ?? true;
  }
  toJSON() {
    return {
      canViewAllSessions: this.canViewAllSessions,
      canCreateSessions: this.canCreateSessions
    };
  }
};

// src/models/session-member-access-level.ts
var SessionMemberAccessLevel = /* @__PURE__ */ ((SessionMemberAccessLevel2) => {
  SessionMemberAccessLevel2["FULL"] = "FULL";
  SessionMemberAccessLevel2["LIMITED"] = "LIMITED";
  return SessionMemberAccessLevel2;
})(SessionMemberAccessLevel || {});
var SessionMemberAccessLevelType = class {
  /**
   * Converts a SessionMemberAccessLevel to a human-readable UI string.
   *
   * @param accessLevel - The access level to convert
   * @returns A formatted string representation of the access level
   *
   * @example
   * ```typescript
   * SessionMemberAccessLevelType.toUIString(SessionMemberAccessLevel.FULL); // "Full Access"
   * SessionMemberAccessLevelType.toUIString(SessionMemberAccessLevel.LIMITED); // "Limited Access"
   * ```
   */
  static toUIString(accessLevel) {
    switch (accessLevel) {
      case "FULL" /* FULL */:
        return "Full Access";
      case "LIMITED" /* LIMITED */:
        return "Limited Access";
      default:
        return "Limited Access";
    }
  }
};

// src/models/organization-member.ts
var OrganizationMember = class {
  id;
  email;
  isEmailWhitelisted;
  firstname;
  lastname;
  status;
  accessRole;
  role;
  title;
  startOfEmployment;
  createdAt;
  updatedAt;
  accessRequestedAt;
  invitedAt;
  invitedBy;
  lastInvitedAt;
  lastInvitedBy;
  activatedAt;
  deactivatedAt;
  deactivatedBy;
  deactivationReason;
  deactivationText;
  permissions;
  /**
   * Resolved default session access level for this organization member.
   * Always populated — falls back to {@link SessionMemberAccessLevel.FULL}
   * when the underlying document does not set a value.
   */
  defaultSessionAccessLevel;
  constructor(params) {
    this.id = params.id;
    this.email = params.email;
    this.isEmailWhitelisted = params.isEmailWhitelisted ?? false;
    this.firstname = params.firstname;
    this.lastname = params.lastname;
    this.status = params.status;
    this.accessRole = params.accessRole;
    this.role = params.role;
    this.title = params.title;
    this.startOfEmployment = typeof params.startOfEmployment === "string" ? new Date(params.startOfEmployment) : params.startOfEmployment;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.updatedAt = typeof params.updatedAt === "string" ? new Date(params.updatedAt) : params.updatedAt;
    this.accessRequestedAt = typeof params.accessRequestedAt === "string" ? new Date(params.accessRequestedAt) : params.accessRequestedAt;
    this.invitedAt = typeof params.invitedAt === "string" ? new Date(params.invitedAt) : params.invitedAt;
    this.invitedBy = params.invitedBy;
    this.lastInvitedAt = typeof params.lastInvitedAt === "string" ? new Date(params.lastInvitedAt) : params.lastInvitedAt;
    this.lastInvitedBy = params.lastInvitedBy;
    this.activatedAt = typeof params.activatedAt === "string" ? new Date(params.activatedAt) : params.activatedAt;
    this.deactivatedAt = typeof params.deactivatedAt === "string" ? new Date(params.deactivatedAt) : params.deactivatedAt;
    this.deactivatedBy = params.deactivatedBy;
    this.deactivationReason = params.deactivationReason;
    this.deactivationText = params.deactivationText;
    this.permissions = new OrganizationMemberPermissions(params.permissions);
    this.defaultSessionAccessLevel = params.defaultSessionAccessLevel ?? "FULL" /* FULL */;
  }
  get fullname() {
    return `${this.firstname} ${this.lastname}`;
  }
  get initials() {
    return `${this.firstname?.charAt(0) ?? ""}${this.lastname?.charAt(0) ?? ""}`;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      id: this.id,
      email: this.email,
      isEmailWhitelisted: this.isEmailWhitelisted,
      firstname: this.firstname,
      lastname: this.lastname,
      status: this.status,
      accessRole: this.accessRole,
      role: this.role,
      title: this.title,
      startOfEmployment: this.startOfEmployment,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      accessRequestedAt: this.accessRequestedAt,
      invitedAt: this.invitedAt,
      invitedBy: this.invitedBy,
      lastInvitedAt: this.lastInvitedAt,
      lastInvitedBy: this.lastInvitedBy,
      activatedAt: this.activatedAt,
      deactivatedAt: this.deactivatedAt,
      deactivatedBy: this.deactivatedBy,
      deactivationReason: this.deactivationReason,
      deactivationText: this.deactivationText,
      permissions: this.permissions.toJSON(),
      defaultSessionAccessLevel: this.defaultSessionAccessLevel
    };
  }
};
var OrganizationMemberConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new OrganizationMember({
      id: doc.id,
      ...data,
      startOfEmployment: data.startOfEmployment?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      accessRequestedAt: data.accessRequestedAt?.toDate(),
      invitedAt: data.invitedAt?.toDate(),
      lastInvitedAt: data.lastInvitedAt?.toDate(),
      activatedAt: data.activatedAt?.toDate(),
      deactivatedAt: data.deactivatedAt?.toDate()
    });
  }
};

// src/models/organization-member-access-role.ts
var OrganizationMemberAccessRole = /* @__PURE__ */ ((OrganizationMemberAccessRole2) => {
  OrganizationMemberAccessRole2["ADMIN"] = "ADMIN";
  OrganizationMemberAccessRole2["VIEWER"] = "VIEWER";
  return OrganizationMemberAccessRole2;
})(OrganizationMemberAccessRole || {});
var OrganizationMemberAccessRoleUtil = class {
  static toUIString(role) {
    switch (role) {
      case "ADMIN" /* ADMIN */:
        return "admin";
      case "VIEWER" /* VIEWER */:
        return "viewer";
      default:
        return "unknown";
    }
  }
};

// src/models/organization-member-deactivation-reason.ts
var OrganizationMemberDeactivationReason = /* @__PURE__ */ ((OrganizationMemberDeactivationReason2) => {
  OrganizationMemberDeactivationReason2["LEFT_ORGANIZATION"] = "LEFT_ORGANIZATION";
  OrganizationMemberDeactivationReason2["DEACTIVATED"] = "DEACTIVATED";
  OrganizationMemberDeactivationReason2["ACCESS_REQUEST_DECLINED"] = "ACCESS_REQUEST_DECLINED";
  OrganizationMemberDeactivationReason2["USER_DOES_NOT_EXIST"] = "USER_DOES_NOT_EXIST";
  return OrganizationMemberDeactivationReason2;
})(OrganizationMemberDeactivationReason || {});
var OrganizationMemberDeactivationReasonUtil = class {
  static toUIString(reason) {
    switch (reason) {
      case "LEFT_ORGANIZATION" /* LEFT_ORGANIZATION */:
        return "left organization";
      case "DEACTIVATED" /* DEACTIVATED */:
        return "deactivated";
      case "ACCESS_REQUEST_DECLINED" /* ACCESS_REQUEST_DECLINED */:
        return "access request declined";
      case "USER_DOES_NOT_EXIST" /* USER_DOES_NOT_EXIST */:
        return "user does not exist";
      default:
        return reason;
    }
  }
};

// src/models/organization-member-role.ts
var OrganizationMemberRole = class {
  id;
  isActive;
  locales;
  constructor(params) {
    this.id = params.id;
    this.isActive = params.isActive;
    this.locales = params.locales;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      id: this.id,
      isActive: this.isActive,
      locales: this.locales
    };
  }
};
var OrganizationMemberRoleConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new OrganizationMemberRole({
      ...data,
      id: doc.id
    });
  }
};

// src/models/organization-member-status.ts
var OrganizationMemberStatus = /* @__PURE__ */ ((OrganizationMemberStatus2) => {
  OrganizationMemberStatus2["PRESELECTED"] = "PRESELECTED";
  OrganizationMemberStatus2["ACCESS_REQUESTED"] = "ACCESS_REQUESTED";
  OrganizationMemberStatus2["CREATED"] = "CREATED";
  OrganizationMemberStatus2["INVITED"] = "INVITED";
  OrganizationMemberStatus2["ACTIVE"] = "ACTIVE";
  OrganizationMemberStatus2["INACTIVE"] = "INACTIVE";
  return OrganizationMemberStatus2;
})(OrganizationMemberStatus || {});
var OrganizationMemberStatusUtil = class {
  static toUIString(status) {
    switch (status) {
      case "PRESELECTED" /* PRESELECTED */:
        return "pre-selected";
      case "ACCESS_REQUESTED" /* ACCESS_REQUESTED */:
        return "access requested";
      case "CREATED" /* CREATED */:
        return "created";
      case "INVITED" /* INVITED */:
        return "invited";
      case "ACTIVE" /* ACTIVE */:
        return "active";
      case "INACTIVE" /* INACTIVE */:
        return "inactive";
      default:
        return "unknown";
    }
  }
};

// src/models/organization-option.ts
var OrganizationOption = class {
  id;
  organizationId;
  handle;
  name;
  organs;
  types;
  constructor(params) {
    this.id = params.id;
    this.organizationId = params.organizationId;
    this.handle = params.handle;
    this.name = params.name;
    this.organs = params.organs ?? [];
    this.types = params.types ?? [];
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      id: this.id,
      organizationId: this.organizationId,
      handle: this.handle,
      name: this.name,
      organs: this.organs,
      types: this.types
    };
  }
};
var OrganizationOptionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new OrganizationOption({ ...data, id: doc.id });
  }
};

// src/models/organization-preselected-member.ts
var OrganizationPreselectedMember = class extends OrganizationMember {
  isConfirmed;
  confirmedAt;
  constructor(params) {
    super(params);
    this.isConfirmed = params.isConfirmed;
    this.confirmedAt = typeof params.confirmedAt === "string" ? new Date(params.confirmedAt) : params.confirmedAt;
  }
  toJSON() {
    return {
      ...super.toJSON(),
      isConfirmed: this.isConfirmed,
      confirmedAt: this.confirmedAt
    };
  }
  serialize() {
    return {
      ...super.serialize(),
      ...JSON.parse(JSON.stringify(this))
    };
  }
};
var OrganizationPreselectedMemberConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new OrganizationPreselectedMember({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      confirmedAt: data.confirmedAt?.toDate()
    });
  }
};

// src/models/organization-status.ts
var OrganizationStatus = /* @__PURE__ */ ((OrganizationStatus2) => {
  OrganizationStatus2["CREATE_REQUESTED"] = "CREATE_REQUESTED";
  OrganizationStatus2["CREATED"] = "CREATED";
  OrganizationStatus2["IN_VALIDATION"] = "IN_VALIDATION";
  OrganizationStatus2["ACTIVE"] = "ACTIVE";
  OrganizationStatus2["INACTIVE"] = "INACTIVE";
  return OrganizationStatus2;
})(OrganizationStatus || {});
var OrganizationStatusUtil = class {
  static toUIString(status) {
    switch (status) {
      case "CREATE_REQUESTED" /* CREATE_REQUESTED */:
        return "create requested";
      case "CREATED" /* CREATED */:
        return "created";
      case "IN_VALIDATION" /* IN_VALIDATION */:
        return "in validation";
      case "ACTIVE" /* ACTIVE */:
        return "active";
      case "INACTIVE" /* INACTIVE */:
        return "inactive";
      default:
        return "unknown";
    }
  }
};

// src/models/organization-type.ts
var OrganizationType = class {
  id;
  isActive;
  locales;
  sort;
  constructor(params) {
    this.id = params.id;
    this.isActive = params.isActive;
    this.locales = params.locales;
    this.sort = params.sort;
  }
  toJSON() {
    return {
      id: this.id,
      isActive: this.isActive,
      locales: this.locales,
      sort: this.sort
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var OrganizationTypeConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new OrganizationType({
      ...data,
      id: doc.id
    });
  }
};

// src/models/organization-unregistered-invite.ts
var OrganizationUnregisteredInvite = class {
  id;
  email;
  organizationId;
  invitedBy;
  createdAt;
  updatedAt;
  constructor(params) {
    this.id = params.id;
    this.email = params.email;
    this.organizationId = params.organizationId;
    this.invitedBy = params.invitedBy;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.updatedAt = typeof params.updatedAt === "string" ? new Date(params.updatedAt) : params.updatedAt;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var OrganizationUnregisteredInviteConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new OrganizationUnregisteredInvite({
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/pdf-type.ts
var PdfType = /* @__PURE__ */ ((PdfType2) => {
  PdfType2["DONORNET_DONOR_SUMMARY"] = "DONORNET_DONOR_SUMMARY";
  PdfType2["UNKNOWN"] = "UNKNOWN";
  return PdfType2;
})(PdfType || {});
var PdfTypeUtil = class {
  /**
   * Converts a PdfType to a human-readable UI string.
   *
   * @param type - The PDF type to convert
   * @returns A formatted string representation of the PDF type
   *
   * @example
   * ```typescript
   * PdfTypeUtil.toUIString(PdfType.DONORNET_DONOR_SUMMARY); // "DonorNet Donor Summary"
   * PdfTypeUtil.toUIString(PdfType.UNKNOWN); // "Unknown"
   * ```
   */
  static toUIString(type) {
    switch (type) {
      case "DONORNET_DONOR_SUMMARY" /* DONORNET_DONOR_SUMMARY */:
        return "DonorNet Donor Summary";
      case "UNKNOWN" /* UNKNOWN */:
        return "Unknown";
      default:
        return "Unknown";
    }
  }
};

// src/models/potential-session-status.ts
var PotentialSessionStatus = /* @__PURE__ */ ((PotentialSessionStatus2) => {
  PotentialSessionStatus2["IDENTIFIED"] = "identified";
  PotentialSessionStatus2["PENDING_REVIEW"] = "pending_review";
  PotentialSessionStatus2["APPROVED"] = "approved";
  PotentialSessionStatus2["REJECTED"] = "rejected";
  return PotentialSessionStatus2;
})(PotentialSessionStatus || {});
var PotentialSessionStatusUtils = class {
  static toUIString(status) {
    switch (status) {
      case "identified" /* IDENTIFIED */:
        return "Identified";
      case "pending_review" /* PENDING_REVIEW */:
        return "Pending Review";
      case "approved" /* APPROVED */:
        return "Approved";
      case "rejected" /* REJECTED */:
        return "Rejected";
      default:
        return "Unknown";
    }
  }
  static getColor(status) {
    switch (status) {
      case "identified" /* IDENTIFIED */:
        return "gray";
      case "pending_review" /* PENDING_REVIEW */:
        return "yellow";
      case "approved" /* APPROVED */:
        return "green";
      case "rejected" /* REJECTED */:
        return "red";
      default:
        return "gray";
    }
  }
  static isReviewable(status) {
    return status === "identified" /* IDENTIFIED */ || status === "pending_review" /* PENDING_REVIEW */;
  }
  static isFinalized(status) {
    return status === "approved" /* APPROVED */ || status === "rejected" /* REJECTED */;
  }
};

// src/models/potential-session.ts
var PotentialSession = class {
  // ─────────────────────────────────────────────────────────────────────────────
  // Identifiers
  // ─────────────────────────────────────────────────────────────────────────────
  /** Unique identifier (same as the referenced session ID) */
  id;
  /** Reference to the actual session document */
  sessionId;
  // ─────────────────────────────────────────────────────────────────────────────
  // Denormalized Session Data
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Session access key (always string).
   * Converted from number if stored as legacy joinKey in database.
   */
  key;
  /** Date when the session was created */
  sessionDate;
  /** Type of organ being transported */
  organ;
  /** Paragonix product type used */
  product;
  /** Recipient center destination handle */
  destination;
  /** UNOS donor identifier */
  unosId;
  // ─────────────────────────────────────────────────────────────────────────────
  // Review Status
  // ─────────────────────────────────────────────────────────────────────────────
  /** Current status in the review workflow */
  status;
  // ─────────────────────────────────────────────────────────────────────────────
  // Identification
  // ─────────────────────────────────────────────────────────────────────────────
  /** How the session was identified as potentially belonging to the organization */
  reason;
  /** Additional details about the identification reason */
  reasonDetails;
  /** Timestamp when the session was identified */
  identifiedAt;
  /** User ID who identified the session */
  identifiedBy;
  // ─────────────────────────────────────────────────────────────────────────────
  // Review Tracking
  // ─────────────────────────────────────────────────────────────────────────────
  /** Timestamp when review was requested */
  reviewRequestedAt;
  /** User ID who requested the review */
  reviewRequestedBy;
  /** Timestamp when the review was completed */
  reviewedAt;
  /** User ID who completed the review */
  reviewedBy;
  /** Notes from the reviewer */
  reviewNotes;
  /** Reason for rejection (if rejected) */
  rejectionReason;
  // ─────────────────────────────────────────────────────────────────────────────
  // Metadata
  // ─────────────────────────────────────────────────────────────────────────────
  /** Timestamp when the document was created */
  createdAt;
  /** Timestamp when the document was last updated */
  updatedAt;
  // ─────────────────────────────────────────────────────────────────────────────
  // Constructor
  // ─────────────────────────────────────────────────────────────────────────────
  constructor(params) {
    this.id = params.id;
    this.sessionId = params.sessionId;
    this.key = typeof params.key === "number" ? String(params.key) : params.key;
    this.sessionDate = typeof params.sessionDate === "string" ? new Date(params.sessionDate) : params.sessionDate;
    this.organ = params.organ;
    this.product = params.product;
    this.destination = params.destination;
    this.unosId = params.unosId;
    this.status = params.status;
    this.reason = params.reason;
    this.reasonDetails = {
      ...params.reasonDetails,
      employmentDates: params.reasonDetails.employmentDates?.map((date) => typeof date === "string" ? new Date(date) : date)
    };
    this.identifiedAt = typeof params.identifiedAt === "string" ? new Date(params.identifiedAt) : params.identifiedAt;
    this.identifiedBy = params.identifiedBy;
    this.reviewRequestedAt = params.reviewRequestedAt ? typeof params.reviewRequestedAt === "string" ? new Date(params.reviewRequestedAt) : params.reviewRequestedAt : void 0;
    this.reviewRequestedBy = params.reviewRequestedBy;
    this.reviewedAt = params.reviewedAt ? typeof params.reviewedAt === "string" ? new Date(params.reviewedAt) : params.reviewedAt : void 0;
    this.reviewedBy = params.reviewedBy;
    this.reviewNotes = params.reviewNotes;
    this.rejectionReason = params.rejectionReason;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.updatedAt = typeof params.updatedAt === "string" ? new Date(params.updatedAt) : params.updatedAt;
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Computed Properties
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Whether this potential session can be reviewed (approved/rejected).
   */
  get isReviewable() {
    return this.status === "identified" /* IDENTIFIED */ || this.status === "pending_review" /* PENDING_REVIEW */;
  }
  /**
   * Whether this potential session has been finalized (approved or rejected).
   */
  get isFinalized() {
    return this.status === "approved" /* APPROVED */ || this.status === "rejected" /* REJECTED */;
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Methods
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Gets a human-readable description of why this session was identified.
   *
   * @returns A formatted description string
   */
  getReasonDescription() {
    switch (this.reason) {
      case "member_employment_date":
        const members = this.reasonDetails.memberNames?.join(", ") || "Unknown members";
        return `Identified via ${members}`;
      case "manual_addition":
        return `Manually added by ${this.reasonDetails.addedByAdminName || "admin"}`;
      case "destination_match":
        return "Destination organization match";
      default:
        return "Unknown reason";
    }
  }
  toJSON() {
    return {
      id: this.id,
      sessionId: this.sessionId,
      key: this.key,
      sessionDate: this.sessionDate,
      organ: this.organ,
      product: this.product,
      destination: this.destination,
      unosId: this.unosId,
      status: this.status,
      reason: this.reason,
      reasonDetails: this.reasonDetails,
      identifiedAt: this.identifiedAt,
      identifiedBy: this.identifiedBy,
      reviewRequestedAt: this.reviewRequestedAt,
      reviewRequestedBy: this.reviewRequestedBy,
      reviewedAt: this.reviewedAt,
      reviewedBy: this.reviewedBy,
      reviewNotes: this.reviewNotes,
      rejectionReason: this.rejectionReason,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var PotentialSessionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new PotentialSession({
      ...data,
      id: doc.id,
      sessionDate: data.sessionDate?.toDate(),
      reasonDetails: {
        ...data.reasonDetails,
        employmentDates: data.reasonDetails?.employmentDates?.map((date) => date?.toDate())
      },
      identifiedAt: data.identifiedAt?.toDate(),
      reviewRequestedAt: data.reviewRequestedAt?.toDate(),
      reviewedAt: data.reviewedAt?.toDate(),
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/preservation-method.ts
var PreservationMethodType = /* @__PURE__ */ ((PreservationMethodType2) => {
  PreservationMethodType2["ICE"] = "ICE";
  PreservationMethodType2["NMP"] = "NMP";
  PreservationMethodType2["HOPE"] = "HOPE";
  PreservationMethodType2["HMP"] = "HMP";
  PreservationMethodType2["OTHER"] = "OTHER";
  return PreservationMethodType2;
})(PreservationMethodType || {});
var ORGAN_PRODUCTS = {
  ["heart" /* HEART */]: ["sherpapak-cts" /* SHERPAPAK */],
  ["lung" /* LUNG */]: ["lungguard" /* LUNGGUARD */, "baroguard" /* BAROGUARD */],
  ["liver" /* LIVER */]: ["liverguard" /* LIVERGUARD */],
  ["kidney" /* KIDNEY */]: ["kidneyvault" /* KIDNEYVAULT */],
  ["pancreas" /* PANCREAS */]: ["pancreaspak" /* PANCREASPAK */]
};
var ORGAN_MISC_METHODS = {
  ["heart" /* HEART */]: ["ICE" /* ICE */, "NMP" /* NMP */],
  ["lung" /* LUNG */]: ["ICE" /* ICE */, "NMP" /* NMP */],
  ["liver" /* LIVER */]: ["ICE" /* ICE */, "NMP" /* NMP */, "HOPE" /* HOPE */],
  ["kidney" /* KIDNEY */]: ["ICE" /* ICE */, "HMP" /* HMP */],
  ["pancreas" /* PANCREAS */]: ["ICE" /* ICE */]
};
var PreservationMethodUtil = class _PreservationMethodUtil {
  static toUIString(type) {
    switch (type) {
      case "ICE" /* ICE */:
        return "Ice";
      case "NMP" /* NMP */:
        return "NMP";
      case "HOPE" /* HOPE */:
        return "HOPE";
      case "HMP" /* HMP */:
        return "HMP";
      case "OTHER" /* OTHER */:
        return "Other";
      default:
        return "Unknown";
    }
  }
  static toLabel(method) {
    if (method.type === "OTHER" /* OTHER */) {
      return method.name;
    }
    return _PreservationMethodUtil.toUIString(method.type);
  }
  static getProductsForOrgan(organ) {
    return ORGAN_PRODUCTS[organ] ?? [];
  }
  static getMiscMethodsForOrgan(organ) {
    return ORGAN_MISC_METHODS[organ] ?? [];
  }
};

// src/models/recipient-data.ts
var RecipientData = class {
  height;
  weight;
  age;
  bmi;
  sex;
  bloodType;
  additionalDetails;
  createdAt;
  updatedAt;
  constructor(params) {
    this.height = params.height;
    this.weight = params.weight;
    this.age = params.age;
    this.bmi = params.bmi;
    this.sex = params.sex;
    this.bloodType = params.bloodType;
    this.additionalDetails = params.additionalDetails;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : void 0;
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : params.updatedAt ? new Date(params.updatedAt) : void 0;
  }
  toJSON() {
    return {
      height: this.height,
      weight: this.weight,
      age: this.age,
      bmi: this.bmi,
      sex: this.sex,
      bloodType: this.bloodType,
      additionalDetails: this.additionalDetails,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var RecipientDataConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new RecipientData({
      ...data,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/demo-state.ts
var DemoState = class {
  sessionTemplateId;
  originalStartTime;
  snapshotId;
  autoPlaySchedulerId;
  constructor(params) {
    this.sessionTemplateId = params.sessionTemplateId;
    this.originalStartTime = params.originalStartTime instanceof Date ? params.originalStartTime : params.originalStartTime ? new Date(params.originalStartTime) : void 0;
    this.snapshotId = params.snapshotId;
    this.autoPlaySchedulerId = params.autoPlaySchedulerId;
  }
  toJSON() {
    return {
      sessionTemplateId: this.sessionTemplateId,
      originalStartTime: this.originalStartTime,
      snapshotId: this.snapshotId,
      autoPlaySchedulerId: this.autoPlaySchedulerId
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/session-ischemic-time-type.ts
var SessionIschemicTimeType = /* @__PURE__ */ ((SessionIschemicTimeType2) => {
  SessionIschemicTimeType2["ISCHEMIC_TIME"] = "ISCHEMIC_TIME";
  return SessionIschemicTimeType2;
})(SessionIschemicTimeType || {});

// src/models/session-ischemic-time.ts
var SessionIschemicTime = class {
  id;
  type;
  organPosition;
  startType;
  startTimestamp;
  endType;
  endTimestamp;
  constructor(params) {
    this.id = params.id;
    this.type = params.type ?? "ISCHEMIC_TIME" /* ISCHEMIC_TIME */;
    this.organPosition = params.organPosition;
    this.startType = params.startType;
    this.endType = params.endType;
    this.startTimestamp = params.startTimestamp instanceof Date ? params.startTimestamp : new Date(params.startTimestamp);
    this.endTimestamp = params.endTimestamp instanceof Date ? params.endTimestamp : params.endTimestamp ? new Date(params.endTimestamp) : void 0;
  }
  get durationFormatted() {
    if (this.startTimestamp == null || this.endTimestamp == null) {
      return "0";
    }
    const milliseconds = this.endTimestamp.getTime() - this.startTimestamp.getTime();
    return formatDuration(milliseconds);
  }
  toJSON() {
    return {
      id: this.id,
      type: this.type,
      organPosition: this.organPosition,
      startType: this.startType,
      startTimestamp: this.startTimestamp,
      endType: this.endType,
      endTimestamp: this.endTimestamp
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var SessionIschemicTimeConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new SessionIschemicTime({
      ...data,
      id: doc.id,
      startTimestamp: data.startTimestamp?.toDate(),
      endTimestamp: data.endTimestamp?.toDate()
    });
  }
};

// src/models/session-metadata.ts
var SessionMetadata = class {
  forceStopAfter48Hours;
  reportSentTo;
  sessionCreatedInfoMailSent;
  sessionStoppedInfoMailSent;
  stopReminderSent;
  constructor(params) {
    this.forceStopAfter48Hours = params?.forceStopAfter48Hours ?? false;
    this.reportSentTo = params?.reportSentTo ?? [];
    this.sessionCreatedInfoMailSent = params?.sessionCreatedInfoMailSent ?? false;
    this.sessionStoppedInfoMailSent = params?.sessionStoppedInfoMailSent ?? false;
    this.stopReminderSent = params?.stopReminderSent ?? false;
  }
  toJSON() {
    return {
      forceStopAfter48Hours: this.forceStopAfter48Hours,
      reportSentTo: this.reportSentTo,
      sessionCreatedInfoMailSent: this.sessionCreatedInfoMailSent,
      sessionStoppedInfoMailSent: this.sessionStoppedInfoMailSent,
      stopReminderSent: this.stopReminderSent
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/session-metrics.ts
var SessionMetrics = class {
  memberCount;
  timelineCount;
  constructor(params) {
    this.memberCount = params.memberCount;
    this.timelineCount = params.timelineCount;
  }
  toJSON() {
    return {
      memberCount: this.memberCount,
      timelineCount: this.timelineCount
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/models/session-state-type.ts
var SessionStateType = /* @__PURE__ */ ((SessionStateType2) => {
  SessionStateType2["CREATED"] = "CREATED";
  SessionStateType2["LOGGER_STARTED"] = "LOGGER_STARTED";
  SessionStateType2["LOGGER_STOPPED"] = "LOGGER_STOPPED";
  SessionStateType2["FINISHED"] = "FINISHED";
  SessionStateType2["UNKNOWN"] = "UNKNOWN";
  return SessionStateType2;
})(SessionStateType || {});
var SessionStateTypeUtil = class {
  static toUIString(state, capitalize = true) {
    switch (state) {
      case "CREATED" /* CREATED */:
        return capitalize ? "Created" : "created";
      case "LOGGER_STARTED" /* LOGGER_STARTED */:
        return capitalize ? "Logger Started" : "logger started";
      case "LOGGER_STOPPED" /* LOGGER_STOPPED */:
        return capitalize ? "Logger Stopped" : "logger stopped";
      case "FINISHED" /* FINISHED */:
        return capitalize ? "Finished" : "finished";
      default:
        return capitalize ? "Unknown" : "unknown";
    }
  }
};

// src/models/session.ts
var Session = class {
  // ─────────────────────────────────────────────────────────────────────────────
  // Identifiers
  // ─────────────────────────────────────────────────────────────────────────────
  /** Unique session identifier (Firestore document ID) */
  id;
  /** UNOS donor identifier */
  unosId;
  /** UNOS match run identifier */
  matchRunId;
  /**
   * Session access key.
   * For v7+ sessions: Letter + 5 digits (e.g., "A12345")
   * For legacy sessions: Derived from joinKey as string (e.g., "123456")
   */
  key;
  /**
   * Numeric join key for session access.
   * @deprecated Use `key` instead. Only present in legacy sessions (schemaVersion < 7).
   */
  joinKey;
  // ─────────────────────────────────────────────────────────────────────────────
  // Session Hierarchy
  // ─────────────────────────────────────────────────────────────────────────────
  /** Parent session ID for breakout sessions */
  parentSessionId;
  /** Position of this organ in a multi-organ session. */
  organPosition;
  /** Map of organ positions to child session IDs */
  childSessionIds;
  /** IDs of cancelled child sessions */
  cancelledChildSessionIds;
  // ─────────────────────────────────────────────────────────────────────────────
  // Sharing
  // ─────────────────────────────────────────────────────────────────────────────
  /** Unique key for sharing session access */
  shareKey;
  /** Whether session has been shared externally */
  shared;
  /** Timestamp when session was shared */
  sharedAt;
  /** User ID who shared the session */
  sharedBy;
  // ─────────────────────────────────────────────────────────────────────────────
  // Session Configuration
  // ─────────────────────────────────────────────────────────────────────────────
  /** Type of organ being transported */
  organ;
  /** Paragonix product type used */
  product;
  /** Session type (clinical, demo, etc.) */
  sessionType;
  /** Donation type (DCD, DBD, etc.) */
  donationType;
  /** Preservation methods used during transport */
  preservationMethods;
  /** Data schema version for migrations */
  schemaVersion;
  /**
   * Offer schema version for offer-management pilot migrations.
   * @see SessionParams.offerSchemaVersion
   */
  offerSchemaVersion;
  // ─────────────────────────────────────────────────────────────────────────────
  // Session State
  // ─────────────────────────────────────────────────────────────────────────────
  /** Whether session has been completed */
  finished;
  /** Current review state for completed sessions */
  reviewState;
  /** Whether session has been cancelled */
  cancelled;
  /** User ID who cancelled the session */
  cancelledBy;
  /** Timestamp when session was cancelled */
  cancelledAt;
  /** Reason for breakout session cancellation */
  cancellationReason;
  // ─────────────────────────────────────────────────────────────────────────────
  // Users & Access
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * User ID of session administrator.
   * @deprecated Not set for schemaVersion >= 7. Use `createdBy` instead.
   */
  admin;
  /** Array of user IDs with access to this session */
  userIds;
  /** User IDs retained after organization removal */
  retainedUserIds;
  /** Organization IDs with access to this session */
  organizationIds;
  /** Area IDs for geographic filtering */
  areaIds;
  /** Territory IDs for geographic filtering */
  territoryIds;
  // ─────────────────────────────────────────────────────────────────────────────
  // Device Information
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Device model that created the session.
   * @deprecated Not set for schemaVersion >= 7.
   */
  device;
  /**
   * App version that created the session.
   * @deprecated Not set for schemaVersion >= 7.
   */
  appVersion;
  /**
   * Operating system name.
   * @deprecated Not set for schemaVersion >= 7.
   */
  os;
  /**
   * Operating system version.
   * @deprecated Not set for schemaVersion >= 7.
   */
  osVersion;
  /**
   * Identifier of paired Bluetooth device.
   * @deprecated Not set for schemaVersion >= 7.
   */
  pairedDeviceIdentifier;
  // ─────────────────────────────────────────────────────────────────────────────
  // Location & Routing
  // ─────────────────────────────────────────────────────────────────────────────
  /** Donor hospital/center information */
  donorCenter;
  /** Recipient transplant center information */
  recipientCenter;
  /** Recipient center handle (destination identifier) */
  destination;
  /** OPO handle (OPO identifier) */
  opoHandle;
  /** OPO (Organ Procurement Organization) information */
  opo;
  /** Calculated distance between centers */
  distance;
  /** Estimated time of arrival */
  eta;
  /** User ID who set the ETA */
  etaSetBy;
  // ─────────────────────────────────────────────────────────────────────────────
  // Advanced Tracking & Flights
  // ─────────────────────────────────────────────────────────────────────────────
  /** Whether advanced tracking is enabled */
  hasAdvancedTracking;
  /**
   * Advanced tracker device identifier.
   * @deprecated Not set for schemaVersion >= 7.
   */
  advancedTrackerId;
  /**
   * Advanced tracking data.
   * @deprecated Not set for schemaVersion >= 7.
   */
  advancedTracking;
  /** Whether flight tracking is enabled */
  hasFlights;
  /**
   * Array of flight information.
   * @deprecated Not set for schemaVersion >= 7.
   */
  flights;
  /**
   * Current flight position data.
   * @deprecated Not set for schemaVersion >= 7.
   */
  flightPosition;
  // ─────────────────────────────────────────────────────────────────────────────
  // Logger & Sensor Data
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Logger device passkey.
   * @deprecated Not set for schemaVersion >= 7. Passkey is stored on the Logger document.
   */
  loggerPasskey;
  /**
   * Primary logger serial number.
   * @deprecated Not set for schemaVersion >= 7. Use `loggers` map and Logger documents.
   */
  loggerSerialNo;
  /**
   * All logger serial numbers ever associated with this session. Append-only
   * audit trail — entries are never removed, even when the logger is unlinked.
   * Use `currentLoggerSerialNumbers` for the live set.
   *
   * Synced by:
   * - v7+: `syncLoggersToArrays` in the on-session-updated trigger
   * - v6: `syncLoggerSerialNumbersToSessionRoot` in the on-organ-updated trigger
   * - v5 and earlier: not synced by any trigger
   */
  loggerSerialNumbers;
  /**
   * Currently linked logger serial numbers. Reflects live state — entries are
   * removed when the logger is unlinked, unlike the append-only
   * `loggerSerialNumbers`.
   *
   * Synced alongside `loggerSerialNumbers`:
   * - v7+: by `syncLoggersToArrays` in the on-session-updated trigger,
   *   computed from `session.loggers[*].serialNo`.
   * - v6: by `syncLoggerSerialNumbersToSessionRoot` in the on-organ-updated
   *   trigger, recomputed by reading all organs and collecting their
   *   `logger.serialNo` values.
   * - v5 and earlier: not synced by any trigger; populated by migration only.
   */
  currentLoggerSerialNumbers;
  /**
   * Whether the session currently has any logger linked. Mirrors
   * `currentLoggerSerialNumbers.length > 0`; denormalized so Algolia can filter
   * on it directly. Synced together with `currentLoggerSerialNumbers` in the
   * same triggers (v7+ and v6 only).
   */
  loggerConnected;
  /**
   * All advanced tracker IDs ever used with this session (v7+ sessions).
   * Synced from session.loggers via trigger. Append-only for audit trail.
   */
  advancedTrackerIds;
  /**
   * All product types ever used with this session (v7+ sessions).
   * Synced from session.loggers via trigger. Append-only for audit trail.
   */
  products;
  /**
   * Current logger state (STARTED, STOPPED, etc.).
   * @deprecated Not set for schemaVersion >= 7. Logger state is on the Logger document.
   */
  loggerState;
  /**
   * User ID who connected the logger.
   * @deprecated Not set for schemaVersion >= 7. Connection info is on the Logger document.
   */
  loggerConnectedBy;
  /**
   * Latest logger sensor data.
   * @deprecated Not set for schemaVersion >= 7. Live data is on the Logger document.
   */
  loggerData;
  /**
   * Latest GPS location from logger.
   * @deprecated Not set for schemaVersion >= 7. Locations are in `/loggers/{loggerId}/appLocations`.
   */
  location;
  /** Logger IDs by position (v7+ sessions only) */
  loggers;
  /**
   * Ischemic time tracking data.
   * @deprecated Not set for schemaVersion >= 7.
   */
  ischemicTime;
  // ─────────────────────────────────────────────────────────────────────────────
  // Reports & Metrics
  // ─────────────────────────────────────────────────────────────────────────────
  /** Session metadata (operational flags, report settings, etc.) */
  metadata;
  /** Report data for session summary */
  reportData;
  /** Aggregated session metrics */
  metrics;
  /** PDF summary availability per organ position */
  pdfSummaryAvailable;
  /** Most recent timeline event type */
  lastTimelineEvent;
  // ─────────────────────────────────────────────────────────────────────────────
  // OR Schedule
  // ─────────────────────────────────────────────────────────────────────────────
  /** Donor OR schedule (type, time, surgeon) */
  donorOr;
  /** Recipient OR schedule (type, time, surgeon) */
  recipientOr;
  // ─────────────────────────────────────────────────────────────────────────────
  // Offer Fields
  // ─────────────────────────────────────────────────────────────────────────────
  /** Current phase in the offer workflow */
  phase;
  /** Offer allocation status */
  offerStatus;
  /** Position/rank in the match run */
  rank;
  /** Sequence identifier (A, B, C, etc.) */
  sequence;
  /** Offer attachments (donor summaries, lab results, etc.) */
  attachments;
  // ─────────────────────────────────────────────────────────────────────────────
  // Timestamps & Creation
  // ─────────────────────────────────────────────────────────────────────────────
  /** Timestamp when session was created */
  createdAt;
  /** User ID who created the session */
  createdBy;
  /** Organization ID that created the session */
  createdByOrganization;
  /** How the session was created (app, web, offer, etc.) */
  createdFrom;
  /** Timestamp when session ended */
  endedAt;
  // ─────────────────────────────────────────────────────────────────────────────
  // Demo Mode
  // ─────────────────────────────────────────────────────────────────────────────
  /** Whether this is a demo session */
  demo;
  /** Parent session ID for demo clones */
  demoParentId;
  /** Demo playback state */
  demoState;
  // ─────────────────────────────────────────────────────────────────────────────
  // Constructor
  // ─────────────────────────────────────────────────────────────────────────────
  constructor(params) {
    this.id = params.id;
    this.unosId = params.unosId;
    this.matchRunId = params.matchRunId;
    const key = params.key ?? (params.joinKey != null ? String(params.joinKey) : null);
    if (!key) {
      throw new Error(`Session ${params.id} has no key or joinKey`);
    }
    this.key = key;
    this.joinKey = params.joinKey;
    this.parentSessionId = params.parentSessionId;
    this.organPosition = params.organPosition;
    this.childSessionIds = params.childSessionIds;
    this.cancelledChildSessionIds = params.cancelledChildSessionIds;
    this.shareKey = params.shareKey;
    this.shared = params.shared ?? false;
    this.sharedBy = params.sharedBy;
    if (!params.product && !params.organ) {
      this.product = "sherpapak-cts" /* SHERPAPAK */;
      this.organ = "heart" /* HEART */;
    } else if (params.product && !params.organ) {
      this.product = params.product;
      this.organ = OrganTypeUtil.fromProductType(params.product) ?? "heart" /* HEART */;
    } else {
      this.product = params.product ?? Object.values(params.loggers ?? {}).find((l) => l?.product)?.product;
      this.organ = params.organ;
    }
    this.sessionType = params.sessionType;
    this.donationType = params.donationType;
    this.preservationMethods = params.preservationMethods;
    this.schemaVersion = params.schemaVersion;
    this.offerSchemaVersion = params.offerSchemaVersion;
    this.finished = params.finished ?? false;
    this.reviewState = params.reviewState;
    this.cancelled = params.cancelled;
    this.cancelledBy = params.cancelledBy;
    this.cancellationReason = params.cancellationReason;
    this.admin = params.admin;
    this.userIds = params.userIds ?? [];
    this.retainedUserIds = params.retainedUserIds;
    this.organizationIds = params.organizationIds ?? [];
    this.areaIds = params.areaIds ?? [];
    this.territoryIds = params.territoryIds ?? [];
    this.device = params.device;
    this.appVersion = params.appVersion;
    this.os = params.os;
    this.osVersion = params.osVersion;
    this.pairedDeviceIdentifier = params.pairedDeviceIdentifier;
    this.donorCenter = params.donorCenter;
    this.recipientCenter = params.recipientCenter;
    this.destination = params.destination;
    this.opoHandle = params.opoHandle;
    this.opo = params.opo;
    this.distance = params.distance;
    this.etaSetBy = params.etaSetBy;
    this.hasAdvancedTracking = params.hasAdvancedTracking ?? false;
    this.advancedTrackerId = params.advancedTrackerId;
    this.advancedTracking = params.advancedTracking ? new AdvancedTracking(params.advancedTracking) : void 0;
    this.hasFlights = params.hasFlights ?? false;
    this.flights = params.flights?.map((flight) => new Flight(flight)) ?? [];
    this.flightPosition = params.flightPosition ? new FlightPosition(params.flightPosition) : void 0;
    this.loggerPasskey = params.loggerPasskey;
    this.loggerSerialNo = params.loggerSerialNo;
    this.loggerSerialNumbers = params.loggerSerialNumbers;
    this.currentLoggerSerialNumbers = params.currentLoggerSerialNumbers;
    this.loggerConnected = params.loggerConnected;
    this.advancedTrackerIds = params.advancedTrackerIds;
    this.products = params.products;
    this.loggerState = params.loggerState;
    this.loggerConnectedBy = params.loggerConnectedBy;
    this.loggerData = params.loggerData ? new LoggerData(params.loggerData) : void 0;
    this.location = params.location ? new LoggerLocation(params.location) : void 0;
    this.loggers = params.loggers;
    this.ischemicTime = params.ischemicTime ? new SessionIschemicTime(params.ischemicTime) : void 0;
    this.metadata = params.metadata ? new SessionMetadata(params.metadata) : void 0;
    this.reportData = params.reportData ? new SessionReportData(params.reportData) : void 0;
    this.metrics = params.metrics ? new SessionMetrics(params.metrics) : void 0;
    this.pdfSummaryAvailable = params.pdfSummaryAvailable;
    this.lastTimelineEvent = params.lastTimelineEvent;
    this.donorOr = params.donorOr;
    this.recipientOr = params.recipientOr;
    this.phase = params.phase;
    this.offerStatus = params.offerStatus;
    this.rank = params.rank;
    this.sequence = params.sequence;
    this.attachments = params.attachments?.filter((att) => att != null).map((att) => new AttachmentRef(att)) ?? [];
    this.createdBy = params.createdBy;
    this.createdByOrganization = params.createdByOrganization;
    this.createdFrom = params.createdFrom;
    this.demo = params.demo ?? false;
    this.demoParentId = params.demoParentId;
    this.demoState = params.demoState ? new DemoState(params.demoState) : void 0;
    this.sharedAt = params.sharedAt instanceof Date ? params.sharedAt : params.sharedAt ? new Date(params.sharedAt) : void 0;
    this.cancelledAt = params.cancelledAt instanceof Date ? params.cancelledAt : params.cancelledAt ? new Date(params.cancelledAt) : void 0;
    this.eta = params.eta instanceof Date ? params.eta : params.eta ? new Date(params.eta) : void 0;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : params.createdAt ? new Date(params.createdAt) : /* @__PURE__ */ new Date();
    this.endedAt = params.endedAt instanceof Date ? params.endedAt : params.endedAt ? new Date(params.endedAt) : void 0;
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Methods
  // ─────────────────────────────────────────────────────────────────────────────
  /**
   * Converts session to an Organ instance for organ-specific operations.
   * Only available for v1-v6 sessions (v7+ sessions don't use organs subcollection).
   * @returns An Organ instance with session data
   * @throws Error if called on a v7+ session
   * @deprecated This method is for v6 and earlier sessions only. v7+ sessions use
   * centralized logger collections at `/loggers/{loggerId}` instead of the organs subcollection.
   */
  toOrgan() {
    if (this.schemaVersion >= 7) {
      throw new Error("toOrgan() is not available for v7+ sessions. The organs subcollection does not exist for v7 sessions.");
    }
    return new Organ({
      id: this.id,
      sessionId: this.id,
      product: this.product ?? "sherpapak-cts" /* SHERPAPAK */,
      organ: this.organ,
      advancedTrackerId: this.advancedTrackerId,
      reportData: this.reportData,
      logger: this.loggerSerialNo != null ? {
        serialNo: this.loggerSerialNo,
        passkey: this.loggerPasskey,
        state: this.loggerState,
        data: this.loggerData
      } : void 0
    });
  }
  /**
   * Converts session to a plain JSON object.
   * @returns Plain object representation of the session
   */
  toJSON() {
    return {
      // Identifiers
      id: this.id,
      unosId: this.unosId,
      matchRunId: this.matchRunId,
      key: this.key,
      joinKey: this.joinKey,
      // Session Hierarchy
      parentSessionId: this.parentSessionId,
      organPosition: this.organPosition,
      childSessionIds: this.childSessionIds,
      cancelledChildSessionIds: this.cancelledChildSessionIds,
      // Sharing
      shareKey: this.shareKey,
      shared: this.shared,
      sharedAt: this.sharedAt,
      sharedBy: this.sharedBy,
      // Session Configuration
      organ: this.organ,
      product: this.product,
      sessionType: this.sessionType,
      donationType: this.donationType,
      preservationMethods: this.preservationMethods,
      schemaVersion: this.schemaVersion,
      offerSchemaVersion: this.offerSchemaVersion,
      // Session State
      finished: this.finished,
      reviewState: this.reviewState,
      cancelled: this.cancelled,
      cancelledBy: this.cancelledBy,
      cancelledAt: this.cancelledAt,
      cancellationReason: this.cancellationReason,
      // Users & Access
      admin: this.admin,
      userIds: this.userIds,
      retainedUserIds: this.retainedUserIds,
      organizationIds: this.organizationIds,
      areaIds: this.areaIds,
      territoryIds: this.territoryIds,
      // Device Information
      device: this.device,
      appVersion: this.appVersion,
      os: this.os,
      osVersion: this.osVersion,
      pairedDeviceIdentifier: this.pairedDeviceIdentifier,
      // Location & Routing
      donorCenter: this.donorCenter,
      recipientCenter: this.recipientCenter,
      destination: this.destination,
      opoHandle: this.opoHandle,
      opo: this.opo,
      distance: this.distance,
      eta: this.eta,
      etaSetBy: this.etaSetBy,
      // Advanced Tracking & Flights
      hasAdvancedTracking: this.hasAdvancedTracking,
      advancedTrackerId: this.advancedTrackerId,
      advancedTracking: this.advancedTracking?.toJSON(),
      hasFlights: this.hasFlights,
      flights: this.flights?.map((flight) => flight.toJSON()) ?? [],
      flightPosition: this.flightPosition?.toJSON(),
      // Logger & Sensor Data
      loggerPasskey: this.loggerPasskey,
      loggerSerialNo: this.loggerSerialNo,
      loggerSerialNumbers: this.loggerSerialNumbers,
      currentLoggerSerialNumbers: this.currentLoggerSerialNumbers,
      loggerConnected: this.loggerConnected,
      advancedTrackerIds: this.advancedTrackerIds,
      products: this.products,
      loggerState: this.loggerState,
      loggerConnectedBy: this.loggerConnectedBy,
      loggerData: this.loggerData?.toJSON(),
      location: this.location?.toJSON(),
      loggers: this.loggers,
      ischemicTime: this.ischemicTime?.toJSON(),
      // Reports & Metrics
      metadata: this.metadata?.toJSON(),
      reportData: this.reportData?.toJSON(),
      metrics: this.metrics?.toJSON(),
      pdfSummaryAvailable: this.pdfSummaryAvailable,
      lastTimelineEvent: this.lastTimelineEvent,
      // OR Schedule
      donorOr: this.donorOr,
      recipientOr: this.recipientOr,
      // Offer Fields
      phase: this.phase,
      offerStatus: this.offerStatus,
      rank: this.rank,
      sequence: this.sequence,
      attachments: this.attachments.map((attachment) => attachment.toJSON()),
      // Timestamps & Creation
      createdAt: this.createdAt,
      createdBy: this.createdBy,
      createdByOrganization: this.createdByOrganization,
      createdFrom: this.createdFrom,
      endedAt: this.endedAt,
      // Demo Mode
      demo: this.demo,
      demoParentId: this.demoParentId,
      demoState: this.demoState?.toJSON()
    };
  }
  /**
   * Session duration in milliseconds.
   * @returns Duration or null if session hasn't ended
   */
  get duration() {
    return this.endedAt ? this.endedAt.getTime() - this.createdAt.getTime() : null;
  }
  /**
   * Computed session state based on logger and finished status.
   * @returns Current session state type
   */
  get state() {
    if (this.isCreatedState()) {
      return "CREATED" /* CREATED */;
    }
    if (this.isLoggerStartedState()) {
      return "LOGGER_STARTED" /* LOGGER_STARTED */;
    }
    if (this.isLoggerStoppedState()) {
      return "LOGGER_STOPPED" /* LOGGER_STOPPED */;
    }
    if (this.isFinishedState()) {
      return "FINISHED" /* FINISHED */;
    }
    return "UNKNOWN" /* UNKNOWN */;
  }
  /**
   * Serializes session to a JSON-safe object (removes class instances).
   * @returns JSON-safe plain object
   */
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // Private Methods
  // ─────────────────────────────────────────────────────────────────────────────
  isCreatedState() {
    return this.loggerState !== "STOPPED" && !this.finished && this.pairedDeviceIdentifier == null;
  }
  isLoggerStartedState() {
    return this.loggerState === "STARTED" && !this.finished;
  }
  isLoggerStoppedState() {
    return this.loggerState === "STOPPED" && !this.finished;
  }
  isFinishedState() {
    return this.finished;
  }
};
var SessionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Session({
      ...data,
      id: doc.id,
      eta: toDate(data.eta),
      location: parseLocation(data.location),
      advancedTracking: parseAdvancedTracking(data.advancedTracking),
      flights: parseFlights(data.flights),
      flightPosition: parseFlightPosition(data.flightPosition),
      loggerData: parseLoggerData(data.loggerData, data.temperature),
      ischemicTime: parseSessionIschemicTime(data.ischemicTime),
      reportData: parseSessionReportData3(data.reportData),
      demoState: parseDemoState(data.demoState),
      donorOr: parseOrSchedule(data.donorOr),
      recipientOr: parseOrSchedule(data.recipientOr),
      createdAt: toDate(data.createdAt),
      endedAt: toDate(data.endedAt)
    });
  }
};
function parseOrSchedule(data) {
  if (!data) return void 0;
  return { ...data, time: toDate(data.time) };
}
function parseDemoState(data) {
  return data ? {
    ...data,
    originalStartTime: data.originalStartTime?.toDate != null && typeof data.originalStartTime?.toDate === "function" ? data.originalStartTime.toDate() : data.originalStartTime
  } : void 0;
}
function parseLocation(data) {
  return data ? { ...data, timestamp: data.timestamp?.toDate() } : void 0;
}
function parseAdvancedTracking(data) {
  return data ? { ...data, timestamp: data.timestamp?.toDate() } : void 0;
}
function parseFlights(data) {
  return data?.length > 0 ? data.map((flight) => ({
    ...flight,
    dataTimestamp: flight.dataTimestamp?.toDate(),
    actualArrival: flight.actualArrival?.toDate(),
    actualDeparture: flight.actualDeparture?.toDate(),
    estimatedDeparture: flight.estimatedDeparture?.toDate(),
    estimatedArrival: flight.estimatedArrival?.toDate(),
    plannedArrival: flight.plannedArrival?.toDate(),
    plannedDeparture: flight.plannedDeparture?.toDate()
  })).sort((a, b) => {
    return (a.actualDeparture?.getTime() ?? a.estimatedDeparture?.getTime() ?? a.plannedDeparture?.getTime() ?? 0) - (b.actualDeparture?.getTime() ?? b.estimatedDeparture?.getTime() ?? b.plannedDeparture?.getTime() ?? 0);
  }) : void 0;
}
function parseFlightPosition(data) {
  return data ? {
    ...data,
    timestamp: data.timestamp?.toDate()
  } : void 0;
}
function parseLoggerData(loggerData, temperature) {
  if (loggerData) {
    return {
      ...loggerData,
      timestamp: loggerData.timestamp?.toDate()
    };
  }
  if (temperature) {
    return {
      ...temperature,
      timestamp: temperature.timestamp?.toDate()
    };
  }
  return void 0;
}
function parseSessionIschemicTime(data) {
  return data ? {
    ...data,
    startTimestamp: data.startTimestamp?.toDate(),
    endTimestamp: data.endTimestamp?.toDate()
  } : void 0;
}
function parseSessionReportData3(data) {
  return data ? {
    ...data,
    loggerStartedAt: data.loggerStartedAt?.toDate(),
    loggerStoppedAt: data.loggerStoppedAt?.toDate(),
    firstReading: data.firstReading?.toDate(),
    lastReading: data.lastReading?.toDate(),
    timeOnPump: data.timeOnPump ? {
      ...data.timeOnPump,
      startTimestamp: data.timeOnPump.startTimestamp?.toDate(),
      endTimestamp: data.timeOnPump.endTimestamp?.toDate()
    } : void 0
  } : void 0;
}

// src/models/session-eta.ts
var SessionETA = class {
  id;
  destinationType;
  timestamp;
  userId;
  deleted;
  deletedAt;
  constructor(params) {
    this.id = params.id;
    this.destinationType = params.destinationType;
    this.userId = params.userId;
    this.deleted = params.deleted ?? false;
    this.timestamp = params.timestamp instanceof Date ? params.timestamp : new Date(params.timestamp);
    this.deletedAt = params.deletedAt instanceof Date ? params.deletedAt : params.deletedAt ? new Date(params.deletedAt) : null;
  }
  toJSON() {
    return {
      id: this.id,
      destinationType: this.destinationType,
      timestamp: this.timestamp,
      userId: this.userId,
      deleted: this.deleted,
      deletedAt: this.deletedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var SessionETAConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new SessionETA({
      ...data,
      id: doc.id,
      timestamp: data.timestamp?.toDate(),
      deletedAt: data.deletedAt?.toDate() ?? null
    });
  }
};

// src/models/session-loggers.ts
function sanitizeLoggersForSharing(loggers) {
  const sanitized = {};
  for (const [key, info] of Object.entries(loggers)) {
    if (info?.loggerId) {
      sanitized[key] = {
        loggerId: info.loggerId,
        ...info.product != null && { product: info.product }
      };
    }
  }
  return sanitized;
}

// src/models/session-member.ts
var SessionMember = class {
  id;
  email;
  firstname;
  lastname;
  isParagonixUser;
  organization;
  organizationId;
  organizationHandle;
  organizationAccessRole;
  organizationRole;
  organizationTitle;
  joinedAt;
  deleted;
  deletedAt;
  muted;
  accessLevel;
  permissions;
  constructor(params) {
    this.id = params.id;
    this.email = params.email;
    this.firstname = params.firstname;
    this.lastname = params.lastname;
    this.isParagonixUser = params.isParagonixUser ?? false;
    this.organization = params.organization;
    this.organizationId = params.organizationId;
    this.organizationHandle = params.organizationHandle;
    this.organizationAccessRole = params.organizationAccessRole;
    this.organizationRole = params.organizationRole;
    this.organizationTitle = params.organizationTitle;
    this.joinedAt = typeof params.joinedAt === "string" ? new Date(params.joinedAt) : params.joinedAt;
    this.deleted = params.deleted ?? false;
    this.deletedAt = params.deletedAt != null ? typeof params.deletedAt === "string" ? new Date(params.deletedAt) : params.deletedAt : void 0;
    this.muted = params.muted ?? false;
    this.accessLevel = params.accessLevel;
    this.permissions = params.permissions;
  }
  toJSON() {
    return {
      id: this.id,
      email: this.email,
      firstname: this.firstname,
      lastname: this.lastname,
      isParagonixUser: this.isParagonixUser,
      organization: this.organization,
      organizationId: this.organizationId,
      organizationHandle: this.organizationHandle,
      organizationAccessRole: this.organizationAccessRole,
      organizationRole: this.organizationRole,
      organizationTitle: this.organizationTitle,
      joinedAt: this.joinedAt,
      deleted: this.deleted,
      deletedAt: this.deletedAt,
      muted: this.muted,
      accessLevel: this.accessLevel,
      permissions: this.permissions
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  get fullname() {
    return `${this.firstname} ${this.lastname}`;
  }
  get initials() {
    return `${this.firstname?.charAt(0) ?? ""}${this.lastname?.charAt(0) ?? ""}`;
  }
};
var SessionMemberConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new SessionMember({ ...data, id: doc.id, joinedAt: data.joinedAt?.toDate(), deletedAt: data.deletedAt?.toDate() });
  }
};

// src/models/session-member-permissions/fallbacks.ts
var OBSERVER_PERMISSIONS = {
  version: 1,
  timeline: {
    readEntries: true,
    readComments: false,
    readAttachments: false,
    addEntry: false,
    editEntry: false,
    deleteEntry: false,
    addComment: false,
    deleteComment: false,
    addAttachment: false,
    deleteAttachment: false,
    react: false,
    addCustomEntry: false,
    readCustomEntries: false,
    editCustomEntry: false,
    deleteCustomEntry: false
  },
  team: {
    read: true,
    addMembers: false,
    removeMembers: false,
    editMemberAccessLevel: false
  },
  chat: {
    read: false,
    send: false,
    delete: false,
    react: false,
    create: false,
    addMembers: false,
    removeMembers: false,
    editMetadata: false,
    leave: false
  },
  offer: {
    readDonorInfo: false,
    editDonorInfo: false,
    readRecipientInfo: false,
    editRecipientInfo: false,
    readStatus: true,
    editStatus: false,
    readCriticalNotes: false,
    editCriticalNotes: false,
    readAttachments: false,
    addAttachments: false,
    deleteAttachments: false
  },
  sharing: {
    share: true,
    createOfferLink: false
  },
  exports: {
    xlsx: true,
    pdf: true
  },
  eta: {
    read: true,
    add: false,
    edit: false,
    remove: false
  },
  flights: {
    read: true,
    add: false,
    delete: false
  },
  breakoutSessions: {
    create: false,
    cancel: false
  },
  session: {
    readDonorId: true,
    editDonorId: false,
    deleteDonorId: false,
    readMatchRunId: true,
    editMatchRunId: false,
    deleteMatchRunId: false,
    readDonorCenter: true,
    editDonorCenter: false,
    deleteDonorCenter: false,
    readRecipientCenter: true,
    editRecipientCenter: false,
    deleteRecipientCenter: false,
    readOpo: true,
    editOpo: false,
    deleteOpo: false
  },
  device: {
    read: true,
    connect: false,
    disconnect: false,
    unlink: false,
    start: false,
    claim: false
  },
  vantagePoint: {
    read: true,
    connect: false,
    unlink: false
  },
  preservationMethods: {
    read: true,
    edit: false
  },
  readAuditLog: true,
  endSession: false,
  muteSession: false,
  pinSession: false,
  leaveSession: false
};
var DEFAULT_FALLBACK_PERMISSIONS = {
  version: 1,
  timeline: {
    readEntries: true,
    readComments: false,
    readAttachments: false,
    addEntry: true,
    editEntry: true,
    deleteEntry: true,
    addComment: false,
    deleteComment: false,
    addAttachment: false,
    deleteAttachment: false,
    react: false,
    addCustomEntry: false,
    readCustomEntries: false,
    editCustomEntry: false,
    deleteCustomEntry: false
  },
  team: {
    read: true,
    addMembers: false,
    removeMembers: false,
    editMemberAccessLevel: false
  },
  chat: {
    read: true,
    send: true,
    delete: true,
    react: true,
    create: true,
    addMembers: true,
    removeMembers: true,
    editMetadata: true,
    leave: true
  },
  offer: {
    readDonorInfo: false,
    editDonorInfo: false,
    readRecipientInfo: false,
    editRecipientInfo: false,
    readStatus: false,
    editStatus: false,
    readCriticalNotes: false,
    editCriticalNotes: false,
    readAttachments: false,
    addAttachments: false,
    deleteAttachments: false
  },
  sharing: {
    share: true,
    createOfferLink: true
  },
  exports: {
    xlsx: true,
    pdf: true
  },
  eta: {
    read: true,
    add: true,
    edit: true,
    remove: true
  },
  flights: {
    read: true,
    add: true,
    delete: true
  },
  breakoutSessions: {
    create: true,
    cancel: true
  },
  session: {
    readDonorId: true,
    editDonorId: true,
    deleteDonorId: true,
    readMatchRunId: true,
    editMatchRunId: true,
    deleteMatchRunId: true,
    readDonorCenter: true,
    editDonorCenter: true,
    deleteDonorCenter: true,
    readRecipientCenter: true,
    editRecipientCenter: true,
    deleteRecipientCenter: true,
    readOpo: true,
    editOpo: true,
    deleteOpo: true
  },
  device: {
    read: true,
    connect: true,
    disconnect: true,
    // Logger unlinking is temporarily disabled for all clients. This read-time
    // fallback is web-only (apps have their own pre-v7 fallback), so this is
    // for consistency — the unlinkLogger callable block is the real safety net.
    unlink: false,
    start: true,
    claim: true
  },
  vantagePoint: {
    read: true,
    connect: true,
    unlink: true
  },
  preservationMethods: {
    read: false,
    edit: false
  },
  readAuditLog: true,
  endSession: true,
  muteSession: true,
  pinSession: false,
  leaveSession: true
};

// src/models/session-member-permissions/v1.ts
var FULL_PERMISSIONS = {
  version: 1,
  timeline: {
    readEntries: true,
    readComments: false,
    readAttachments: false,
    addEntry: true,
    editEntry: true,
    deleteEntry: true,
    addComment: false,
    deleteComment: false,
    addAttachment: false,
    deleteAttachment: false,
    react: false,
    addCustomEntry: false,
    readCustomEntries: false,
    editCustomEntry: false,
    deleteCustomEntry: false
  },
  team: {
    read: true,
    addMembers: true,
    removeMembers: true,
    editMemberAccessLevel: true
  },
  chat: {
    read: true,
    send: true,
    delete: true,
    react: true,
    create: true,
    addMembers: true,
    removeMembers: true,
    editMetadata: true,
    leave: true
  },
  offer: {
    readDonorInfo: false,
    editDonorInfo: false,
    readRecipientInfo: false,
    editRecipientInfo: false,
    readStatus: true,
    editStatus: true,
    readCriticalNotes: false,
    editCriticalNotes: false,
    readAttachments: false,
    addAttachments: false,
    deleteAttachments: false
  },
  sharing: {
    share: true,
    createOfferLink: true
  },
  exports: {
    xlsx: true,
    pdf: true
  },
  eta: {
    read: true,
    add: true,
    edit: true,
    remove: true
  },
  flights: {
    read: true,
    add: true,
    delete: true
  },
  breakoutSessions: {
    create: true,
    cancel: true
  },
  session: {
    readDonorId: true,
    editDonorId: true,
    deleteDonorId: true,
    readMatchRunId: true,
    editMatchRunId: true,
    deleteMatchRunId: true,
    readDonorCenter: true,
    editDonorCenter: true,
    deleteDonorCenter: true,
    readRecipientCenter: true,
    editRecipientCenter: true,
    deleteRecipientCenter: true,
    readOpo: true,
    editOpo: true,
    deleteOpo: true
  },
  device: {
    read: true,
    connect: true,
    disconnect: true,
    // Logger unlinking is temporarily disabled for all clients. resolveV1Permissions
    // also forces this to false; kept false here so the preset can't grant it.
    unlink: false,
    start: true,
    claim: true
  },
  vantagePoint: {
    read: true,
    connect: true,
    unlink: true
  },
  preservationMethods: {
    read: true,
    edit: true
  },
  readAuditLog: true,
  endSession: true,
  muteSession: true,
  pinSession: true,
  leaveSession: true
};
var LIMITED_PERMISSIONS = {
  version: 1,
  timeline: {
    readEntries: true,
    readComments: false,
    readAttachments: false,
    addEntry: true,
    editEntry: true,
    deleteEntry: true,
    addComment: false,
    deleteComment: false,
    addAttachment: false,
    deleteAttachment: false,
    react: false,
    addCustomEntry: false,
    readCustomEntries: false,
    editCustomEntry: false,
    deleteCustomEntry: false
  },
  team: {
    read: true,
    addMembers: false,
    removeMembers: false,
    editMemberAccessLevel: false
  },
  chat: {
    read: true,
    send: true,
    delete: true,
    react: true,
    create: true,
    addMembers: true,
    removeMembers: true,
    editMetadata: true,
    leave: true
  },
  offer: {
    readDonorInfo: false,
    editDonorInfo: false,
    readRecipientInfo: false,
    editRecipientInfo: false,
    readStatus: true,
    editStatus: false,
    readCriticalNotes: false,
    editCriticalNotes: false,
    readAttachments: false,
    addAttachments: false,
    deleteAttachments: false
  },
  sharing: {
    share: true,
    createOfferLink: true
  },
  exports: {
    xlsx: true,
    pdf: true
  },
  eta: {
    read: true,
    add: true,
    edit: true,
    remove: true
  },
  flights: {
    read: true,
    add: true,
    delete: true
  },
  breakoutSessions: {
    create: true,
    cancel: true
  },
  session: {
    readDonorId: true,
    editDonorId: true,
    deleteDonorId: true,
    readMatchRunId: true,
    editMatchRunId: true,
    deleteMatchRunId: true,
    readDonorCenter: true,
    editDonorCenter: true,
    deleteDonorCenter: true,
    readRecipientCenter: true,
    editRecipientCenter: true,
    deleteRecipientCenter: true,
    readOpo: true,
    editOpo: true,
    deleteOpo: true
  },
  device: {
    read: true,
    connect: true,
    disconnect: true,
    // Logger unlinking is temporarily disabled for all clients. resolveV1Permissions
    // also forces this to false; kept false here so the preset can't grant it.
    unlink: false,
    start: true,
    claim: true
  },
  vantagePoint: {
    read: true,
    connect: true,
    unlink: true
  },
  preservationMethods: {
    read: true,
    edit: true
  },
  readAuditLog: true,
  endSession: true,
  muteSession: true,
  pinSession: true,
  leaveSession: true
};
function applyPhiOverlay(permissions) {
  permissions.timeline.readComments = true;
  permissions.timeline.readAttachments = true;
  permissions.timeline.addComment = true;
  permissions.timeline.deleteComment = true;
  permissions.timeline.addAttachment = true;
  permissions.timeline.deleteAttachment = true;
  permissions.timeline.react = true;
  permissions.timeline.addCustomEntry = true;
  permissions.timeline.readCustomEntries = true;
  permissions.timeline.editCustomEntry = true;
  permissions.timeline.deleteCustomEntry = true;
  permissions.offer.readDonorInfo = true;
  permissions.offer.editDonorInfo = true;
  permissions.offer.readRecipientInfo = true;
  permissions.offer.editRecipientInfo = true;
  permissions.offer.readCriticalNotes = true;
  permissions.offer.editCriticalNotes = true;
  permissions.offer.readAttachments = true;
  permissions.offer.addAttachments = true;
  permissions.offer.deleteAttachments = true;
}
function shouldEnablePhi(organType, context) {
  if (!context) return false;
  if (context.user?.access?.dev === true || context.user?.access?.admin === true) {
    return true;
  }
  const isOrgInPilot = organType != null && (context.organization?.pilot?.offerManagement?.organTypes?.includes(organType) ?? false);
  const isOrgActive = context.organization?.status === "ACTIVE" /* ACTIVE */;
  const isMemberActive = context.organizationMember?.status === "ACTIVE" /* ACTIVE */;
  return isOrgInPilot && isOrgActive && isMemberActive;
}
function resolveV1Permissions(accessLevel, organType, context) {
  let permissions;
  switch (accessLevel) {
    case "FULL" /* FULL */:
      permissions = structuredClone(FULL_PERMISSIONS);
      break;
    case "LIMITED" /* LIMITED */:
      permissions = structuredClone(LIMITED_PERMISSIONS);
      break;
  }
  if (accessLevel === "FULL" /* FULL */ && shouldEnablePhi(organType, context)) {
    applyPhiOverlay(permissions);
  }
  if (organType !== "kidney" /* KIDNEY */) {
    permissions.breakoutSessions.create = false;
    permissions.breakoutSessions.cancel = false;
    permissions.sharing.createOfferLink = false;
    permissions.exports.pdf = false;
  }
  permissions.device.unlink = false;
  return permissions;
}
function revokeChatOnMap(permissions) {
  permissions.chat.read = false;
  permissions.chat.send = false;
  permissions.chat.delete = false;
  permissions.chat.react = false;
  permissions.chat.create = false;
  permissions.chat.addMembers = false;
  permissions.chat.removeMembers = false;
  permissions.chat.editMetadata = false;
  permissions.chat.leave = false;
}
function resolveEffectiveV1Permissions(session, sessionMember, userId, context) {
  const isInSession = session.userIds.includes(userId);
  if (!isInSession) {
    return structuredClone(OBSERVER_PERMISSIONS);
  }
  if (session.schemaVersion < 7) {
    const permissions = structuredClone(DEFAULT_FALLBACK_PERMISSIONS);
    if (session.finished) {
      revokeChatOnMap(permissions);
    }
    return permissions;
  }
  const accessLevel = sessionMember?.accessLevel ?? "LIMITED" /* LIMITED */;
  return resolveV1Permissions(accessLevel, session.organ, context);
}

// src/models/session-member-permissions/index.ts
function resolveSessionMemberPermissions(accessLevel, organType, context) {
  return resolveV1Permissions(accessLevel, organType, context);
}
function resolveEffectivePermissions(session, sessionMember, userId, context) {
  return resolveEffectiveV1Permissions(session, sessionMember, userId, context);
}

// src/models/session-phase.ts
var SessionPhase = /* @__PURE__ */ ((SessionPhase2) => {
  SessionPhase2["OFFER"] = "OFFER";
  SessionPhase2["PROCUREMENT"] = "PROCUREMENT";
  return SessionPhase2;
})(SessionPhase || {});
var SessionPhaseUtil = class {
  /**
   * Converts a SessionPhase to a human-readable UI string.
   *
   * @param phase - The session phase to convert
   * @param capitalize - Whether to capitalize the first letter (default: true)
   * @returns A formatted string representation of the phase
   *
   * @example
   * ```typescript
   * SessionPhaseUtil.toUIString(SessionPhase.OFFER); // "Offer"
   * SessionPhaseUtil.toUIString(SessionPhase.OFFER, false); // "offer"
   * ```
   */
  static toUIString(phase, capitalize = true) {
    switch (phase) {
      case "OFFER" /* OFFER */:
        return capitalize ? "Offer" : "offer";
      case "PROCUREMENT" /* PROCUREMENT */:
        return capitalize ? "Procurement" : "procurement";
      default:
        return capitalize ? "Unknown" : "unknown";
    }
  }
};

// src/models/session-review-state.ts
var SessionReviewState = /* @__PURE__ */ ((SessionReviewState2) => {
  SessionReviewState2["OPEN"] = "OPEN";
  SessionReviewState2["MORE_INFORMATION_REQUIRED"] = "MORE_INFORMATION_REQUIRED";
  SessionReviewState2["REVIEWED"] = "REVIEWED";
  SessionReviewState2["NO_REVIEW_REQUIRED"] = "NO_REVIEW_REQUIRED";
  return SessionReviewState2;
})(SessionReviewState || {});
var SessionReviewStateUtil = {
  toUIString: (state) => {
    switch (state) {
      case "OPEN" /* OPEN */:
        return "Open";
      case "MORE_INFORMATION_REQUIRED" /* MORE_INFORMATION_REQUIRED */:
        return "More Information Required";
      case "REVIEWED" /* REVIEWED */:
        return "Reviewed";
      case "NO_REVIEW_REQUIRED" /* NO_REVIEW_REQUIRED */:
        return "No Review Required";
      default:
        return state;
    }
  }
};

// src/models/session-type.ts
var SessionType = /* @__PURE__ */ ((SessionType2) => {
  SessionType2["CLINICAL"] = "CLINICAL";
  SessionType2["TRAINING"] = "TRAINING";
  SessionType2["UNKNOWN"] = "UNKNOWN";
  return SessionType2;
})(SessionType || {});
var SessionTypeUtil = class {
  static toUIString(type, capitalize = true) {
    switch (type) {
      case "CLINICAL" /* CLINICAL */:
        return capitalize ? "Clinical" : "clinical";
      case "TRAINING" /* TRAINING */:
        return capitalize ? "Training" : "training";
      default:
        return capitalize ? "Unknown" : "unknown";
    }
  }
};

// src/models/session-view.ts
var SessionView = class {
  id;
  name;
  searchParams;
  columns;
  createdAt;
  updatedAt;
  constructor(params) {
    this.id = params.id;
    this.name = params.name;
    this.searchParams = { ...params.searchParams };
    this.columns = [...params.columns];
    this.createdAt = toDate(params.createdAt) || /* @__PURE__ */ new Date();
    this.updatedAt = toDate(params.updatedAt);
  }
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      searchParams: this.searchParams,
      columns: this.columns,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var SessionViewConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new SessionView({
      id: doc.id,
      name: data.name ?? "",
      searchParams: data.searchParams ?? {},
      columns: Array.isArray(data.columns) ? data.columns : [],
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/session-view-settings.ts
var SessionViewSettings = class {
  builtInColumns;
  updatedAt;
  constructor(params = {}) {
    this.builtInColumns = { ...params.builtInColumns ?? {} };
    this.updatedAt = toDate(params.updatedAt);
  }
  /** Returns the configured columns for a given built-in view, or undefined if none. */
  columnsFor(builtIn) {
    const cols = this.builtInColumns[builtIn];
    return Array.isArray(cols) ? [...cols] : void 0;
  }
  toJSON() {
    return {
      builtInColumns: this.builtInColumns,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var SessionViewSettingsConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new SessionViewSettings({
      builtInColumns: data.builtInColumns,
      updatedAt: data.updatedAt?.toDate()
    });
  }
};

// src/models/shared-session.ts
var SharedSession = class {
  /** Shared session ID (matches the original session ID) */
  id;
  /** Whether the shared session is currently active */
  shared;
  /** Unique key used to construct the public share URL */
  shareKey;
  /** Timestamp when the session was shared */
  sharedAt;
  /** User ID of the person who shared the session */
  sharedBy;
  /** Organ position for breakout sessions */
  organPosition;
  /** Map of organ position to child breakout session IDs */
  childSessionIds;
  /** Whether the session has been completed */
  finished;
  /**
   * Device model that created the session.
   * @deprecated Not set for schemaVersion >= 7.
   */
  device;
  /**
   * App version that created the session.
   * @deprecated Not set for schemaVersion >= 7.
   */
  appVersion;
  /**
   * Operating system name.
   * @deprecated Not set for schemaVersion >= 7.
   */
  os;
  /**
   * Operating system version.
   * @deprecated Not set for schemaVersion >= 7.
   */
  osVersion;
  /** Organ type being transported */
  organ;
  /** Product type (e.g., KidneyVault) */
  product;
  /** Session type (clinical, demo, etc.) */
  sessionType;
  /** Donation type (DBD, DCD, etc.) */
  donationType;
  /** Schema version of the original session */
  schemaVersion;
  /** UNOS ID for the organ */
  unosId;
  /**
   * Sanitized loggers map for v7+ sessions.
   * @see {@link SharedSessionLoggers}
   */
  loggers;
  /** User IDs who have viewed the shared session */
  readByUserIds;
  /** Original session creation timestamp */
  createdAt;
  /** Session end timestamp */
  endedAt;
  constructor(params) {
    this.id = params.id;
    this.shared = params.shared ?? true;
    this.shareKey = params.shareKey;
    this.sharedAt = typeof params.sharedAt === "string" ? new Date(params.sharedAt) : params.sharedAt;
    this.sharedBy = params.sharedBy;
    this.organPosition = params.organPosition;
    this.childSessionIds = params.childSessionIds;
    this.finished = params.finished ?? false;
    this.device = params.device;
    this.appVersion = params.appVersion;
    this.os = params.os;
    this.osVersion = params.osVersion;
    this.organ = params.organ;
    this.product = params.product;
    this.sessionType = params.sessionType;
    this.donationType = params.donationType;
    this.schemaVersion = params.schemaVersion;
    this.unosId = params.unosId;
    this.loggers = params.loggers;
    this.readByUserIds = params.readByUserIds ?? [];
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.endedAt = typeof params.endedAt === "string" ? new Date(params.endedAt) : params.endedAt;
  }
  toJSON() {
    return {
      id: this.id,
      shared: this.shared,
      shareKey: this.shareKey,
      sharedAt: this.sharedAt,
      sharedBy: this.sharedBy,
      organPosition: this.organPosition,
      childSessionIds: this.childSessionIds,
      finished: this.finished,
      device: this.device,
      appVersion: this.appVersion,
      os: this.os,
      osVersion: this.osVersion,
      organ: this.organ,
      product: this.product,
      sessionType: this.sessionType,
      donationType: this.donationType,
      schemaVersion: this.schemaVersion,
      unosId: this.unosId,
      loggers: this.loggers,
      readByUserIds: this.readByUserIds,
      createdAt: this.createdAt,
      endedAt: this.endedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var SharedSessionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new SharedSession({
      ...data,
      id: doc.id,
      sharedAt: data.sharedAt?.toDate(),
      createdAt: data.createdAt?.toDate(),
      endedAt: data.endedAt?.toDate()
    });
  }
};

// src/models/territory.ts
var Territory = class {
  id;
  areaId;
  name;
  createdAt;
  updatedAt;
  constructor(params) {
    this.id = params.id;
    this.areaId = params.areaId;
    this.name = params.name;
    this.createdAt = params.createdAt instanceof Date ? params.createdAt : new Date(params.createdAt);
    this.updatedAt = params.updatedAt instanceof Date ? params.updatedAt : new Date(params.updatedAt);
  }
  toJSON() {
    return {
      id: this.id,
      areaId: this.areaId,
      name: this.name,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var TerritoryConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Territory({ ...data, id: doc.id, createdAt: data.createdAt?.toDate(), updatedAt: data.updatedAt?.toDate() });
  }
};

// src/models/timeline-entry.ts
var TimelineEntry2 = class {
  id;
  referenceId;
  type;
  title;
  annotations;
  senderId;
  skipPushNotification;
  timestamp;
  createdAt;
  committedAt;
  deleted;
  deletedAt;
  commentCount;
  unreadCount;
  constructor(params) {
    this.id = params.id;
    this.referenceId = params.referenceId;
    this.type = params.type;
    this.title = params.title;
    this.annotations = params.annotations ?? {};
    this.senderId = params.senderId;
    this.skipPushNotification = params.skipPushNotification ?? false;
    this.timestamp = typeof params.timestamp === "string" ? new Date(params.timestamp) : params.timestamp;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.committedAt = typeof params.committedAt === "string" ? new Date(params.committedAt) : params.committedAt;
    this.deleted = params.deleted ?? false;
    this.deletedAt = typeof params.deletedAt === "string" ? new Date(params.deletedAt) : params.deletedAt;
    this.commentCount = params.commentCount ?? 0;
    this.unreadCount = params.unreadCount ?? {};
  }
  toJSON() {
    return {
      id: this.id,
      referenceId: this.referenceId,
      type: this.type,
      title: this.title,
      annotations: this.annotations,
      senderId: this.senderId,
      skipPushNotification: this.skipPushNotification,
      timestamp: this.timestamp,
      createdAt: this.createdAt,
      committedAt: this.committedAt,
      deleted: this.deleted,
      deletedAt: this.deletedAt,
      commentCount: this.commentCount,
      unreadCount: this.unreadCount
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var TimelineEntryConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new TimelineEntry2({
      ...data,
      id: doc.id,
      timestamp: data.timestamp?.toDate(),
      createdAt: data.createdAt?.toDate(),
      committedAt: data.committedAt?.toDate(),
      deletedAt: data.deletedAt?.toDate()
    });
  }
};
function compareTimelineEntriesDesc(a, b) {
  const timeDiff = b.timestamp.getTime() - a.timestamp.getTime();
  if (timeDiff !== 0) return timeDiff;
  return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
}

// src/models/timeline-entry-attribute-type.ts
var TimelineEntryAttributeType = /* @__PURE__ */ ((TimelineEntryAttributeType3) => {
  TimelineEntryAttributeType3["STARTS_LOGGER"] = "STARTS_LOGGER";
  TimelineEntryAttributeType3["STOPS_LOGGER"] = "STOPS_LOGGER";
  return TimelineEntryAttributeType3;
})(TimelineEntryAttributeType || {});

// src/models/timeline-entry-read-state.ts
var TimelineEntryReadState = class {
  userId;
  lastReadCommentCursor;
  constructor(params) {
    this.userId = params.userId;
    this.lastReadCommentCursor = params.lastReadCommentCursor ? new ReadCursor(params.lastReadCommentCursor) : void 0;
  }
  toJSON() {
    return {
      userId: this.userId,
      lastReadCommentCursor: this.lastReadCommentCursor?.toJSON()
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var TimelineEntryReadStateConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new TimelineEntryReadState({
      userId: doc.id,
      lastReadCommentCursor: data.lastReadCommentCursor ? { documentId: data.lastReadCommentCursor.documentId, readAt: data.lastReadCommentCursor.readAt?.toDate() } : void 0
    });
  }
};

// src/models/tutorial.ts
var Tutorial = class {
  id;
  title;
  text;
  sort;
  videoRefName;
  imageRefName;
  constructor(params) {
    this.id = params.id;
    this.title = params.title;
    this.text = params.text;
    this.sort = params.sort ?? 0;
    this.videoRefName = params.videoRefName;
    this.imageRefName = params.imageRefName;
  }
  toJSON() {
    return {
      id: this.id,
      title: this.title,
      text: this.text,
      sort: this.sort,
      videoRefName: this.videoRefName,
      imageRefName: this.imageRefName
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var TutorialConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new Tutorial({ ...data, id: doc.id });
  }
};

// src/models/user-access.ts
var UserAccess = class {
  types;
  dev;
  admin;
  qa;
  global;
  areas;
  territories;
  organizations;
  features;
  restrictToOrgans;
  constructor(params) {
    this.types = params?.types ?? [];
    this.dev = params?.dev ?? false;
    this.admin = params?.admin ?? false;
    this.qa = params?.qa ?? false;
    this.global = params?.global ?? false;
    this.areas = params?.areas ?? [];
    this.territories = params?.territories ?? [];
    this.organizations = params?.organizations ?? [];
    this.features = params?.features ?? [];
    this.restrictToOrgans = params?.restrictToOrgans ?? [];
  }
  toJSON() {
    return {
      types: this.types,
      dev: this.dev,
      admin: this.admin,
      qa: this.qa,
      global: this.global,
      areas: this.areas,
      territories: this.territories,
      organizations: this.organizations,
      features: this.features,
      restrictToOrgans: this.restrictToOrgans
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};

// src/utils/get-default-distance-unit.ts
function getDefaultDistanceUnit(localeHint) {
  const locale = localeHint ?? (typeof window !== "undefined" ? navigator.language ?? "en-US" : void 0);
  if (locale?.toLowerCase().startsWith("en-us")) {
    return "nautical" /* NAUTICAL */;
  }
  return "metric" /* METRIC */;
}

// src/models/user-settings.ts
var UserSettings = class {
  includeTimezone;
  force24hours;
  marketingMails;
  sessionReportMails;
  forceDistanceUnit;
  forceHeightUnit;
  forceWeightUnit;
  constructor(params, localeHint) {
    this.includeTimezone = params?.includeTimezone ?? false;
    this.force24hours = params?.force24hours ?? false;
    this.marketingMails = params?.marketingMails ?? false;
    this.sessionReportMails = params?.sessionReportMails ?? true;
    this.forceDistanceUnit = params?.forceDistanceUnit ?? getDefaultDistanceUnit(localeHint);
    this.forceHeightUnit = params?.forceHeightUnit ?? getDefaultMeasurementUnit(localeHint);
    this.forceWeightUnit = params?.forceWeightUnit ?? getDefaultMeasurementUnit(localeHint);
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      includeTimezone: this.includeTimezone,
      force24hours: this.force24hours,
      marketingMails: this.marketingMails,
      sessionReportMails: this.sessionReportMails,
      forceDistanceUnit: this.forceDistanceUnit,
      forceHeightUnit: this.forceHeightUnit,
      forceWeightUnit: this.forceWeightUnit
    };
  }
};

// src/models/user.ts
var TwoFactorRequirement = /* @__PURE__ */ ((TwoFactorRequirement2) => {
  TwoFactorRequirement2["OPTIONAL"] = "OPTIONAL";
  TwoFactorRequirement2["REQUIRED"] = "REQUIRED";
  return TwoFactorRequirement2;
})(TwoFactorRequirement || {});
var User = class {
  id;
  access;
  email;
  emailVerified;
  acceptedTermsVersion;
  firstname;
  lastname;
  avatarFileName;
  avatarUrl;
  organization;
  role;
  settings;
  timezone;
  timezoneName;
  organizationIds;
  activeInOrganizationIds;
  needsOrganizationProfile;
  needsEmailVerification;
  createdAt;
  deactivatedAt;
  deactivationReason;
  inventoryLocationIds;
  isParagonixUser;
  twoFactorRequirement;
  hasSecurePassword;
  constructor(params, localeHint) {
    this.id = params.id;
    this.access = new UserAccess(params.access);
    this.email = params.email;
    this.emailVerified = params.emailVerified ?? false;
    this.hasSecurePassword = params.hasSecurePassword ?? false;
    this.firstname = params.firstname;
    this.lastname = params.lastname;
    this.avatarFileName = params.avatarFileName;
    this.avatarUrl = params.avatarUrl;
    this.organization = params.organization;
    this.role = params.role;
    this.acceptedTermsVersion = params.acceptedTermsVersion;
    this.settings = new UserSettings(params.settings, localeHint);
    this.timezone = params.timezone;
    this.timezoneName = params.timezoneName;
    this.organizationIds = params.organizationIds ?? [];
    this.activeInOrganizationIds = params.activeInOrganizationIds ?? [];
    this.needsOrganizationProfile = params.needsOrganizationProfile ?? false;
    this.needsEmailVerification = params.needsEmailVerification ?? false;
    this.createdAt = typeof params.createdAt === "string" ? new Date(params.createdAt) : params.createdAt;
    this.deactivatedAt = typeof params.deactivatedAt === "string" ? new Date(params.deactivatedAt) : params.deactivatedAt;
    this.deactivationReason = params.deactivationReason;
    this.inventoryLocationIds = params.inventoryLocationIds ?? [];
    this.isParagonixUser = params.isParagonixUser ?? false;
    this.twoFactorRequirement = params.twoFactorRequirement;
    this.hasSecurePassword = params.hasSecurePassword;
  }
  /**
   * User's full name (first + last).
   */
  get fullname() {
    return `${this.firstname} ${this.lastname}`;
  }
  /**
   * User's initials (first letter of first and last name).
   */
  get initials() {
    return `${this.firstname?.charAt(0) ?? ""}${this.lastname?.charAt(0) ?? ""}`;
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
  toJSON() {
    return {
      id: this.id,
      access: this.access.toJSON(),
      email: this.email,
      emailVerified: this.emailVerified,
      firstname: this.firstname,
      lastname: this.lastname,
      avatarFileName: this.avatarFileName,
      avatarUrl: this.avatarUrl,
      organization: this.organization,
      role: this.role,
      acceptedTermsVersion: this.acceptedTermsVersion,
      settings: this.settings.toJSON(),
      timezone: this.timezone,
      timezoneName: this.timezoneName,
      organizationIds: this.organizationIds,
      activeInOrganizationIds: this.activeInOrganizationIds,
      needsOrganizationProfile: this.needsOrganizationProfile,
      needsEmailVerification: this.needsEmailVerification,
      createdAt: this.createdAt,
      deactivatedAt: this.deactivatedAt,
      deactivationReason: this.deactivationReason,
      inventoryLocationIds: this.inventoryLocationIds,
      isParagonixUser: this.isParagonixUser,
      twoFactorRequirement: this.twoFactorRequirement,
      hasSecurePassword: this.hasSecurePassword
    };
  }
};
var UserConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new User({ ...data, id: doc.id, createdAt: data.createdAt?.toDate() });
  }
};

// src/models/user-access-type.ts
var UserAccessType = /* @__PURE__ */ ((UserAccessType2) => {
  UserAccessType2["DEV"] = "DEV";
  UserAccessType2["QA"] = "QA";
  UserAccessType2["ADMIN"] = "ADMIN";
  UserAccessType2["GLOBAL_MANAGER"] = "GLOBAL_MANAGER";
  UserAccessType2["AREA_MANAGER"] = "AREA_MANAGER";
  UserAccessType2["TERRITORY_MANAGER"] = "TERRITORY_MANAGER";
  UserAccessType2["ACCOUNT_MANAGER"] = "ACCOUNT_MANAGER";
  return UserAccessType2;
})(UserAccessType || {});
var UserAccessTypeUtil = class {
  static toUIString(type, capitalize = true) {
    switch (type) {
      case "DEV" /* DEV */:
        return capitalize ? "Dev" : "dev";
      case "QA" /* QA */:
        return capitalize ? "QA" : "qa";
      case "ADMIN" /* ADMIN */:
        return capitalize ? "Administrator" : "administrator";
      case "GLOBAL_MANAGER" /* GLOBAL_MANAGER */:
        return capitalize ? "Global Manager" : "global manager";
      case "AREA_MANAGER" /* AREA_MANAGER */:
        return capitalize ? "Area Manager" : "area manager";
      case "TERRITORY_MANAGER" /* TERRITORY_MANAGER */:
        return capitalize ? "Territory Manager" : "territory manager";
      case "ACCOUNT_MANAGER" /* ACCOUNT_MANAGER */:
        return capitalize ? "Account Manager" : "account manager";
      default:
        return capitalize ? "Unknown" : "unknown";
    }
  }
};

// src/models/user-deactivation-reason.ts
var UserDeactivationReason = /* @__PURE__ */ ((UserDeactivationReason2) => {
  UserDeactivationReason2["LEFT_ORGANIZATION"] = "LEFT_ORGANIZATION";
  return UserDeactivationReason2;
})(UserDeactivationReason || {});

// src/models/user-device.ts
var UserDevice = class {
  id;
  device;
  os;
  osVersion;
  appVersion;
  language;
  locale;
  timezone;
  timezoneName;
  timezoneIANA;
  lastSeen;
  constructor(params) {
    this.id = params.id;
    this.device = params.device;
    this.os = params.os;
    this.osVersion = params.osVersion;
    this.appVersion = params.appVersion;
    this.language = params.language;
    this.locale = params.locale;
    this.timezone = params.timezone;
    this.timezoneName = params.timezoneName;
    this.timezoneIANA = params.timezoneIANA;
    this.lastSeen = params.lastSeen instanceof Date ? params.lastSeen : new Date(params.lastSeen);
  }
  toJSON() {
    return {
      id: this.id,
      device: this.device,
      os: this.os,
      osVersion: this.osVersion,
      appVersion: this.appVersion,
      language: this.language,
      locale: this.locale,
      timezone: this.timezone,
      timezoneName: this.timezoneName,
      timezoneIANA: this.timezoneIANA,
      lastSeen: this.lastSeen
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var UserDeviceConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new UserDevice({ ...data, id: doc.id, lastSeen: data.lastSeen?.toDate() });
  }
};

// src/models/user-session.ts
var UserSession = class {
  sessionId;
  userId;
  finished;
  muted;
  pinned;
  sessionUpdates;
  unreadTimelineEntryComments;
  unreadChatMessages;
  createdAt;
  updatedAt;
  constructor(params) {
    this.sessionId = params.sessionId;
    this.userId = params.userId;
    this.finished = params.finished ?? false;
    this.muted = params.muted ?? false;
    this.pinned = params.pinned ?? false;
    this.sessionUpdates = {
      unreadSessionUpdates: params.sessionUpdates?.unreadSessionUpdates ?? 0,
      lastReadCursor: params.sessionUpdates?.lastReadCursor ? new ReadCursor(params.sessionUpdates.lastReadCursor) : void 0
    };
    this.unreadTimelineEntryComments = params.unreadTimelineEntryComments ?? 0;
    this.unreadChatMessages = params.unreadChatMessages ?? 0;
    this.createdAt = toDate(params.createdAt) || /* @__PURE__ */ new Date();
    this.updatedAt = toDate(params.updatedAt);
  }
  toJSON() {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      finished: this.finished,
      muted: this.muted,
      pinned: this.pinned,
      sessionUpdates: {
        unreadSessionUpdates: this.sessionUpdates.unreadSessionUpdates,
        lastReadCursor: this.sessionUpdates.lastReadCursor?.toJSON()
      },
      unreadTimelineEntryComments: this.unreadTimelineEntryComments,
      unreadChatMessages: this.unreadChatMessages,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
  serialize() {
    return JSON.parse(JSON.stringify(this));
  }
};
var UserSessionConverter = {
  toFirestore() {
    return {};
  },
  fromFirestore(doc) {
    const data = doc.data();
    return new UserSession({
      ...data,
      sessionId: doc.id,
      sessionUpdates: data.sessionUpdates ? {
        ...data.sessionUpdates,
        lastReadCursor: data.sessionUpdates.lastReadCursor ? { documentId: data.sessionUpdates.lastReadCursor.documentId, readAt: data.sessionUpdates.lastReadCursor.readAt?.toDate() } : void 0
      } : void 0,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate()
    });
  }
};
export {
  AdvancedTracking,
  AdvancedTrackingConverter,
  Airport,
  Area,
  AreaConverter,
  Attachment,
  AttachmentAccessLevel,
  AttachmentConverter,
  AttachmentRef,
  AttachmentStatus,
  AttachmentType,
  AuditLog,
  AuditLogConverter,
  AuditLogType,
  AuditLogTypeUtil,
  BarcodeInputMethod,
  BarcodeInputMethodUtil,
  BirthSex,
  BirthSexUtil,
  BloodType,
  BloodTypeUtil,
  BreakoutSessionCancellationReason,
  CannulationType,
  CannulationTypeUtil,
  Chat,
  ChatConverter,
  ChatMember,
  ChatMemberConverter,
  ChatRole,
  ChatSettings,
  ChatType,
  ComponentStateRecord,
  ComponentStateRecordConverter,
  ComponentStateType,
  ComponentStateTypeUtil,
  ComponentType,
  ComponentTypeUtil,
  DEFAULT_FALLBACK_PERMISSIONS,
  LIMITED_PERMISSIONS as DEFAULT_SESSION_MEMBER_PERMISSIONS,
  Destination,
  DestinationConverter,
  DestinationType,
  DestinationTypeUtil,
  DisposalReason,
  DistanceCalculationMethodType,
  DistanceCalculationMethodTypeUtil,
  DistanceUnit,
  DistanceUnitUtil,
  Document,
  DocumentConverter,
  DonationType,
  DonationTypeUtil,
  DonorData,
  DonorDataConverter,
  Draft,
  DraftConverter,
  DraftStateType,
  Extraction,
  FULL_PERMISSIONS,
  FeatureType,
  FeatureTypeUtil,
  Flight,
  FlightConverter,
  FlightPosition,
  FlightPositionConverter,
  FlightState,
  FlightStateUtil,
  InventoryAudit,
  InventoryAuditConverter,
  InventoryAuditStatus,
  InventoryComponent,
  InventoryComponentConverter,
  InventoryLocation,
  InventoryLocationConverter,
  InventoryLocationState,
  InventoryLocationStateUtil,
  InventoryLocationType,
  InventoryLocationTypeUtil,
  InventoryTransaction,
  InventoryTransactionConverter,
  InventoryTransactionStateType,
  InventoryTransactionStateTypeUtil,
  InventoryTransactionTrigger,
  InventoryTransactionTriggerUtil,
  InventoryTransactionType,
  InventoryTransactionTypeUtil,
  LIMITED_PERMISSIONS,
  Log,
  LogConverter,
  Logger,
  LoggerAppLocation,
  LoggerAppLocationConverter,
  LoggerAppLogPage,
  LoggerAppLogPageConverter,
  LoggerAppPressure,
  LoggerAppPressureConverter,
  LoggerChartData,
  LoggerChartDataConverter,
  LoggerChartPoint,
  LoggerConnection,
  LoggerConverter,
  LoggerData,
  LoggerDataRangeEdit,
  LoggerDataRangeEditBound,
  LoggerDataRangeEditTrim,
  LoggerHeader,
  LoggerLocation,
  LoggerLocationConverter,
  LoggerLogEntry2 as LoggerLogEntry,
  LoggerState,
  LungPositionType,
  LungPositionTypeUtil,
  META_ALL_FIELDS,
  META_OPTIONAL_FIELDS,
  META_REQUIRED_FIELDS,
  MUTATION_HISTORY_DIFF_VERSION,
  MeasurementUnit,
  Message,
  MessageConverter,
  MessageDeliveryStatus,
  MessageMention,
  MessageMentionType,
  MessagePreview,
  MessageReactions,
  MessageReceipt,
  MessageReceiptConverter,
  MessageType,
  MutationHistory,
  MutationHistoryConverter,
  NotesData,
  NotesDataConverter,
  OBSERVER_PERMISSIONS,
  OfferDraftType,
  OfferDraftTypeUtil,
  OfferStatusType,
  OfferStatusTypeUtil,
  OrScheduleType,
  OrScheduleTypeUtil,
  Organ,
  OrganConverter,
  OrganDataRangeEdit,
  OrganDataRangeEditBound,
  OrganDataRangeEditTrim,
  OrganLogger,
  OrganLoggerConnection,
  OrganLoggerData,
  OrganLoggerHeader,
  OrganPositionType,
  OrganPositionTypeUtil,
  OrganReading,
  OrganReadingsPage,
  OrganReadingsPageConverter,
  OrganType,
  OrganTypeUtil,
  Organization,
  OrganizationConverter,
  OrganizationMember,
  OrganizationMemberAccessRole,
  OrganizationMemberAccessRoleUtil,
  OrganizationMemberConverter,
  OrganizationMemberDeactivationReason,
  OrganizationMemberDeactivationReasonUtil,
  OrganizationMemberPermissions,
  OrganizationMemberRole,
  OrganizationMemberRoleConverter,
  OrganizationMemberStatus,
  OrganizationMemberStatusUtil,
  OrganizationOption,
  OrganizationOptionConverter,
  OrganizationPreselectedMember,
  OrganizationPreselectedMemberConverter,
  OrganizationStatus,
  OrganizationStatusUtil,
  OrganizationType,
  OrganizationTypeConverter,
  OrganizationUnregisteredInvite,
  OrganizationUnregisteredInviteConverter,
  PdfType,
  PdfTypeUtil,
  PotentialSession,
  PotentialSessionConverter,
  PotentialSessionStatus,
  PotentialSessionStatusUtils,
  PreservationMethodType,
  PreservationMethodUtil,
  Pressure,
  PressureConverter,
  ProductType,
  ProductTypeUtil,
  ReadCursor,
  Reading,
  ReadingCompressed,
  ReadingCompressedConverter,
  ReadingConverter,
  RecipientData,
  RecipientDataConverter,
  SYSTEM_SENDER_ID,
  Session,
  SessionConverter,
  SessionETA,
  SessionETAConverter,
  SessionIschemicTime,
  SessionIschemicTimeConverter,
  SessionIschemicTimeType,
  SessionMember,
  SessionMemberAccessLevel,
  SessionMemberAccessLevelType,
  SessionMemberConverter,
  SessionMetadata,
  SessionMetrics,
  SessionPhase,
  SessionPhaseUtil,
  SessionReportData,
  SessionReviewState,
  SessionReviewStateUtil,
  SessionStateType,
  SessionStateTypeUtil,
  SessionType,
  SessionTypeUtil,
  SessionView,
  SessionViewConverter,
  SessionViewSettings,
  SessionViewSettingsConverter,
  SharedSession,
  SharedSessionConverter,
  SystemEvent,
  Territory,
  TerritoryConverter,
  TimelineEntry2 as TimelineEntry,
  TimelineEntryAttributeType,
  TimelineEntryConverter,
  TimelineEntryReadState,
  TimelineEntryReadStateConverter,
  TimelineEntryType,
  TimelineEntryTypeUtil,
  Tutorial,
  TutorialConverter,
  TwoFactorRequirement,
  User,
  UserAccess,
  UserAccessType,
  UserAccessTypeUtil,
  UserChat,
  UserChatConverter,
  UserConverter,
  UserDeactivationReason,
  UserDevice,
  UserDeviceConverter,
  UserSession,
  UserSessionConverter,
  UserSettings,
  VALID_DRAFT_STATES,
  VALID_STATE_TRANSITIONS,
  assertValidStateForDraftType,
  compareTimelineEntriesDesc,
  createSystemMeta,
  createTrackedWriteMeta,
  enrichMeta,
  getValidStatesForDraftType,
  hasCloudFunctionContext,
  isCompleteMeta,
  isFreshCompleteMeta,
  isHistoryPatchEmpty,
  isMetaStale,
  isValidStateForDraftType,
  isValidStateTransition,
  normalizeOrScheduleTime,
  resolveEffectivePermissions,
  resolveSessionMemberPermissions,
  sanitizeLoggersForSharing,
  validateMeta
};
